/**
 * Genius Product Options - Frontend Widget
 * @version 2.1.0 - Option look preview (image + name, configured appearance)
 * 
 * This version works with Shopify's native variant system:
 * - Options map to product variants
 * - Selecting options changes the active variant
 * - Price updates automatically based on variant
 * - No separate fee product needed
 */

(function() {
  'use strict';

  const STRINGS_EN = {
    loading: 'Loading options...',
    error: 'Failed to load options. Please refresh the page.',
    required: 'Required',
    included: 'Included',
    free: 'Free',
    selectOption: 'Please select an option',
    resetSelections: 'Reset options',
    total: 'Total:',
    totalLabel: 'Total',
    unavailable: 'Unavailable',
    basePriceLabel: 'Base Price',
    wholesaleQuote: 'Wholesale Quote',
    quoteSubtitleHtml: 'Special pricing for <strong>10+ units</strong>',
    quoteNameLabel: 'Name *',
    quoteNamePlaceholder: 'Your name',
    quoteEmailLabel: 'Email *',
    quoteEmailPlaceholder: 'your@email.com',
    quoteQtyLabel: 'Qty *',
    quoteCountryLabel: 'Country *',
    quoteCountryPlaceholder: 'Select Country',
    quoteZipLabel: 'Zip / Postal Code *',
    quoteZipPlaceholder: 'For shipping estimate',
    quoteNotesLabel: 'Notes ',
    quoteNotesHint: '(optional, max 200 characters)',
    quoteNotesPlaceholder:
      'Any special requirements, questions, or customization needs…',
    quoteSend: 'Send request',
    quoteSubmitting: 'Submitting…',
    quoteErrName: 'Please enter your name.',
    quoteErrEmail: 'Please enter your email address.',
    quoteErrEmailFmt: 'Please enter a valid email address.',
    quoteErrCountry: 'Please select your country.',
    quoteErrZip: 'Please enter your zip or postal code.',
    quoteErrSubmit:
      'Could not submit the request. Please try again or contact us.',
    quoteSuccessTitle: 'Request received',
    quoteSuccessLead: 'We will send pricing details to',
    quoteClose: 'Close',
    quoteIdLabel: 'Reference:',
    variantSelectFirst: 'Please select a product variant first.',
    checkoutProcessing: 'Processing…',
    qtyDecrease: 'Decrease quantity',
    qtyIncrease: 'Increase quantity',
    qtyInputAria: 'Quantity',
    zhichunComDefaultShort:
      'Standard serial ports (included)',
    zhichunComDefaultLong:
      'Extended serial layout (included)',
    lookLabel: 'Selected look',
  };
  const STRINGS_TR = {
    loading: 'Seçenekler yükleniyor...',
    error: 'Seçenekler yüklenemedi. Lütfen sayfayı yenileyin.',
    required: 'Zorunlu',
    included: 'Dahil',
    free: 'Ücretsiz',
    selectOption: 'Lütfen bir seçenek seçin',
    resetSelections: 'Seçenekleri sıfırla',
    total: 'Toplam:',
    totalLabel: 'Toplam',
    unavailable: 'Stokta yok',
    basePriceLabel: 'Taban fiyat',
    wholesaleQuote: 'Toptan teklif',
    quoteSubtitleHtml: '<strong>10 ve üzeri adet</strong> için özel fiyatlandırma',
    quoteNameLabel: 'Ad Soyad *',
    quoteNamePlaceholder: 'Adınız soyadınız',
    quoteEmailLabel: 'E-posta *',
    quoteEmailPlaceholder: 'ornek@eposta.com',
    quoteQtyLabel: 'Adet *',
    quoteCountryLabel: 'Ülke *',
    quoteCountryPlaceholder: 'Ülke seçin',
    quoteZipLabel: 'Posta kodu *',
    quoteZipPlaceholder: 'Kargo tahmini için',
    quoteNotesLabel: 'Notlar ',
    quoteNotesHint: '(isteğe bağlı, en fazla 200 karakter)',
    quoteNotesPlaceholder:
      'Özel istekleriniz, sorularınız veya özelleştirme ihtiyaçlarınız…',
    quoteSend: 'Talebi gönder',
    quoteSubmitting: 'Gönderiliyor…',
    quoteErrName: 'Lütfen adınızı girin.',
    quoteErrEmail: 'Lütfen e-posta adresinizi girin.',
    quoteErrEmailFmt: 'Lütfen geçerli bir e-posta girin.',
    quoteErrCountry: 'Lütfen ülke seçin.',
    quoteErrZip: 'Lütfen posta kodunu girin.',
    quoteErrSubmit:
      'Talep gönderilemedi. Lütfen tekrar deneyin veya bize ulaşın.',
    quoteSuccessTitle: 'Talebiniz alındı',
    quoteSuccessLead: 'Fiyat bilgisi şu adrese iletilecek:',
    quoteClose: 'Kapat',
    quoteIdLabel: 'Referans:',
    variantSelectFirst: 'Lütfen önce ürün varyantını seçin.',
    checkoutProcessing: 'İşleniyor…',
    qtyDecrease: 'Adedi azalt',
    qtyIncrease: 'Adedi artır',
    qtyInputAria: 'Adet',
    zhichunComDefaultShort:
      'Standart seri portlar (dahil)',
    zhichunComDefaultLong:
      'Genişletilmiş seri port düzeni (dahil)',
    lookLabel: 'Seçilen görünüm',
  };

  /** Kart SKU (`nanopi-r6s`, `nanopi-r6s-*`); aksesuar slug (`metal-case-for-nanopi-r6s`) değil. */
  function isPrimaryNanoPiR6SBoardProductHandle(handle) {
    const h = String(handle || '').toLowerCase().trim();
    return /^nanopi-r6s(?:$|[\-_])/i.test(h);
  }

  function geniusCsvTokens(csv) {
    return String(csv || '')
      .split(',')
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
  }

  /** Open Frame Android panel PC — PoE vb. seçenekler bu ürünlerde gösterilmez. */
  function geniusIsOpenFrameAndroidProduct(handle, title, tagsCsv, collectionsInput) {
    const t = String(title || '').toLowerCase();
    const h = String(handle || '').toLowerCase();
    if (
      t.includes('open frame') ||
      t.includes('open-frame') ||
      h.includes('open-frame') ||
      h.includes('open_frame')
    ) {
      return true;
    }
    const tagBlob = String(tagsCsv || '').toLowerCase();
    if (
      tagBlob.includes('open frame') ||
      tagBlob.includes('open-frame') ||
      tagBlob.includes('openframe')
    ) {
      return true;
    }
    const cols = [];
    if (Array.isArray(collectionsInput)) {
      collectionsInput.forEach(function (c) {
        cols.push(String(c || '').toLowerCase());
      });
    }
    geniusCsvTokens(tagsCsv).forEach(function (tag) {
      const lo = tag.toLowerCase();
      if (lo.indexOf('gc-col:') === 0) {
        cols.push(lo.replace(/^gc-col:/, ''));
      }
    });
    return cols.some(function (c) {
      return c.indexOf('open-frame') !== -1;
    });
  }

  function geniusIsPoeAccessoryOption(opt) {
    const v = String(opt?.value || '').toLowerCase().trim();
    return v === 'poe' || v === 'ext_poe' || v === 'power_over_ethernet';
  }

  function geniusIsOpenFrameAndroidProductFromConfig(config) {
    const cols = [];
    const pcCtx = (config && config.productContext) || {};
    if (Array.isArray(pcCtx.collections)) {
      pcCtx.collections.forEach(function (c) {
        cols.push(String(c || ''));
      });
    }
    geniusCsvTokens(config && config.collectionHandles).forEach(function (ch) {
      if (cols.indexOf(ch) === -1) cols.push(ch);
    });
    return geniusIsOpenFrameAndroidProduct(
      config && config.productHandle,
      config && config.productTitle,
      config && config.productTags,
      cols
    );
  }

  /** Proxy `productQualifiesForNanoPiR6SHardwareContext`: tam etiket `R6S`, koleksiyonda `nanopi-r6s`, veya birincil kart handle. */
  function geniusProductQualifiesForNanoPiR6SExclusive(handle, tagsCsv, collectionsInput) {
    const hLo = String(handle || '').toLowerCase().trim();
    if (isPrimaryNanoPiR6SBoardProductHandle(hLo)) return true;
    const tagsLo = geniusCsvTokens(tagsCsv).map(function (t) {
      return t.toLowerCase();
    });
    if (tagsLo.indexOf('r6s') !== -1) return true;
    let cols = [];
    if (Array.isArray(collectionsInput)) {
      cols = collectionsInput.map(function (c) {
        return String(c || '');
      });
    } else {
      cols = geniusCsvTokens(String(collectionsInput || ''));
    }
    return cols.some(function (c) {
      return c.toLowerCase().includes('nanopi-r6s');
    });
  }

  /** USD surcharge applied to each remaining SSD capacity tier (32/64 GB tiers removed). */
  const SSD_CAPACITY_SURCHARGE_USD = 60;

  /**
   * Must match `scripts/seed-zhichun-hardware.cjs` (ZHICHUN_COM_SHORT_HANDLES / LONG).
   * Used to pick the correct “default COM” copy for `com_matrix_default`.
   */
  const ZHICHUN_COM_SHORT_HANDLE_SET = new Set(
    "gz-apc7-zs1,gz-apc7-zs2,gz-apc7-zs3,gz-apc8-zs1,gz-apc8-zs2,gz-apc8-zs3,gz-wpc7-zs1,gz-wpc7-zs2,gz-wpc8-zs1,gz-wpc8-zs2"
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
  );
  const ZHICHUN_COM_LONG_HANDLE_SET = new Set(
    "gz-apc101-zs1,gz-apc101-zs2,gz-apc101-zs3,gz-apc104-zs1,gz-apc104-zs2,gz-apc104-zs3,gz-apc121-zs1,gz-apc121-zs2,gz-apc121-zs3,gz-apc133-zs1,gz-apc133-zs2,gz-apc133-zs3,gz-apc15-zs1,gz-apc15-zs2,gz-apc15-zs3,gz-apc156-zs1,gz-apc156-zs2,gz-apc156-zs3,gz-apc17-zs1,gz-apc17-zs2,gz-apc17-zs3,gz-apc19-zs1,gz-apc19-zs2,gz-apc19-zs3,gz-apc215-zs1,gz-apc215-zs2,gz-apc215-zs3,gz-wpc101-zs1,gz-wpc101-zs2,gz-wpc101-zs3,gz-wpc101-zs4,gz-wpc104-zs1,gz-wpc104-zs2,gz-wpc104-zs3,gz-wpc104-zs4,gz-wpc121-zs1,gz-wpc121-zs2,gz-wpc121-zs3,gz-wpc121-zs4,gz-wpc133-zs1,gz-wpc133-zs2,gz-wpc133-zs3,gz-wpc133-zs4,gz-wpc15-zs1,gz-wpc15-zs2,gz-wpc15-zs3,gz-wpc15-zs4,gz-wpc156-zs1,gz-wpc156-zs2,gz-wpc156-zs3,gz-wpc156-zs4,gz-wpc17-zs1,gz-wpc17-zs2,gz-wpc17-zs3,gz-wpc17-zs4,gz-wpc19-zs1,gz-wpc19-zs2,gz-wpc19-zs3,gz-wpc19-zs4,gz-wpc215-zs1,gz-wpc215-zs2,gz-wpc215-zs3,gz-wpc215-zs4"
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
  );

  /**
   * Must match `scripts/seed-zhichun-hardware.cjs` group `sortOrder` (100–107).
   * RAM → SSD → OS first; then COM, power, display, cellular.
   */
  const ZHICHUN_GROUP_RENDER_ORDER = [
    'zhichun_ram_ddr4',
    'zhichun_msata',
    'zhichun_os_suite',
    'zhichun_serial_ports',
    'zhichun_power_supply',
    'zhichun_display_type',
    'zhichun_m2_4g_eu',
    'zhichun_m2_5g_global',
  ];

  /** Android APC fabrika RAM/eMMC eşlemesi (`zhichun-android-matrix.json`). */
  const ZHICHUN_ANDROID_FACTORY_MATRIX = {"gz-apc101-zs1":{"cpu":"RK3568","ramGb":2,"storageGb":32},"gz-apc101-zs2":{"cpu":"RK3576","ramGb":4,"storageGb":64},"gz-apc101-zs3":{"cpu":"RK3588","ramGb":4,"storageGb":64},"gz-apc104-zs1":{"cpu":"RK3568","ramGb":2,"storageGb":32},"gz-apc104-zs2":{"cpu":"RK3576","ramGb":4,"storageGb":64},"gz-apc104-zs3":{"cpu":"RK3588","ramGb":4,"storageGb":64},"gz-apc121-zs1":{"cpu":"RK3568","ramGb":2,"storageGb":32},"gz-apc121-zs2":{"cpu":"RK3576","ramGb":4,"storageGb":64},"gz-apc121-zs3":{"cpu":"RK3588","ramGb":4,"storageGb":64},"gz-apc133-zs1":{"cpu":"RK3568","ramGb":2,"storageGb":32},"gz-apc133-zs2":{"cpu":"RK3576","ramGb":4,"storageGb":64},"gz-apc133-zs3":{"cpu":"RK3588","ramGb":4,"storageGb":64},"gz-apc15-zs1":{"cpu":"RK3568","ramGb":2,"storageGb":32},"gz-apc15-zs2":{"cpu":"RK3576","ramGb":4,"storageGb":64},"gz-apc15-zs3":{"cpu":"RK3588","ramGb":4,"storageGb":64},"gz-apc156-zs1":{"cpu":"RK3568","ramGb":2,"storageGb":32},"gz-apc156-zs2":{"cpu":"RK3576","ramGb":4,"storageGb":64},"gz-apc156-zs3":{"cpu":"RK3588","ramGb":4,"storageGb":64},"gz-apc17-zs1":{"cpu":"RK3568","ramGb":2,"storageGb":32},"gz-apc17-zs2":{"cpu":"RK3576","ramGb":4,"storageGb":64},"gz-apc17-zs3":{"cpu":"RK3588","ramGb":4,"storageGb":64},"gz-apc19-zs1":{"cpu":"RK3568","ramGb":2,"storageGb":32},"gz-apc19-zs2":{"cpu":"RK3576","ramGb":4,"storageGb":64},"gz-apc19-zs3":{"cpu":"RK3588","ramGb":4,"storageGb":64},"gz-apc215-zs1":{"cpu":"RK3568","ramGb":2,"storageGb":32},"gz-apc215-zs2":{"cpu":"RK3576","ramGb":4,"storageGb":64},"gz-apc215-zs3":{"cpu":"RK3588","ramGb":4,"storageGb":64},"gz-apc7-zs1":{"cpu":"RK3568","ramGb":2,"storageGb":32},"gz-apc7-zs2":{"cpu":"RK3576","ramGb":4,"storageGb":64},"gz-apc7-zs3":{"cpu":"RK3588","ramGb":4,"storageGb":64},"gz-apc8-zs1":{"cpu":"RK3568","ramGb":2,"storageGb":32},"gz-apc8-zs2":{"cpu":"RK3576","ramGb":4,"storageGb":64},"gz-apc8-zs3":{"cpu":"RK3588","ramGb":4,"storageGb":64}};

  /**
   * NanoPi / FriendlyELEC vb.; yanlış koleksiyonda olsa bile panel PC setleri gösterilmesin.
   * Sunucu: `app/routes/proxy.tsx` → `isLikelyStandaloneSbcOrGatewayProduct`.
   */
  const GENOVA_EMBEDDED_ANDROID_BOARD_MODEL_TAGS = new Set(
    [
      'K0', 'K2', 'K3', 'K4', 'K-3588S', 'D-3568', 'D-3576', 'D-3588', 'K-3566', 'K-S905', 'K-A311D',
      'H3', 'H-3399', 'H-3568', 'H-3566', 'H-A133', 'H-A133L', 'H-T972', 'H0',
      'M1', 'M2', 'M3',
      'F1', 'F3', 'F4', 'F3X', 'F1i', 'Fi-1109', 'Fi-3568', 'Fi-S905D3', 'Fli',
      'C0', 'C3', 'C4',
      'P2', 'P3x', 'PX-3568', 'PX-3399', 'UPC-3568',
      'VNS-3568', 'VPC-3588', 'MS-Y3399', 'TM-YA133MC', 'YA133AW-Y3566AW', 'Y3566AW-3568',
      'GW-3288', 'YEC-X3288-18U', 'B5S', 'SST-3399', 'ITX-Y3399', 'ITX-Y3568',
      'Y-A133', 'Y-3566', 'Y-3568', 'Y-3399', 'Z-3588',
      'ECG-3568', 'SS-A133', 'IS-A133', 'DF-A133',
    ].map(function (t) {
      return t.toLowerCase();
    })
  );

  function geniusIsGenovaEmbeddedAndroidBoardProduct(handle, title, tagsCsv) {
    const tags = String(tagsCsv || '')
      .split(/[,;|]/)
      .map(function (t) {
        return t.trim().toLowerCase();
      })
      .filter(Boolean);
    const tagBlob = tags.join(' ');
    if (tagBlob.includes('android gömülü') || tagBlob.includes('android gomulu')) return true;
    if (tags.some(function (t) {
      return t.indexOf('gc-col:android-gomulu') === 0;
    })) {
      return true;
    }
    if (tags.some(function (t) {
      return GENOVA_EMBEDDED_ANDROID_BOARD_MODEL_TAGS.has(t);
    })) {
      return true;
    }
    const t = String(title || '').toLowerCase();
    const h = String(handle || '').toLowerCase();
    if (
      (t.includes('anakart') ||
        t.includes('motherboard') ||
        t.includes('main board') ||
        h.includes('anakart')) &&
      !t.includes('panel pc')
    ) {
      return true;
    }
    return false;
  }

  function geniusEnrichEmbeddedBoardCollectionsFromTags(tagsCsv, collections) {
    const normalized = (collections || []).slice();
    const seen = new Set(
      normalized.map(function (c) {
        return String(c || '').toLowerCase();
      })
    );
    const add = function (c) {
      const lo = String(c || '').toLowerCase();
      if (!seen.has(lo)) {
        normalized.push(c);
        seen.add(lo);
      }
    };
    const tags = String(tagsCsv || '')
      .split(/[,;|]/)
      .map(function (t) {
        return t.trim().toLowerCase();
      })
      .filter(Boolean);
    const tagBlob = tags.join(' ');
    if (
      tagBlob.includes('android gömülü') ||
      tagBlob.includes('android gomulu') ||
      tags.some(function (t) {
        return t.indexOf('gc-col:android-gomulu') === 0;
      })
    ) {
      add('embedded-android-boards');
    }
    if (tags.some(function (t) {
      return GENOVA_EMBEDDED_ANDROID_BOARD_MODEL_TAGS.has(t);
    })) {
      add('embedded-android-boards');
    }
    if (tags.indexOf('development-boards') >= 0) {
      add('development-boards');
    }
    if (
      tags.some(function (t) {
        return (
          t === 'gc-col:gelistirme-kartlari' ||
          t.indexOf('gc-col:gelistirme-kartlari') === 0
        );
      })
    ) {
      add('gelistirme-kartlari');
      add('development-boards');
    }
    return normalized;
  }

  /** Tema `android-panel-pc` göndermezse TR/Global vitrin koleksiyon + gc-col etiketinden çıkar. */
  function geniusEnrichPanelPcCollectionsFromTags(tagsCsv, collections) {
    const normalized = (collections || []).slice();
    const seen = new Set(
      normalized.map(function (c) {
        return String(c || '').toLowerCase();
      })
    );
    const add = function (c) {
      const lo = String(c || '').toLowerCase();
      if (!seen.has(lo)) {
        normalized.push(c);
        seen.add(lo);
      }
    };
    const tags = String(tagsCsv || '')
      .split(/[,;|]/)
      .map(function (t) {
        return t.trim().toLowerCase();
      })
      .filter(Boolean);
    const colLo = normalized.map(function (c) {
      return String(c || '').toLowerCase();
    });
    const blob = tags.join(' ') + ' ' + colLo.join(' ');
    if (
      tags.some(function (t) {
        return t === 'gc-col:android-panel-pc' || t.indexOf('gc-col:android-panel') === 0;
      }) ||
      colLo.indexOf('android-panel-pc') !== -1 ||
      colLo.indexOf('kiosk-ve-odeme-panelleri') !== -1 ||
      colLo.indexOf('kiosk-payments-panels') !== -1 ||
      blob.indexOf('kiosk-ve-odeme') !== -1 ||
      blob.indexOf('kiosk-payments') !== -1 ||
      blob.indexOf('android-panel-pc') !== -1
    ) {
      add('android-panel-pc');
    }
    if (
      tags.some(function (t) {
        return (
          t === 'gc-col:windows-panel-pc' ||
          t.indexOf('gc-col:windows-panel') === 0 ||
          t.indexOf('gc-col:premium-panel') === 0 ||
          t.indexOf('gc-col:endustriyel-panel') === 0
        );
      }) ||
      colLo.indexOf('windows-panel-pc') !== -1 ||
      colLo.indexOf('premium-panel-pc-ler') !== -1 ||
      colLo.indexOf('endustriyel-panel-pc-sistemleri') !== -1 ||
      blob.indexOf('windows-panel-pc') !== -1 ||
      blob.indexOf('premium-panel-pc') !== -1 ||
      blob.indexOf('endustriyel-panel-pc') !== -1
    ) {
      add('windows-panel-pc');
    }
    return normalized;
  }

  function geniusLooksLikeStandaloneSbcOrGateway(handle, title, type, vendor, tagsCsv, skusCsv) {
    const h = String(handle || '')
      .toLowerCase()
      .trim();
    const t = String(title || '').toLowerCase();
    const ty = String(type || '').toLowerCase();
    const v = String(vendor || '').toLowerCase();
    const tagBlob = String(tagsCsv || '').toLowerCase();
    const skuBlob = String(skusCsv || '').toLowerCase();
    if (h.includes('gz-apc') || h.includes('gz-wpc')) return false;
    if (/(?:^|[-_])apc\d/i.test(h) || /(?:^|[-_])wpc\d/i.test(h)) return false;
    if (
      h.includes('panel-pc') ||
      h.includes('panel_pc') ||
      h.includes('hmi-android') ||
      h.includes('hmi-panel')
    ) {
      return false;
    }
    if (
      t.includes('panel pc') ||
      t.includes('panel-pc') ||
      ty.includes('panel pc') ||
      ty.includes('flat panel')
    ) {
      return false;
    }
    if (
      tagBlob.includes('nanopi') ||
      tagBlob.includes('friendlyelec') ||
      tagBlob.includes('friendly elec') ||
      tagBlob.includes('orangepi') ||
      tagBlob.includes('radxa')
    ) {
      return true;
    }
    if (geniusIsGenovaEmbeddedAndroidBoardProduct(h, t, tagsCsv)) return false;
    if (
      skuBlob.includes('nanopi') ||
      skuBlob.includes('r5s') ||
      skuBlob.includes('r5c') ||
      skuBlob.includes('r4s') ||
      skuBlob.includes('r6s')
    ) {
      return true;
    }
    if (
      h.includes('nanopi') ||
      h.includes('rockpi') ||
      h.includes('orangepi') ||
      h.includes('orange-pi') ||
      h.includes('radxa') ||
      h.includes('pine64') ||
      h.includes('libre-computer') ||
      h.includes('beaglebone') ||
      h.includes('odroid')
    ) {
      return true;
    }
    if (
      h.includes('friendlyelec') ||
      h.includes('friendly-arm') ||
      v.includes('friendlyelec') ||
      v.includes('friendly arm') ||
      v.includes('friendlyarm')
    ) {
      return true;
    }
    if (
      h.includes('iot-gateway') ||
      h.includes('iot_gateway') ||
      h.includes('mini-nas') ||
      h.includes('mini_nas') ||
      h.includes('nvme-iot')
    ) {
      return true;
    }
    if (/(?:^|[-_])r[2-6][sc](?:[-_]|$)/i.test(h)) return true;
    if ((t.includes('nanopi') || t.includes('friendlyelec')) && !t.includes('panel pc')) return true;
    if (t.includes('iot gateway') || t.includes('mini nas') || t.includes('single board computer'))
      return true;
    if (
      ty.includes('single board') ||
      ty.includes('geliştirme kartı') ||
      ty.includes('development board')
    ) {
      return true;
    }
    return false;
  }

  /**
   * SYNC: `app/lib/option-set-scope.server.ts` — koleksiyon/handle/ürün/SKU daraltması olan set varken
   * yalnız etiket ile eşleşen rakipleri vitrinde düşürür (Zhichun muaf).
   */
  function geniusConditionNarrowsOptionSetTargeting(cond) {
    if (!cond || typeof cond !== 'object') return false;
    var op = String(cond.operator || '').toLowerCase();
    var positive =
      op === 'equals' ||
      op === 'contains' ||
      op === 'starts_with' ||
      op === 'ends_with' ||
      op === 'in';
    if (!positive) return false;
    var t = String(cond.conditionType || '').toLowerCase();
    return (
      t === 'collection' ||
      t === 'handle' ||
      t === 'product' ||
      t === 'sku'
    );
  }

  function geniusPreferCollectionOrHandleScopedSets(sets) {
    if (!Array.isArray(sets) || sets.length === 0) return sets;
    var zhichun = sets.filter(function (s) {
      return (s.name || '').toLowerCase().indexOf('zhichun') !== -1;
    });
    var rest = sets.filter(function (s) {
      return (s.name || '').toLowerCase().indexOf('zhichun') === -1;
    });
    if (rest.length === 0) return sets;
    var scopedRest = rest.filter(function (s) {
      return (s.conditions || []).some(geniusConditionNarrowsOptionSetTargeting);
    });
    if (scopedRest.length === 0) return sets;
    var unscopedRest = rest.filter(function (s) {
      return !(s.conditions || []).some(geniusConditionNarrowsOptionSetTargeting);
    });
    if (unscopedRest.length === 0) return sets;
    console.log(
      '[GeniusOptions] Narrowed scope wins — dropping broad tag-only:',
      unscopedRest
        .map(function (s) {
          return s.name;
        })
        .join(', ')
    );
    return zhichun.concat(scopedRest);
  }

  /**
   * İsteğe bağlı: belirli handle’da özel kısa etiket. Boş bırakıldı — genel `strings.zhichunComDefault*` kullanılır.
   */
  const ZHICHUN_COM_DEFAULT_LABEL_BY_HANDLE = {};

  const ZHICHUN_COM_DEFAULT_LABEL_BY_HANDLE_TR = {};

  const GeniusOptions = {
    container: null,
    config: {},
    optionSets: [],
    selectedOptions: {},
    optionsDataMap: {}, // Map to store option data: { "groupId:value": { displayValue, priceModifier, ... } }
    groupDisplayNames: {}, // Map to store group display names: { groupId: displayName }
    basePrice: 0,
    productVariants: [], // Shopify product variants
    currentVariantId: null,
    dropdownPortals: new Map(),

    strings: { ...STRINGS_EN },

    /**
     * Shopify product handle for API calls. Liquid `data-product-handle` is preferred;
     * if missing (some themes), derive from storefront URL `/products/handle` or `/xx/products/handle`.
     */
    resolveProductHandle(suggested) {
      let h = String(suggested || '').trim();
      if (h) return h;
      if (typeof window === 'undefined' || !window.location || !window.location.pathname) return '';
      const path = window.location.pathname || '';
      const m = path.match(
        /\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/([^/?#]+)/i
      );
      if (!m || !m[1]) return '';
      try {
        return decodeURIComponent(m[1]).trim();
      } catch {
        return String(m[1]).trim();
      }
    },

    async init() {
      console.log('[GeniusOptions] Init started - Variant Mode');
      
      // Wait a tick to ensure all DOM elements are ready
      await new Promise(resolve => setTimeout(resolve, 10));
      
      // Look for manual block first (priority), then auto widget
      const manualWidget = document.getElementById('genius-product-options');
      const autoWidget = document.getElementById('genius-product-options-auto');
      
      console.log('[GeniusOptions] Manual widget:', !!manualWidget, 'Auto widget:', !!autoWidget);
      
      // If manual block exists, ALWAYS use it and remove auto
      if (manualWidget) {
        this.container = manualWidget;
        if (autoWidget) {
          autoWidget.remove();
          console.log('[GeniusOptions] Manual block found, removed auto widget');
        }
      } else if (autoWidget) {
        this.container = autoWidget;
        autoWidget.style.display = '';
      }
      
      if (!this.container) {
        console.log('[GeniusOptions] Container not found');
        return;
      }

      // Prevent re-initialization (pending = async init in flight)
      if (
        this.container.dataset.initialized === 'true' ||
        this.container.dataset.initialized === 'pending'
      ) {
        console.log('[GeniusOptions] Container already initialized');
        return;
      }
      this.container.dataset.initialized = 'pending';

      try {
        // Ensure dropdown menus are not clipped by theme containers
        this.ensureOverflowVisible();
        this.applyGenovaMonochromePaletteIfNeeded();

      console.log('[GeniusOptions] Using container:', this.container.id);

      // Parse product variants from the page
      this.parseProductVariants();

      // Get product title from dataset or fallback to DOM
      let productTitle = this.container.dataset.productTitle || '';
      if (!productTitle) {
          const titleElement = document.querySelector(
            '.product__title, h1.product__title, .product-single__title, h1'
          );
        if (titleElement) {
          productTitle = titleElement.textContent?.trim() || '';
        }
      }

        const handleFromLiquid = this.container.dataset.productHandle;
        const productHandle = this.resolveProductHandle(handleFromLiquid);
        if (productHandle && productHandle !== (handleFromLiquid || '').trim()) {
          console.log(
            '[GeniusOptions] Handle from URL (Liquid handle missing or empty):',
            productHandle
          );
        }

      this.config = {
        productId: this.container.dataset.productId,
          productHandle: productHandle,
        productTitle: productTitle,
        productTags: this.container.dataset.productTags,
        productType: this.container.dataset.productType,
        productVendor: this.container.dataset.productVendor,
        collectionHandles: this.container.dataset.collectionHandles || '',
        productSku: this.container.dataset.productSku || '',
        productSkus: this.container.dataset.productSkus || '',
        basePrice: parseFloat(this.container.dataset.basePrice) / 100,
        currency: this.container.dataset.currency || 'USD',
        shop: this.container.dataset.shop,
        moneyFormat: this.container.dataset.moneyFormat,
        appProxyUrl: window.GeniusOptionsConfig?.appProxyUrl || '/apps/genius-options',
        locale: (this.container.dataset.locale || 'en').toLowerCase().slice(0, 2),
      };

      this.setLocale(this.config.locale);
      this.updateLocaleInDom();

      console.log('[GeniusOptions] Product title:', this.config.productTitle);
      
      this.basePrice = this.config.basePrice;
      console.log('[GeniusOptions] Config loaded, base price:', this.basePrice);
      console.log('[GeniusOptions] Product variants found:', this.productVariants.length);

        await this.loadOptions();
        console.log('[GeniusOptions] Options loaded, count:', this.optionSets.length);
        if (this.optionSets.length > 0) {
          this.render();
          this.ensureOverflowVisible();
          try {
          this.bindEvents();
          } catch (bindErr) {
            console.error(
              '[GeniusOptions] bindEvents failed (options may be partially interactive):',
              bindErr
            );
          }
          try {
          this.updatePrice();
          } catch (priceErr) {
            console.error('[GeniusOptions] updatePrice failed after render:', priceErr);
          }
          try {
          this.setupVariantSync();
          } catch (syncErr) {
            console.error('[GeniusOptions] setupVariantSync failed:', syncErr);
          }

          window.addEventListener('scroll', () => this.repositionActiveDropdowns(), {
            passive: true,
          });
          window.addEventListener('resize', () => this.repositionActiveDropdowns(), {
            passive: true,
          });
          
          console.log('[GeniusOptions] Widget initialized successfully');
          
          setTimeout(() => {
            try {
              if (this.container && typeof this.removeEachTextFromCheckboxes === 'function') {
                console.log('[GeniusOptions] Post-init cleanup check...');
                this.removeEachTextFromCheckboxes();
              }
            } catch (error) {
              console.error('[GeniusOptions] Error in post-init cleanup:', error);
            }
          }, 500);
        } else {
          console.log('[GeniusOptions] No options found, hiding widget');
          this.hideWidget();
        }
      } catch (error) {
        console.error('[GeniusOptions] Init Error:', error);
        this.showError();
      } finally {
        if (this.container && this.container.dataset.initialized === 'pending') {
          this.container.dataset.initialized = 'true';
        }
      }
    },

    ensureOverflowVisible() {
      const container = this.container;
      if (!container || container.dataset.overflowFixed === 'true') return;

      // Force the widget itself to sit above neighbors
      container.style.position = 'relative';
      container.style.zIndex = '2';

      /** Ürün medya / slideshow bileşenlerinin overflow'unu değiştirme (TR temada görsel “küçük köşede” bug’ı). */
      const mediaRoot = container.closest(
        'media-gallery, slideshow-component, slider-component, .product__media-wrapper, .product__media, [class*="product-media"], [class*="product-media-container"], deferred-media'
      );
      if (mediaRoot) {
        container.dataset.overflowFixed = 'true';
        return;
      }

      const shouldSkipAncestor = (el) => {
        if (!el || el === document.body) return true;
        const tag = (el.tagName || '').toLowerCase();
        const cls = (
          typeof el.className === 'string' ? el.className : String(el.className || '')
        ).toLowerCase();
        const idRaw =
          typeof el.getAttribute === 'function' ? el.getAttribute('id') : null;
        const id = String(idRaw != null ? idRaw : el.id != null ? el.id : '').toLowerCase();
        if (
          tag === 'media-gallery' ||
          tag === 'slideshow-component' ||
          tag === 'slider-component' ||
          tag === 'deferred-media'
        ) {
          return true;
        }
        if (
          cls.includes('product__media') ||
          cls.includes('product-media') ||
          cls.includes('media-gallery') ||
          cls.includes('slideshow') ||
          id.includes('gallery')
        ) {
          return true;
        }
        return false;
      };

      // Walk up the DOM and relax clipping containers (stop before product media)
      let node = container.parentElement;
      let depth = 0;
      while (node && node !== document.body && depth < 8) {
        if (shouldSkipAncestor(node)) break;

        const computed = window.getComputedStyle(node);
        const overflowValues = [
          computed.overflow,
          computed.overflowX,
          computed.overflowY
        ];
        const isClipping = overflowValues.some(val =>
          val === 'hidden' || val === 'auto' || val === 'clip'
        );

        if (isClipping) {
          node.style.overflow = 'visible';
          node.style.overflowX = 'visible';
          node.style.overflowY = 'visible';
        }

        node = node.parentElement;
        depth += 1;
      }

      container.dataset.overflowFixed = 'true';
    },
    
    /**
     * Parse product variants from the page's JSON
     * Shopify themes typically include variant data in a script tag
     */
    parseProductVariants() {
      try {
        // Method 1: Look for product JSON in script tag
        const productJson = document.querySelector('[data-product-json], script[type="application/json"][data-product-json]');
        if (productJson) {
          const product = JSON.parse(productJson.textContent);
          this.productVariants = product.variants || [];
          console.log('[GeniusOptions] Parsed variants from product JSON:', this.productVariants.length);
          return;
        }
        
        // Method 2: Look for variant data in various common locations
        const variantSelects = document.querySelectorAll('select[name="id"], input[name="id"]');
        if (variantSelects.length > 0) {
          // Extract from select options or data attributes
          variantSelects.forEach(select => {
            if (select.tagName === 'SELECT') {
              Array.from(select.options).forEach(opt => {
                if (opt.value) {
                  this.productVariants.push({
                    id: parseInt(opt.value),
                    title: opt.textContent,
                    available: !opt.disabled,
                    price: opt.dataset.price ? parseInt(opt.dataset.price) : null
                  });
                }
              });
            }
          });
        }
        
        // Method 3: Try to fetch from product.json endpoint
        const handle = this.container?.dataset?.productHandle;
        if (handle && this.productVariants.length === 0) {
          // Will fetch async in loadOptions
          this.needsVariantFetch = true;
        }
        
      } catch (e) {
        console.warn('[GeniusOptions] Could not parse product variants:', e);
        this.productVariants = [];
      }
    },

    /**
     * Setup variant synchronization with the product form
     */
    setupVariantSync() {
      // Store reference for global access
      window.GeniusOptions = this;
      
      // Watch for variant changes from Shopify's native selectors
      const variantSelectors = document.querySelectorAll('[data-variant-option], .single-option-selector, [name^="options"]');
      variantSelectors.forEach(selector => {
        selector.addEventListener('change', () => {
          this.syncFromNativeOptions();
        });
      });
      
      console.log('[GeniusOptions] Variant sync setup complete');
    },
    
    /**
     * Sync our options with Shopify's native variant selectors
     */
    syncFromNativeOptions() {
      // This syncs when user changes Shopify's native options (like size, color)
      // We can update our display to match
      const currentVariant = this.getCurrentVariantFromForm();
      if (currentVariant) {
        this.currentVariantId = currentVariant.id;
        console.log('[GeniusOptions] Synced to variant:', currentVariant.id);
      }
    },
    
    /**
     * Get currently selected variant from the form
     */
    getCurrentVariantFromForm() {
      const form = document.querySelector('form[action*="/cart/add"]');
      if (!form) return null;
      
      const variantInput = form.querySelector('input[name="id"], select[name="id"]');
      if (!variantInput) return null;
      
      const variantId = parseInt(variantInput.value);
      return this.productVariants.find(v => v.id === variantId);
    },
    
    /**
     * Find variant that matches selected options
     */
    findMatchingVariant(optionValues) {
      if (!this.productVariants.length) return null;
      
      // Try to find exact match
      return this.productVariants.find(variant => {
        if (!variant.available) return false;
        
        // Check if all option values match
        const variantOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);
        return optionValues.every(val => variantOptions.includes(val));
      });
    },
    
    /**
     * Update the product form's variant ID
     */
    updateFormVariant(variantId) {
      const form = document.querySelector('form[action*="/cart/add"]');
      if (!form) return;
      
      const variantInput = form.querySelector('input[name="id"], select[name="id"]');
      if (variantInput) {
        variantInput.value = variantId;
        console.log('[GeniusOptions] Updated form variant to:', variantId);
        
        // Trigger change event for Shopify's JS to pick up
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },

    async loadOptions() {
      // First, fetch variants if needed (must not block forever — otherwise proxy never runs)
      const VARIANT_FETCH_MS = 12000;
      if (this.needsVariantFetch && this.config.productHandle) {
        const vAbort = new AbortController();
        const vTimer = setTimeout(function () {
          vAbort.abort();
        }, VARIANT_FETCH_MS);
        try {
          const response = await fetch(
            `/products/${this.config.productHandle}.js`,
            { cache: 'no-store', signal: vAbort.signal }
          );
          clearTimeout(vTimer);
          if (response.ok) {
            const product = await response.json();
            this.productVariants = product.variants || [];
            console.log('[GeniusOptions] Fetched variants:', this.productVariants.length);
          }
        } catch (e) {
          clearTimeout(vTimer);
          const aborted =
            (e && e.name === 'AbortError') ||
            (typeof DOMException !== 'undefined' &&
              e instanceof DOMException &&
              e.name === 'AbortError');
          console.warn(
            '[GeniusOptions] Could not fetch product variants:',
            aborted ? 'timeout after ' + VARIANT_FETCH_MS + 'ms' : e
          );
        }
      }
      
      // Now load our custom options
      const proxyUrl = this.config.appProxyUrl || `/apps/genius-options`;
      const params = new URLSearchParams({
        action: 'options',
        product_id: this.config.productId,
        title: this.config.productTitle || '',
        tags: this.config.productTags || '',
        type: this.config.productType || '',
        vendor: this.config.productVendor || '',
        collections: this.config.collectionHandles || '',
        sku: this.config.productSku || '',
        skus: this.config.productSkus || '',
        handle: this.resolveProductHandle(this.config.productHandle),
        shop: this.config.shop || '',
        locale: (this.config.locale || 'en').toLowerCase().slice(0, 2),
      });
      
      console.log('[GeniusOptions] Loading options with params:', Object.fromEntries(params));
      console.log('[GeniusOptions] Product title being sent:', this.config.productTitle);
      
      const FETCH_MS = 22000;
      const controller = new AbortController();
      const timeoutId = setTimeout(function () {
        controller.abort();
      }, FETCH_MS);

      try {
        const response = await fetch(`${proxyUrl}?${params}`, {
          cache: 'no-store',
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (!response.ok) throw new Error('Failed to fetch options');
        const data = await response.json();
        try {
          console.log(
            '[GeniusOptions] API response:',
            JSON.stringify(data).substring(0, 200)
          );
        } catch (logErr) {
          console.log('[GeniusOptions] API response received (log stringify skipped)', logErr);
        }
        this.optionSets = data.optionSets || [];

        const zclApi =
          data.zhichunComLayout != null && String(data.zhichunComLayout).trim()
            ? String(data.zhichunComLayout).trim()
            : '';
        const zclDom = (
          this.container.dataset.geniusComLayout ||
          this.container.dataset.comLayout ||
          ''
        ).trim();
        this.config.zhichunComLayout = zclApi || zclDom;

        const zdsRaw =
          data.zhichunDatasheetStrip != null ? String(data.zhichunDatasheetStrip).trim() : '';
        this.config.zhichunDatasheetStrip = zdsRaw;

        // Store full product context (collections, product_type, metafields, etc.)
        if (data.debug && data.debug.productContext) {
          this.config.productContext = data.debug.productContext;
        }

        // For Android Panel PC products, never show Embedded Android Board \"Model Accessories\" sets
        const pcCtx = this.config.productContext || {};
        let collections = pcCtx.collections || [];
        collections = geniusEnrichEmbeddedBoardCollectionsFromTags(
          this.config.productTags,
          collections
        );
        collections = geniusEnrichPanelPcCollectionsFromTags(
          this.config.productTags,
          collections
        );
        if (Array.isArray(collections) && collections.length) {
          pcCtx.collections = collections;
          this.config.productContext = pcCtx;
        }
        const productType = (pcCtx.product_type || pcCtx.productType || '').toLowerCase();
        const handleLo = String(
          this.resolveProductHandle(this.config.productHandle) || ''
        ).toLowerCase();
        const titleLo = String(this.config.productTitle || '').toLowerCase();
        const vendorLo = String(this.config.productVendor || '').toLowerCase();
        const tagsCsv = String(this.config.productTags || '');
        const skusCsv = [this.config.productSkus, this.config.productSku]
          .filter(Boolean)
          .join(',');
        const sbcLike = geniusLooksLikeStandaloneSbcOrGateway(
          handleLo,
          titleLo,
          productType,
          vendorLo,
          tagsCsv,
          skusCsv
        );
        const handleLooksApc =
          handleLo.includes('gz-apc') || /(?:^|[-_])apc\d/i.test(handleLo);
        const isAndroidPanelPc =
          !sbcLike &&
          ((Array.isArray(collections) &&
            collections.includes('android-panel-pc')) ||
            productType.includes('android panel pc') ||
            (productType.includes('android') && productType.includes('panel pc')) ||
            titleLo.includes('android panel pc') ||
            (titleLo.includes('android') && titleLo.includes('panel pc')) ||
            handleLooksApc);

        const colsLoEmb = (collections || []).map(function (c) {
          return String(c || '').toLowerCase();
        });
        const stripEmbeddedMatrices =
          sbcLike &&
          colsLoEmb.indexOf('embedded-android-boards') === -1 &&
          colsLoEmb.indexOf('development-boards') === -1 &&
          colsLoEmb.indexOf('arm-single-board-computers') === -1;

        if (sbcLike && Array.isArray(this.optionSets)) {
          this.optionSets = this.optionSets.filter(function (set) {
            const n = (set.name || '').toLowerCase();
            if (n.includes('zhichun')) return true;
            if (n.includes('android panel pc')) return false;
            if (n.includes('windows panel pc')) return false;
            if (stripEmbeddedMatrices) {
              const groups = set.optionGroups || [];
              const hasLcdTouch = groups.some(function (g) {
                const gn = String(g.name || '').toLowerCase();
                return gn.includes('lcd_module') || gn.includes('touch_module');
              });
              if (hasLcdTouch) return false;
              if (
                n.includes('model accessories') ||
                /\bd[\s_-]?3588\b/.test(n) ||
                n.includes('open frame') ||
                n.includes('open-frame')
              )
                return false;
            }
            return true;
          });
        }

        if (isAndroidPanelPc && Array.isArray(this.optionSets)) {
          this.optionSets = this.optionSets.filter((set) => {
            const name = (set.name || '').toLowerCase();
            return !name.includes('model accessories');
          });
        }

        if (Array.isArray(this.optionSets)) {
          this.optionSets = geniusPreferCollectionOrHandleScopedSets(this.optionSets);
        }

        var colsUnifiedR6s = [];
        if (Array.isArray(collections)) {
          for (var ri = 0; ri < collections.length; ri++) {
            colsUnifiedR6s.push(String(collections[ri]));
          }
        }
        geniusCsvTokens(this.config.collectionHandles).forEach(function (ch) {
          if (colsUnifiedR6s.indexOf(ch) === -1) colsUnifiedR6s.push(ch);
        });

        if (
          Array.isArray(this.optionSets) &&
          geniusProductQualifiesForNanoPiR6SExclusive(handleLo, tagsCsv, colsUnifiedR6s)
        ) {
          const onlyR6s = this.optionSets.filter(function (s) {
            const n = (s.name || '').toLowerCase();
            return n.includes('nanopi r6s') || n.includes('r6s hardware');
          });
          if (onlyR6s.length) {
            console.log(
              '[GeniusOptions] NanoPi R6S (tag/collection/handle) →',
              onlyR6s.length,
              'dedicated set(s) only'
            );
            this.optionSets = onlyR6s;
          }
        }

        if (data.locale) {
          this.config.locale = data.locale.toLowerCase().slice(0, 2);
          this.setLocale(this.config.locale);
          this.updateLocaleInDom();
        }

        // Store metafields from response for metafield-based option groups
        if (data.debug && data.debug.productContext && data.debug.productContext.metafields) {
          this.config.productMetafields = data.debug.productContext.metafields;
          console.log('[GeniusOptions] Stored product metafields:', this.config.productMetafields);
          console.log('[GeniusOptions] Metafield custom.cpu_soc:', this.config.productMetafields?.custom?.cpu_soc);
          
          // If we've already rendered, update metafield-based groups now
          const contentEl = this.container.querySelector('.genius-options__content');
          if (contentEl && contentEl.style.display !== 'none') {
            console.log('[GeniusOptions] Re-evaluating metafield conditions after metafields loaded');
            this.setupMetafieldBasedOptions();
          }
        } else {
          console.warn('[GeniusOptions] No metafields found in response!');
          console.log('[GeniusOptions] Debug data:', data.debug);
        }
      } catch (error) {
        clearTimeout(timeoutId);
        const isAbort =
          (error && error.name === 'AbortError') ||
          (typeof DOMException !== 'undefined' &&
            error instanceof DOMException &&
            error.name === 'AbortError');
        console.warn(
          'Could not fetch from proxy:',
          isAbort ? 'timeout after ' + FETCH_MS + 'ms' : error
        );
          this.optionSets = [];
        this.config.zhichunDatasheetStrip = '';
      }
    },

    hideWidget() {
      const loading = this.container.querySelector('.genius-options__loading');
      const globalTrust = this.container.querySelector('[data-genius-global-trust]');
      const content = this.container.querySelector('.genius-options__content');
      const summary = this.container.querySelector('.genius-options__summary');
      const error = this.container.querySelector('.genius-options__error');

      if (loading) loading.style.display = 'none';

      /** Varsayılan yapılandırma / checkout bilgi şeridi — seçenek seti eşleşmese de göster */
      if (globalTrust) {
        this.container.style.display = '';
        globalTrust.classList.add('genius-options__global-trust--visible');
        if (content) {
          content.style.display = 'none';
          content.innerHTML = '';
        }
        if (summary) summary.style.display = 'none';
        if (error) error.style.display = 'none';
        return;
      }

      this.container.style.display = 'none';
    },

    /** Avoid `querySelector(\`[data-group-name="${name}"]\`)` — breaks on special characters / throws. */
    findOptionGroupElementByName(name) {
      if (!this.container) return null;
      const want = String(name || '');
      const nodes = this.container.querySelectorAll('[data-group-name]');
      for (let i = 0; i < nodes.length; i++) {
        if ((nodes[i].getAttribute('data-group-name') || '') === want) return nodes[i];
      }
      return null;
    },

    findDependentGroupElementsForParent(parentName) {
      if (!this.container) return [];
      const want = String(parentName || '');
      const out = [];
      const nodes = this.container.querySelectorAll('[data-depends-on-group]');
      for (let i = 0; i < nodes.length; i++) {
        if ((nodes[i].getAttribute('data-depends-on-group') || '') === want) out.push(nodes[i]);
      }
      return out;
    },

    /** genovadonanim.com.tr: beyaz / gri / siyah seçenek paneli (CSS: --palette-mono). */
    applyGenovaMonochromePaletteIfNeeded() {
      if (!this.container) return;
      if (this.shouldForceTurkishStorefrontUi()) {
        this.container.classList.add('genius-options--palette-mono');
      }
    },

    /** genovadonanim.com.tr: vitrin metinleri her zaman TR (ShopSettings locale / API en kalmasın). */
    shouldForceTurkishStorefrontUi() {
      try {
        const host = (
          typeof window !== 'undefined' && window.location && window.location.hostname
            ? String(window.location.hostname)
            : ''
        ).toLowerCase();
        if (host.includes('genovadonanim')) return true;
        const shop = String(this.config && this.config.shop ? this.config.shop : '').toLowerCase();
        if (shop.includes('genovadonanim')) return true;
      } catch (_) {}
      return false;
    },

    setLocale(locale) {
      let l = (locale || 'en').toLowerCase().slice(0, 2);
      if (this.shouldForceTurkishStorefrontUi()) l = 'tr';
      if (this.config) this.config.locale = l;
      if (this.container) this.container.setAttribute('data-locale', l);
      this.strings = l === 'tr' ? { ...STRINGS_TR } : { ...STRINGS_EN };
    },

    updateLocaleInDom() {
      if (!this.container) return;
      const loadingText = this.container.querySelector('[data-genius-loading-text]');
      if (loadingText) loadingText.textContent = this.strings.loading;
      const errorText = this.container.querySelector('[data-genius-error-text]');
      if (errorText) errorText.textContent = this.strings.error;
      const totalLabel = this.container.querySelector('[data-genius-total-label]');
      if (totalLabel) totalLabel.textContent = this.strings.total;
      const resetBtn = this.container.querySelector('[data-genius-reset-options]');
      if (resetBtn) resetBtn.textContent = this.strings.resetSelections;
      const quoteLabel = this.container.querySelector('[data-genius-quote-label]');
      if (quoteLabel) quoteLabel.textContent = this.strings.wholesaleQuote;

      this.container.querySelectorAll('.genius-option__qty-minus').forEach((btn) => {
        btn.setAttribute('aria-label', this.strings.qtyDecrease);
      });
      this.container.querySelectorAll('.genius-option__qty-plus').forEach((btn) => {
        btn.setAttribute('aria-label', this.strings.qtyIncrease);
      });
      this.container.querySelectorAll('.genius-option__qty-input').forEach((inp) => {
        inp.setAttribute('aria-label', this.strings.qtyInputAria);
        inp.setAttribute('title', this.strings.qtyInputAria);
      });
    },

    hasZhichunProductTag() {
      const tagsRaw = String(this.config?.productTags || '');
      const tagParts = tagsRaw
        .split(/[,;|]/)
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
      if (
        tagParts.some((t) => t.includes('zhichun')) ||
        tagsRaw.toLowerCase().includes('zhichun')
      ) {
        return true;
      }
      const h = String(this.resolveProductHandle(this.config.productHandle) || '')
        .toLowerCase()
        .trim();
      // Shopify etiketi yokken bile Zhichun seed handle listesi bu SKU’ları kapsar; companion + Zhichun birlikte render.
      if (h.startsWith('gz-apc') || h.startsWith('gz-wpc')) return true;
      return false;
    },

    isAndroidPanelPcProduct() {
      const type = (this.config.productType || '').toLowerCase();
      const title = (this.config.productTitle || '').toLowerCase();
      const col = (this.config.collectionHandles || '').toLowerCase();
      const handle = String(
        this.resolveProductHandle(this.config.productHandle) || ''
      ).toLowerCase();
      // GZ-APC… / …-apc156… — başlık sadece SoC (ör. RK3576) ise tema koleksiyonu da eksik kalabiliyor
      if (handle.includes('gz-apc') || /(?:^|[-_])apc\d/i.test(handle)) return true;
      if (col.includes('android-panel-pc')) return true;
      if (type.includes('android panel pc') || title.includes('android panel pc')) return true;
      if (type.includes('android') && type.includes('panel pc')) return true;
      if (title.includes('android') && title.includes('panel pc')) return true;
      return false;
    },

    isWindowsPanelPcProduct() {
      const type = (this.config.productType || '').toLowerCase();
      const title = (this.config.productTitle || '').toLowerCase();
      const col = (this.config.collectionHandles || '').toLowerCase();
      const tags = String(this.config.productTags || '').toLowerCase();
      const skuBlob = [this.config.productSku, this.config.productSkus].filter(Boolean).join(' ').toLowerCase();
      const handle = String(
        this.resolveProductHandle(this.config.productHandle) || ''
      ).toLowerCase();
      if (handle.includes('gz-wpc') || /(?:^|[-_])wpc\d/i.test(handle)) return true;
      if (/^gh-pc/i.test(skuBlob)) return true;
      if (tags.includes('pink') || tags.includes('green')) {
        if (title.includes('panel pc') || title.includes('industrial panel')) return true;
      }
      if (col.includes('windows') && col.includes('panel')) return true;
      if (col.includes('industrial-panel-pc')) return true;
      if (type.includes('windows panel pc') || title.includes('windows panel pc')) return true;
      if (type.includes('windows') && type.includes('panel pc')) return true;
      if (title.includes('windows') && title.includes('panel pc')) return true;
      if (title.includes('fanless') && title.includes('panel pc') && title.includes('intel')) return true;
      return false;
    },

    /** Ana panel seti (RAM/eMMC/CPU); proxy ile Zhichun setine eklenir. */
    isPanelPcCompanionSetName(setName) {
      const n = String(setName || '').toLowerCase();
      if (this.isAndroidPanelPcProduct()) {
        return (
          n === 'android panel pc options' ||
          n.startsWith('android panel pc options (')
        );
      }
      if (this.isWindowsPanelPcProduct()) {
        return (
          n === 'windows panel pc options' ||
          n.startsWith('windows panel pc options (') ||
          n.includes('windows industrial panel pc')
        );
      }
      return (
        n === 'android panel pc options' ||
        n.startsWith('android panel pc options (') ||
        n === 'windows panel pc options' ||
        n.startsWith('windows panel pc options (') ||
        n.includes('windows industrial panel pc')
      );
    },

    /**
     * SYNC: `app/lib/panel-companion-canonical.ts` — rank/priority + Zhichun RK/A133 affinity;
     * burada aynı mantık (theme asset TS import edemediği için çift yazılı).
     */
    sortPanelCompanionSetsByCanonicalName(companions) {
      const rank = (nameRaw) => {
        const n = String(nameRaw || '').toLowerCase().trim();
        if (n === 'android panel pc options' || n === 'windows panel pc options') return 0;
        if (
          n.startsWith('android panel pc options (') ||
          n.startsWith('windows panel pc options (')
        )
          return 2;
        return 1;
      };
      const mf = this.config.productMetafields?.custom || {};
      const mfSoc = mf.cpu_soc != null ? String(mf.cpu_soc) : mf.soc != null ? String(mf.soc) : '';
      const blob = [
        this.config.productTitle || '',
        this.config.productType || '',
        this.config.productTags || '',
        mfSoc,
      ]
        .join(' ')
        .toUpperCase();
      const prefersA133 =
        blob.includes('A133P') ||
        blob.includes('A133 P') ||
        blob.includes('ALLWINNER A133');
      const prefersRkOther = /\bRK\d{4}/.test(blob) && !prefersA133;

      const variantAffinity = (nameRaw) => {
        const n = String(nameRaw || '').toLowerCase();
        if (prefersA133 && n.includes('a133p')) return 2;
        if (prefersRkOther && n.includes('other cpus')) return 2;
        return 0;
      };

      return [...companions].sort((a, b) => {
        const va = variantAffinity(a.name);
        const vb = variantAffinity(b.name);
        if (va !== vb) return vb - va;

        const ra = rank(a.name);
        const rb = rank(b.name);
        if (ra !== rb) return rb - ra;
        return (b.priority ?? 0) - (a.priority ?? 0);
      });
    },

    /**
     * Zhichun Windows panel (GZ-WPC / zhichun-celeron): Industrial companion RAM/OS/SSD/4G tekrarlar.
     */
    isZhichunWindowsCatalogProduct() {
      if (!this.hasZhichunProductTag() || !this.isWindowsPanelPcProduct()) return false;
      const tags = String(this.config.productTags || '').toLowerCase();
      if (tags.includes('zhichun-celeron')) return true;
      const h = String(this.resolveProductHandle(this.config.productHandle) || '')
        .toLowerCase()
        .trim();
      if (h.startsWith('gz-wpc')) return true;
      return false;
    },

    /**
     * Zhichun + Panel PC: Android’da companion + Zhichun; Windows Zhichun katalogda yalnızca Zhichun.
     */
    getOptionSetsToRender(sortedSets) {
      if (!sortedSets.length) return [];
      const hasZhichun = this.hasZhichunProductTag();
      const zhichunSets = sortedSets.filter((s) =>
        (s.name || '').toLowerCase().includes('zhichun')
      );
      if (hasZhichun && zhichunSets.length && this.isZhichunWindowsCatalogProduct()) {
        const zh = [...zhichunSets].sort(
          (a, b) => (b.priority ?? 0) - (a.priority ?? 0)
        )[0];
        console.log('[GeniusOptions] Zhichun Windows catalog → Zhichun set only:', zh?.name);
        return zh ? [zh] : [];
      }
      const companions = sortedSets.filter((s) => this.isPanelPcCompanionSetName(s.name));
      if (hasZhichun && zhichunSets.length && companions.length) {
        const zh = [...zhichunSets].sort(
          (a, b) => (b.priority ?? 0) - (a.priority ?? 0)
        )[0];
        const comp = this.sortPanelCompanionSetsByCanonicalName(companions)[0];
        if (zh && comp && zh.id !== comp.id) {
          console.log(
            '[GeniusOptions] Zhichun + panel: rendering',
            comp.name,
            'then',
            zh.name
          );
          return [comp, zh];
        }
      }
      const one = this.selectBestOptionSet(sortedSets) || sortedSets[0];
      return one ? [one] : [];
    },

    /**
     * Zhichun + Android companion: remove overlapping UI — Zhichun already has priced
     * M.2 4G/5G radios; Android set’s legacy regional “4G LTE Module” duplicates that.
     * Also hide the `zhichun_android_wwan_slot` group (legacy: option `android_opt_4g5g_slot` on
     * `zhichun_android_matrix`) — overlaps priced M.2 4G/5G above.
     */
    dedupeZhichunAndroidOverlaps(setsToRender) {
      if (!Array.isArray(setsToRender) || setsToRender.length < 2) {
        return setsToRender;
      }
      const hasZhichun = setsToRender.some((s) =>
        (s.name || '').toLowerCase().includes('zhichun')
      );
      const hasAndroidCompanion =
        hasZhichun &&
        this.isAndroidPanelPcProduct() &&
        setsToRender.some((s) => {
          const n = (s.name || '').toLowerCase();
          return (
            n === 'android panel pc options' ||
            n.startsWith('android panel pc options (')
          );
        });
      if (!hasAndroidCompanion) return setsToRender;

      return setsToRender.map((set) => {
        const nameLo = (set.name || '').toLowerCase();
        const isZhichun = nameLo.includes('zhichun');
        const isAndroidCompanion =
          nameLo === 'android panel pc options' ||
          nameLo.startsWith('android panel pc options (');

        if (isAndroidCompanion) {
          const nextGroups = (set.optionGroups || []).filter((g) => {
            const gn = (g.name || '').toLowerCase();
            return gn !== '4g_module';
          });
          if (nextGroups.length === (set.optionGroups || []).length) return set;
          return { ...set, optionGroups: nextGroups };
        }

        if (isZhichun) {
          let touched = false;
          const nextGroups = (set.optionGroups || [])
            .filter((g) => {
              if ((g.name || '') === 'zhichun_android_wwan_slot') {
                touched = true;
                return false;
              }
              return true;
            })
            .map((g) => {
              if ((g.name || '') !== 'zhichun_android_matrix') return g;
              const opts = (g.options || []).filter(
                (o) => String(o.value || '') !== 'android_opt_4g5g_slot'
              );
              if (opts.length !== (g.options || []).length) touched = true;
              return { ...g, options: opts };
            });
          return touched ? { ...set, optionGroups: nextGroups } : set;
        }

        return set;
      });
    },

    /** Factory COM line for Zhichun `com_matrix_default` (handle = Genius Model No). */
    getZhichunComDefaultLabel(handle) {
      const h = String(handle || "").trim().toLowerCase();
      if (!h) return null;
      const fromCatalogOrMeta = String(
        this.config?.zhichunComLayout || ''
      ).trim();
      if (fromCatalogOrMeta) return fromCatalogOrMeta;
      const tr =
        String(this.config?.locale || "")
          .toLowerCase()
          .slice(0, 2) === "tr";
      const exact =
        (tr && ZHICHUN_COM_DEFAULT_LABEL_BY_HANDLE_TR[h]) ||
        ZHICHUN_COM_DEFAULT_LABEL_BY_HANDLE[h];
      if (exact) return exact;
      if (ZHICHUN_COM_SHORT_HANDLE_SET.has(h)) {
        return this.strings.zhichunComDefaultShort;
      }
      if (ZHICHUN_COM_LONG_HANDLE_SET.has(h)) {
        return this.strings.zhichunComDefaultLong;
      }
      return null;
    },

    applyZhichunSerialPortDisplayOverrides(group, optionsForRendering) {
      if ((group.name || "") !== "zhichun_serial_ports") return optionsForRendering;
      const handle = this.resolveProductHandle(this.config.productHandle);
      const label = this.getZhichunComDefaultLabel(handle);
      if (!label) return optionsForRendering;
      return optionsForRendering.map((opt) => {
        if (String(opt.value || "") !== "com_matrix_default") return opt;
        return { ...opt, displayValue: label };
      });
    },

    buildHtmlForOptionSet(optionSet) {
      let html = '';
      console.log('[GeniusOptions] Processing option set:', optionSet.name);
      const groups = Array.isArray(optionSet?.optionGroups) ? optionSet.optionGroups : [];
      if (!groups.length) {
        console.warn('[GeniusOptions] Option set has no optionGroups, skipping:', optionSet?.name);
        return '';
      }
      groups.forEach((group) => {
        console.log(
          '[GeniusOptions] Group:',
          group.name,
          'dependsOnGroupName:',
          group.dependsOnGroupName,
          'dependsOnOptionValue:',
          group.dependsOnOptionValue
        );
      });

        const parentGroups = [];
        const dependentGroups = {};
        
      groups.forEach((group) => {
          if (group.dependsOnGroupName) {
            if (!dependentGroups[group.dependsOnGroupName]) {
              dependentGroups[group.dependsOnGroupName] = [];
            }
            dependentGroups[group.dependsOnGroupName].push(group);
          } else {
            parentGroups.push(group);
          }
        });
        
        parentGroups.sort((a, b) => {
          const na = String(a.name || '').toLowerCase();
          const nb = String(b.name || '').toLowerCase();
          const ia = ZHICHUN_GROUP_RENDER_ORDER.indexOf(na);
          const ib = ZHICHUN_GROUP_RENDER_ORDER.indexOf(nb);
          const za = ia >= 0;
          const zb = ib >= 0;
          if (za && zb && ia !== ib) return ia - ib;
          const rank = (g) => {
            if (this.isCpuGroup(g)) return 10;
            if (this.isRamGroup(g)) return 20;
            const n = `${g.name || ''} ${g.displayName || ''}`.toLowerCase();
            if (/ssd|msata|nvme|\bsata\b|storage/.test(n)) return 30;
            if (/com port|serial|rs232|rs485/.test(n)) return 40;
            return 80;
          };
          const ra = rank(a);
          const rb = rank(b);
          if (ra !== rb) return ra - rb;
          const sortOrderA =
            a.sortOrder !== null && a.sortOrder !== undefined ? a.sortOrder : 999999;
          const sortOrderB =
            b.sortOrder !== null && b.sortOrder !== undefined ? b.sortOrder : 999999;
          if (sortOrderA !== sortOrderB) return sortOrderA - sortOrderB;
          return 0;
        });
        
      Object.keys(dependentGroups).forEach((parentName) => {
          dependentGroups[parentName].sort((a, b) => {
          const sortOrderA =
            a.sortOrder !== null && a.sortOrder !== undefined ? a.sortOrder : 999999;
          const sortOrderB =
            b.sortOrder !== null && b.sortOrder !== undefined ? b.sortOrder : 999999;
            return sortOrderA - sortOrderB;
          });
        });
        
      parentGroups.forEach((group) => {
          const children = dependentGroups[group.name] || [];
          
          if (children.length > 0) {
          html += `<div class="genius-option-linked" data-parent-group="${this.escapeAttr(
            group.name || ''
          )}">`;
            html += `<div class="genius-option-linked__header">
              <h4 class="genius-option-linked__title">${this.escapeHtml(group.displayName)} Options</h4>
            </div>`;
            html += `<div class="genius-option-linked__row">`;
          html += this.renderOptionGroup(group, optionSet, true);
          children.forEach((child) => {
              html += this.renderOptionGroup(child, optionSet, true);
            });
            html += `</div></div>`;
          } else {
            html += this.renderOptionGroup(group, optionSet, false);
          }
        });

      return html;
    },

    render() {
      const loading = this.container.querySelector('.genius-options__loading');
      const content = this.container.querySelector('.genius-options__content');
      const summary = this.container.querySelector('.genius-options__summary');
      const error = this.container.querySelector('.genius-options__error');

      if (this.optionSets.length === 0) {
        this.hideWidget();
        return;
      }

      if (!content || !loading) {
        console.error('[GeniusOptions] render: missing DOM nodes', {
          hasContent: !!content,
          hasLoading: !!loading,
        });
        if (loading) loading.style.display = 'none';
        this.hideWidget();
        return;
      }

      try {
      // Sort option sets by priority (higher priority first)
      const sortedSets = [...this.optionSets].sort((a, b) => {
        const priorityA = a.priority !== null && a.priority !== undefined ? a.priority : 0;
        const priorityB = b.priority !== null && b.priority !== undefined ? b.priority : 0;
        return priorityB - priorityA; // Higher priority first
      });

      const setsToRenderRaw = this.getOptionSetsToRender(sortedSets);
      const setsToRender = this.dedupeZhichunAndroidOverlaps(setsToRenderRaw);
      if (sortedSets.length > 1) {
        console.log(
          '[GeniusOptions] Matched option sets:',
          sortedSets.map((s) => s.name).join(' | ')
        );
        console.log(
          '[GeniusOptions] Rendering:',
          setsToRender.map((s) => s.name).join(' then ')
        );
      }

      let html = '';
      setsToRender.forEach((optionSet) => {
        try {
          html += this.buildHtmlForOptionSet(optionSet);
        } catch (setErr) {
          console.error(
            '[GeniusOptions] buildHtmlForOptionSet failed for set:',
            optionSet?.name,
            setErr
          );
        }
      });

      if (!String(html || '').trim()) {
        console.error('[GeniusOptions] render: empty HTML for sets', setsToRender.map((s) => s.name));
        this.hideWidget();
        return;
      }

      try {
      content.innerHTML = html;
      this.mountLookStage(content);
      } catch (innerErr) {
        console.error('[GeniusOptions] innerHTML assignment failed:', innerErr);
        throw innerErr;
      }
      
      // IMMEDIATELY remove "each" from checkbox prices after DOM insertion
      // This catches any "each" text that might have been added during render
      try {
        const checkboxPrices = content.querySelectorAll('.genius-option--checkbox .genius-option__price');
        checkboxPrices.forEach(priceEl => {
          if (priceEl.textContent && /each/i.test(priceEl.textContent)) {
            let text = priceEl.textContent || priceEl.innerText || '';
            const originalText = text;
            text = text.replace(/\beach\b/gi, '');
            text = text.replace(/\s*each\s*$/i, '');
            text = text.replace(/^\s*each\s*/i, '');
            text = text.replace(/\s+each\s+/gi, ' ');
            text = text.replace(/([^\s])each/gi, '$1');
            text = text.replace(/each([^\s])/gi, '$1');
            text = text.replace(/\s+/g, ' ').trim();
            if (text !== originalText) {
              priceEl.textContent = text;
              console.log('[GeniusOptions] Removed "each" from price immediately after render:', originalText, '->', text);
            }
          }
        });
      } catch (error) {
        console.error('[GeniusOptions] Error in immediate cleanup:', error);
      }
      
      if (loading) loading.style.display = 'none';
      if (error) error.style.display = 'none';
      const globalTrust = this.container.querySelector('[data-genius-global-trust]');
      if (globalTrust) {
        globalTrust.classList.add('genius-options__global-trust--visible');
      }
      content.style.display = 'block';
      
      if (summary) {
        summary.style.display = 'flex';
      }
      } catch (renderErr) {
        console.error('[GeniusOptions] render failed:', renderErr);
        if (loading) loading.style.display = 'none';
        if (error) error.style.display = 'block';
        const globalTrust = this.container.querySelector('[data-genius-global-trust]');
        if (globalTrust) {
          globalTrust.classList.remove('genius-options__global-trust--visible');
        }
        throw renderErr;
      }

      // DOM is visible; failures here must not replace the whole widget with the fatal error panel.
      // Visibility rules must run before setDefaults(), otherwise <select>s in generic
      // ram_other / storage_other get handleSelection() while still visible and prices stick.
      try {
      this.setupScreenSizeBasedOptions();
      this.setupMetafieldBasedOptions();
      this.setDefaults();
      this.setupDependencyListeners();
      
      setTimeout(() => this.setupMetafieldBasedOptions(), 300);
      
      try {
        this.setupCustomDropdowns();
      } catch (error) {
        console.error('[GeniusOptions] Error in setupCustomDropdowns:', error);
      }
      
      try {
        const removeEach = () => {
          try {
            if (this.container) {
              console.log('[GeniusOptions] Running removeEachTextFromCheckboxes cleanup...');
              this.removeEachTextFromCheckboxes();
            } else {
              console.warn('[GeniusOptions] Container not available for removeEachTextFromCheckboxes');
            }
          } catch (error) {
            console.error('[GeniusOptions] Error in removeEach:', error);
          }
        };
        
        console.log('[GeniusOptions] Scheduling removeEachTextFromCheckboxes cleanup...');
        removeEach();
        setTimeout(removeEach, 100);
        setTimeout(removeEach, 500);
        setTimeout(removeEach, 1000);
        setTimeout(removeEach, 2000);
        
        if (!this.eachTextCleanupInterval) {
          console.log('[GeniusOptions] Setting up interval cleanup (every 2s)');
          this.eachTextCleanupInterval = setInterval(removeEach, 2000);
        }
      } catch (error) {
        console.error('[GeniusOptions] Error setting up cleanup:', error);
        }
      } catch (postRenderErr) {
        console.error(
          '[GeniusOptions] Post-render setup failed (options UI left visible):',
          postRenderErr
        );
      }
    },
    
    /**
     * Check metafield conditions and show/hide option groups accordingly
     * Similar to setupScreenSizeBasedOptions but for metafield conditions
     */
    setupMetafieldBasedOptions() {
      let productMetafields = {};
      try {
        productMetafields = this.config.productMetafields
        ? JSON.parse(JSON.stringify(this.config.productMetafields))
        : {};
      } catch (cloneErr) {
        console.error('[GeniusOptions] productMetafields clone failed:', cloneErr);
        productMetafields = {};
      }
      const productTitleRaw = this.config.productTitle || '';
      const productTitle = productTitleRaw.toLowerCase();
      const openFrameCols = [];
      const pcCtxOf = this.config.productContext || {};
      if (Array.isArray(pcCtxOf.collections)) {
        pcCtxOf.collections.forEach(function (c) {
          openFrameCols.push(String(c || ''));
        });
      }
      geniusCsvTokens(this.config.collectionHandles).forEach(function (ch) {
        if (openFrameCols.indexOf(ch) === -1) openFrameCols.push(ch);
      });
      const isOpenFrame = geniusIsOpenFrameAndroidProduct(
        this.resolveProductHandle(this.config.productHandle),
        productTitleRaw,
        this.config.productTags,
        openFrameCols
      );

      if (!productMetafields.custom) {
        productMetafields.custom = {};
      }

      /** Zhichun + Android matrix (xlsx): doğru RAM/Storage grubu için cpu_soc zorunlu. */
      let zhAndroidFactForBom = null;
      const handleLoMc = String(this.config.productHandle || '')
        .toLowerCase()
        .trim();
      const zhMatrixRow = ZHICHUN_ANDROID_FACTORY_MATRIX[handleLoMc];
      if (zhMatrixRow) {
        productMetafields.custom.cpu_soc = String(zhMatrixRow.cpu || '').toLowerCase();
        zhAndroidFactForBom = zhMatrixRow;
        console.log(
          '[GeniusOptions] Zhichun Android matrix (xlsx):',
          handleLoMc,
          '→ cpu_soc',
          productMetafields.custom.cpu_soc,
          'factory RAM/Storage GB',
          zhMatrixRow.ramGb,
          zhMatrixRow.storageGb
        );
      }

      const tagsCsvMb = this.config.productTags || '';
      const skusCsvMb = [this.config.productSkus, this.config.productSku]
        .filter(Boolean)
        .join(',');
      const skipTitleCpuGuess = geniusLooksLikeStandaloneSbcOrGateway(
        this.config.productHandle,
        productTitleRaw,
        this.config.productType || '',
        this.config.productVendor || '',
        tagsCsvMb,
        skusCsvMb
      );

      // Heuristic: if cpu_soc metafield is missing but title clearly contains
      // RK/A133 CPU names, derive a best-effort value so Android Panel PC RAM/Storage
      // groups can still work correctly on stores where metafields are not set.
      if (!skipTitleCpuGuess && !productMetafields.custom.cpu_soc) {
        let cpuGuess = null;
        if (productTitle.includes('rk3568')) cpuGuess = 'rk3568';
        else if (productTitle.includes('rk3576')) cpuGuess = 'rk3576';
        else if (productTitle.includes('rk3588')) cpuGuess = 'rk3588';
        else if (productTitle.includes('rk3288')) cpuGuess = 'rk3288';
        else if (productTitle.includes('a133p') || productTitle.includes('a133 p')) cpuGuess = 'a133p';
        else if (productTitle.includes('a133')) cpuGuess = 'a133';

        if (cpuGuess) {
          productMetafields.custom.cpu_soc = cpuGuess;
          console.log('[GeniusOptions] Derived custom.cpu_soc from title:', cpuGuess, 'for', productTitleRaw);
        }
      }

      // Shopify list metafields often arrive as JSON arrays: ["RK3568"] — normalize
      // so equals/in/not_in match the bare SoC token (and Storage groups stay visible).
      if (productMetafields.custom && productMetafields.custom.cpu_soc != null) {
        const rawCpu = productMetafields.custom.cpu_soc;
        let normalized = rawCpu;
        if (Array.isArray(rawCpu)) {
          normalized = rawCpu.map((x) => String(x || '').trim()).filter(Boolean)[0] || '';
        } else {
          const s = String(rawCpu).trim();
          if (s.startsWith('[')) {
            try {
              const parsed = JSON.parse(s);
              if (Array.isArray(parsed) && parsed.length) {
                normalized = String(parsed[0] || '').trim();
              }
            } catch (_) {
              const m = s.match(/[A-Z]{0,3}\d{3,4}[A-Z]?/i);
              if (m) normalized = m[0];
            }
          }
        }
        if (normalized && normalized !== rawCpu) {
          productMetafields.custom.cpu_soc = normalized;
          console.log('[GeniusOptions] Normalized custom.cpu_soc:', rawCpu, '→', normalized);
        }
      }

      console.log('[GeniusOptions] setupMetafieldBasedOptions called');
      console.log('[GeniusOptions] Product metafields (enriched):', productMetafields);
      console.log('[GeniusOptions] Product title:', this.config.productTitle);
      console.log('[GeniusOptions] Is Open Frame product:', isOpenFrame);
      console.log('[GeniusOptions] Full config:', this.config);
      
      // Hide LCD Module and Touch Module for Open Frame products
      if (isOpenFrame) {
        const allGroups = this.container.querySelectorAll('.genius-option-group');
        allGroups.forEach(groupEl => {
          const displayName = (groupEl.dataset.displayName || '').toLowerCase();
          if (displayName.includes('lcd module') || displayName.includes('touch module')) {
            console.log(`[GeniusOptions] Hiding "${groupEl.dataset.displayName}" for Open Frame product`);
            groupEl.classList.add('genius-option-group--hidden');
            groupEl.style.display = 'none';
            groupEl.style.visibility = 'hidden';
            groupEl.setAttribute('hidden', 'true');
            // Clear selection
            const select = groupEl.querySelector('select');
            if (select) {
              select.value = '';
              select.selectedIndex = 0;
            }
            const radios = groupEl.querySelectorAll('input[type="radio"]:checked');
            radios.forEach(r => r.checked = false);
            const checkboxes = groupEl.querySelectorAll('input[type="checkbox"]:checked');
            checkboxes.forEach(cb => cb.checked = false);
          }
        });
      }
      
      // Find all groups with metafield conditions
      const allGroups = this.container.querySelectorAll('[data-metafield-namespace][data-metafield-key][data-metafield-operator][data-metafield-value]');
      console.log('[GeniusOptions] Found', allGroups.length, 'metafield-based groups');
      
      allGroups.forEach(groupEl => {
        const namespace = groupEl.dataset.metafieldNamespace;
        const key = groupEl.dataset.metafieldKey;
        const operator = groupEl.dataset.metafieldOperator;
        const expectedValue = groupEl.dataset.metafieldValue;
        const groupName = groupEl.dataset.groupName;
        
        // Get actual metafield value
        const actualValue = productMetafields[namespace]?.[key];
        console.log(`[GeniusOptions] Group "${groupName}" checking: ${namespace}.${key} ${operator} "${expectedValue}"`);
        console.log(`[GeniusOptions] Actual value:`, actualValue);
        
        // Evaluate condition
        let shouldShow = false;
        if (actualValue !== undefined && actualValue !== null) {
          const actualStr = String(actualValue).toLowerCase();
          const expectedStr = expectedValue.toLowerCase();
          
          switch (operator) {
            case 'equals':
              shouldShow = actualStr === expectedStr;
              // JSON list metafield leftovers: ["rk3568"] vs rk3568
              if (!shouldShow && actualStr.indexOf(expectedStr) !== -1) {
                shouldShow = true;
              }
              break;
            case 'contains':
              shouldShow = actualStr.includes(expectedStr);
              break;
            case 'not_contains':
              shouldShow = !actualStr.includes(expectedStr);
              break;
            case 'in':
              const expectedArray = expectedValue.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
              shouldShow = expectedArray.includes(actualStr) ||
                expectedArray.some((tok) => tok && actualStr.indexOf(tok) !== -1);
              break;
            case 'not_in':
              const notExpectedArray = expectedValue.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
              shouldShow = !notExpectedArray.includes(actualStr) &&
                !notExpectedArray.some((tok) => tok && actualStr.indexOf(tok) !== -1);
              break;
            default:
              shouldShow = false;
          }
        } else {
          // If metafield is undefined/null, we can't evaluate the condition
          // For metafield-based groups, if the metafield doesn't exist, we hide the group
          // This ensures that only groups with matching metafield values are shown
          shouldShow = false;
          console.log(`[GeniusOptions] Group "${groupName}": Metafield ${namespace}.${key} is undefined/null, hiding group`);
        }
        
        console.log(`[GeniusOptions] Group "${groupName}" shouldShow:`, shouldShow, `(actualValue: ${actualValue}, operator: ${operator}, expectedValue: ${expectedValue})`);
        
        if (shouldShow) {
          groupEl.classList.remove('genius-option-group--hidden');
          groupEl.classList.add('genius-option-group--visible');
          groupEl.style.display = '';
          groupEl.style.visibility = '';
          groupEl.removeAttribute('hidden');
        } else {
          groupEl.classList.add('genius-option-group--hidden');
          groupEl.classList.remove('genius-option-group--visible');
          groupEl.style.display = 'none';
          groupEl.style.visibility = 'hidden';
          groupEl.setAttribute('hidden', 'true');
          // Clear selection
          const select = groupEl.querySelector('select');
          if (select) {
            select.value = '';
            select.selectedIndex = 0;
          }
          const radios = groupEl.querySelectorAll('input[type="radio"]:checked');
          radios.forEach(r => r.checked = false);
          const checkboxes = groupEl.querySelectorAll('input[type="checkbox"]:checked');
          checkboxes.forEach(cb => cb.checked = false);
        }
      });

      // Hard guarantee: at most one visible RAM_* and one STORAGE_* (prefer CPU-specific over *_other).
      this.dedupeVisibleRamStorageGroups();

      // Fallback for products (especially Android Panel PC) where cpu_soc metafield
      // is missing: if *no* RAM/Storage groups are visible at all, force-show
      // the generic ram_other / storage_other groups so customer always has
      // at least one RAM/ROM choice.
      try {
        const allDomGroups = Array.from(
          this.container.querySelectorAll('.genius-option-group')
        );

        const hasVisibleRamOrStorage = allDomGroups.some(groupEl => {
          const name = (groupEl.dataset.groupName || '').toLowerCase();
          const isRamOrStorage =
            name.startsWith('ram_') || name.startsWith('storage_');
          const isVisible =
            !groupEl.classList.contains('genius-option-group--hidden') &&
            groupEl.style.display !== 'none';
          return isRamOrStorage && isVisible;
        });

        if (!hasVisibleRamOrStorage && !zhMatrixRow) {
          console.log(
            '[GeniusOptions] No visible RAM/Storage groups detected. Falling back to ram_other/storage_other.'
          );
          allDomGroups.forEach(groupEl => {
            const name = (groupEl.dataset.groupName || '').toLowerCase();
            if (name === 'ram_other' || name === 'storage_other') {
              groupEl.classList.remove('genius-option-group--hidden');
              groupEl.classList.add('genius-option-group--visible');
              groupEl.style.display = '';
              groupEl.style.visibility = '';
              groupEl.removeAttribute('hidden');
            }
          });
        } else {
          // RAM visible but Storage missing (e.g. A133P before storage_a133p existed)
          const hasVisibleRam = allDomGroups.some((groupEl) => {
            const name = (groupEl.dataset.groupName || '').toLowerCase();
            return (
              name.startsWith('ram_') &&
              !groupEl.classList.contains('genius-option-group--hidden') &&
              groupEl.style.display !== 'none'
            );
          });
          const hasVisibleStorage = allDomGroups.some((groupEl) => {
            const name = (groupEl.dataset.groupName || '').toLowerCase();
            return (
              name.startsWith('storage_') &&
              !groupEl.classList.contains('genius-option-group--hidden') &&
              groupEl.style.display !== 'none'
            );
          });
          if (hasVisibleRam && !hasVisibleStorage) {
            const cpuLo = String(productMetafields?.custom?.cpu_soc || '').toLowerCase();
            const prefer =
              cpuLo.indexOf('a133p') !== -1 ? 'storage_a133p' : 'storage_other';
            console.log(
              '[GeniusOptions] RAM visible but Storage missing → show',
              prefer
            );
            allDomGroups.forEach((groupEl) => {
              const name = (groupEl.dataset.groupName || '').toLowerCase();
              if (name === prefer || (prefer === 'storage_a133p' && name === 'storage_other' && !allDomGroups.some((g) => (g.dataset.groupName || '').toLowerCase() === 'storage_a133p'))) {
                groupEl.classList.remove('genius-option-group--hidden');
                groupEl.classList.add('genius-option-group--visible');
                groupEl.style.display = '';
                groupEl.style.visibility = '';
                groupEl.removeAttribute('hidden');
              }
            });
          }
        }
        // Re-run after fallback so we never leave two RAM or two Storage rows.
        this.dedupeVisibleRamStorageGroups();
      } catch (e) {
        console.warn(
          '[GeniusOptions] Error applying RAM/Storage fallback based on DOM groups:',
          e
        );
      }

      try {
        this.config.productMetafields = productMetafields;
      } catch (persistErr) {
        console.warn('[GeniusOptions] Could not persist productMetafields:', persistErr);
      }

      if (zhAndroidFactForBom) {
        const fact = zhAndroidFactForBom;
        const self = this;
        setTimeout(function () {
          try {
            self.applyZhichunAndroidFactoryBomSelections(fact);
          } catch (bomErr) {
            console.warn('[GeniusOptions] Zhichun factory BOM selections:', bomErr);
          }
        }, 0);
      }
    },

    /**
     * Prefer CPU-specific ram_/storage_ groups over *_other when several match
     * (e.g. JSON cpu_soc ["RK3568"] made both ram_rk3568 and ram_other visible).
     */
    dedupeVisibleRamStorageGroups() {
      if (!this.container) return;
      const hide = (groupEl) => {
        groupEl.classList.add('genius-option-group--hidden');
        groupEl.classList.remove('genius-option-group--visible');
        groupEl.style.display = 'none';
        groupEl.style.visibility = 'hidden';
        groupEl.setAttribute('hidden', 'true');
        const select = groupEl.querySelector('select');
        if (select) {
          select.value = '';
          select.selectedIndex = 0;
        }
      };
      const isVisible = (groupEl) =>
        !groupEl.classList.contains('genius-option-group--hidden') &&
        groupEl.style.display !== 'none';
      const score = (name) => {
        if (name.endsWith('_other')) return 0;
        if (name.includes('a133p')) return 50;
        if (name.includes('rk3588')) return 40;
        if (name.includes('rk3576')) return 30;
        if (name.includes('rk3568')) return 20;
        return 10;
      };
      for (const prefix of ['ram_', 'storage_']) {
        const groups = Array.from(
          this.container.querySelectorAll('.genius-option-group')
        ).filter((el) => {
          const n = (el.dataset.groupName || '').toLowerCase();
          return n.startsWith(prefix) && isVisible(el);
        });
        if (groups.length <= 1) continue;
        groups.sort(
          (a, b) =>
            score((b.dataset.groupName || '').toLowerCase()) -
            score((a.dataset.groupName || '').toLowerCase())
        );
        const keep = groups[0];
        console.log(
          '[GeniusOptions] Dedupe',
          prefix,
          'keep',
          keep.dataset.groupName,
          'hide',
          groups.slice(1).map((g) => g.dataset.groupName).join(', ')
        );
        groups.slice(1).forEach(hide);
      }
    },
    
    /**
     * Detect screen size from product title and show appropriate option groups
     * Uses screenSizeMin/screenSizeMax from backend data
     */
    setupScreenSizeBasedOptions() {
      if (this.shouldHideDisplayBrightnessForProduct()) {
        const brightnessGroups = this.container.querySelectorAll(
          '[data-screen-size-min], [data-screen-size-max], [data-group-name^="highlight_screen_"]'
        );
        brightnessGroups.forEach((groupEl) => {
          groupEl.classList.add('genius-option-group--hidden');
          groupEl.style.display = 'none';
          groupEl.style.visibility = 'hidden';
        });
        console.log('[GeniusOptions] Open Frame product → Display Brightness hidden');
        return;
      }

      const detectedInches = this.detectScreenSize();
      console.log('[GeniusOptions] Detected screen size:', detectedInches, 'inches');
      
      if (detectedInches === null) {
        console.log('[GeniusOptions] No screen size detected, hiding all screen-size-based groups');
      }
      
      // Find all groups with screen size conditions
      const allGroups = this.container.querySelectorAll('[data-screen-size-min], [data-screen-size-max]');
      console.log('[GeniusOptions] Found', allGroups.length, 'screen-size-based groups');
      
      allGroups.forEach(groupEl => {
        const minSize = parseFloat(groupEl.dataset.screenSizeMin) || 0;
        const maxSize = parseFloat(groupEl.dataset.screenSizeMax) || Infinity;
        const groupName = groupEl.dataset.groupName;
        
        console.log(`[GeniusOptions] Group "${groupName}" requires ${minSize}" - ${maxSize}"`);
        
        // Check if detected size falls within range
        const shouldShow = detectedInches !== null && 
                          detectedInches >= minSize && 
                          detectedInches <= maxSize;
        
        console.log(`[GeniusOptions] Group "${groupName}" shouldShow:`, shouldShow);
        
        if (shouldShow) {
          groupEl.classList.remove('genius-option-group--hidden');
          groupEl.classList.add('genius-option-group--visible');
          groupEl.style.display = '';
          groupEl.style.visibility = '';
        } else {
          groupEl.classList.add('genius-option-group--hidden');
          groupEl.classList.remove('genius-option-group--visible');
          // Force hide with inline style in case theme CSS overrides our classes
          groupEl.style.display = 'none';
          groupEl.style.visibility = 'hidden';
          // Clear selection
          const select = groupEl.querySelector('select');
          if (select) select.value = '';
          const radios = groupEl.querySelectorAll('input[type="radio"]:checked');
          radios.forEach(r => r.checked = false);
        }
      });
    },
    
    /**
     * Detect screen size from product title or metafields
     * Returns: number (inches) or null
     */
    detectScreenSize() {
      // Method 1: Try from product handle (most reliable)
      const handle = this.config.productHandle || '';
      console.log('[GeniusOptions] Product handle:', handle);
      
      // Handle patterns: "10-4-fanless..." (10.4"), "15-6-inch..." (15.6"), "21-5-..." (21.5")
      // First try to match XX-Y pattern at start (e.g., "10-4" = 10.4 inches)
      const decimalHandleMatch = handle.match(/^(\d+)-(\d)-/);
      if (decimalHandleMatch) {
        const inches = parseFloat(`${decimalHandleMatch[1]}.${decimalHandleMatch[2]}`);
        if (inches >= 7 && inches <= 50) {
          console.log('[GeniusOptions] Screen size from handle (decimal):', inches);
          return inches;
        }
      }
      
      // Then try simple pattern like "19-intel-..." or "15-inch-..."
      const handleMatch = handle.match(/^(\d+)[-"]/) || handle.match(/(\d+)-?(inch)/i);
      if (handleMatch) {
        const inches = parseFloat(handleMatch[1]);
        if (inches >= 7 && inches <= 50) {
          console.log('[GeniusOptions] Screen size from handle:', inches);
          return inches;
        }
      }
      
      // Method 2: Try from page title
      const pageTitle = document.title || '';
      console.log('[GeniusOptions] Page title:', pageTitle);
      const pageTitleMatch = pageTitle.match(/(\d+\.?\d*)["″''\s]*(inch)?/i);
      if (pageTitleMatch) {
        const inches = parseFloat(pageTitleMatch[1]);
        if (inches >= 7 && inches <= 50) {
          console.log('[GeniusOptions] Screen size from page title:', inches);
          return inches;
        }
      }
      
      // Method 3: Try from H1 or product title element
      const titleSelectors = [
        'h1.product__title',
        '.product__title h1', 
        '.product-single__title',
        'h1[itemprop="name"]',
        '.product-title h1',
        'h1'
      ];
      
      for (const selector of titleSelectors) {
        const el = document.querySelector(selector);
        if (el && el.textContent) {
          const text = el.textContent.trim();
          const match = text.match(/^(\d+\.?\d*)["″''\s]/);
          if (match) {
            const inches = parseFloat(match[1]);
            if (inches >= 7 && inches <= 50) {
              console.log('[GeniusOptions] Screen size from', selector, ':', inches);
              return inches;
            }
          }
        }
      }
      
      // Method 4: Check metafields if available (data attribute)
      const metafieldInches = this.container.dataset.screenInches;
      if (metafieldInches) {
        const inches = parseFloat(metafieldInches);
        console.log('[GeniusOptions] Metafield inches:', inches);
        return inches;
      }
      
      console.log('[GeniusOptions] Could not detect screen size');
      return null; // Unknown screen size
    },
    
    /**
     * Setup listeners for dependent option groups
     */
    setupDependencyListeners() {
      console.log('[GeniusOptions] Setting up dependency listeners...');
      
      // Find all dependent groups
      const dependentGroups = this.container.querySelectorAll('[data-depends-on-group]');
      console.log('[GeniusOptions] Found dependent groups:', dependentGroups.length);
      
      dependentGroups.forEach(depGroup => {
        const parentName = depGroup.dataset.dependsOnGroup;
        const requiredValue = depGroup.dataset.dependsOnValue;
        console.log('[GeniusOptions] Dependent group:', depGroup.dataset.groupName, 'depends on:', parentName, '=', requiredValue);
        
        // Find the parent group
        const parentGroup = this.findOptionGroupElementByName(parentName);
        if (!parentGroup) {
          console.log('[GeniusOptions] Parent group not found:', parentName);
          return;
        }
        
        // Listen for changes on parent
        const inputs = parentGroup.querySelectorAll('input, select');
        console.log('[GeniusOptions] Adding listeners to', inputs.length, 'inputs in parent');
        inputs.forEach(input => {
          input.addEventListener('change', () => {
            console.log('[GeniusOptions] Change event on:', input.tagName, 'value:', input.value);
            this.updateDependentGroups(parentName);
          });
        });
      });
      
      // Initial update - use setTimeout to ensure DOM is ready
      const parentNames = new Set();
      dependentGroups.forEach(g => parentNames.add(g.dataset.dependsOnGroup));
      console.log('[GeniusOptions] Parent names for initial update:', Array.from(parentNames));
      
      // Delay initial update to ensure dropdowns have their values set
      setTimeout(() => {
        parentNames.forEach(name => {
          console.log('[GeniusOptions] Initial update for parent:', name);
          this.updateDependentGroups(name);
        });
      }, 100);
    },
    
    /**
     * Update visibility of dependent groups based on parent selection
     */
    updateDependentGroups(parentName) {
      const parentGroup = this.findOptionGroupElementByName(parentName);
      if (!parentGroup) return;
      
      // Get selected value from parent
      let selectedValue = '';
      const select = parentGroup.querySelector('select');
      const checkedRadio = parentGroup.querySelector('input[type="radio"]:checked');
      
      if (select) {
        selectedValue = select.value;
      } else if (checkedRadio) {
        selectedValue = checkedRadio.value;
      }
      
      console.log('[GeniusOptions] Parent:', parentName, 'Selected:', selectedValue);
      
      // Update all dependent groups
      const dependentGroups = this.findDependentGroupElementsForParent(parentName);
      
      dependentGroups.forEach(depGroup => {
        const requiredValue = depGroup.dataset.dependsOnValue || '';
        
        // If required value is empty, never show (invalid dependency config)
        // If selected value is empty/placeholder, don't show dependent groups
        const shouldShow = requiredValue && selectedValue && 
                           selectedValue.toLowerCase() === requiredValue.toLowerCase();
        
        console.log('[GeniusOptions] Dependent group:', depGroup.dataset.groupName, 'requires:', requiredValue, 'show:', shouldShow);
        
        // Use class-based visibility - must toggle BOTH classes
        if (shouldShow) {
          depGroup.classList.add('genius-option-group--visible');
          depGroup.classList.remove('genius-option-group--hidden');
          depGroup.style.display = '';
          depGroup.style.visibility = '';
        } else {
          depGroup.classList.remove('genius-option-group--visible');
          depGroup.classList.add('genius-option-group--hidden');
          depGroup.style.display = 'none';
          depGroup.style.visibility = 'hidden';
          const depGroupId = depGroup.dataset.groupId;
          if (depGroupId && this.selectedOptions[depGroupId]) {
            delete this.selectedOptions[depGroupId];
          }
          // Clear selection if hiding
          const inputs = depGroup.querySelectorAll('input:checked');
          inputs.forEach(input => input.checked = false);
          const selects = depGroup.querySelectorAll('.genius-option-dropdown');
          selects.forEach(sel => {
            sel.selectedIndex = 0;
          });
        }
      });
      
      // Update price after dependency change
      this.updatePrice();
    },

    renderOptionGroup(group, optionSet, isLinked = false) {
      const setId = optionSet.id;
      const groupId = `${setId}-${group.id}`;
      /** DB may still say `radio` until `npm run db:seed-zhichun`; render as dropdown anyway. */
      const ZHICHUN_GROUPS_AS_DROPDOWN = new Set([
        "zhichun_msata",
        "zhichun_m2_4g_eu",
        "zhichun_m2_5g_global",
      ]);
      let isDropdownGroup = group.inputType === "dropdown";
      if (ZHICHUN_GROUPS_AS_DROPDOWN.has(group.name || "")) {
        isDropdownGroup = true;
      }
      const isCpuDropdown = isDropdownGroup && this.isCpuGroup(group);
      const isSsdCapacityDropdown = isDropdownGroup && this.isSsdCapacityGroup(group);
      let optionsForRendering = isDropdownGroup
        ? (isCpuDropdown
            ? this.getFilteredDropdownOptions(group)
            : (isSsdCapacityDropdown
                ? this.getAdjustedSsdCapacityOptions(group.options || [])
                : (group.options || [])))
        : (group.options || []);

      if (isDropdownGroup && this.isAndroidPanelPcRamGroup(group)) {
        optionsForRendering = this.filterRamOptionsForGciSkuCap(optionsForRendering);
      }

      if (this.isDisplayBrightnessGroup(group) && this.shouldHideDisplayBrightnessForProduct()) {
        return '';
      }

      // CPU: hide when the shopper has no real choice:
      // - one SKU in the admin catalog, or
      // - several SKUs in DB but title/tag filtering leaves exactly one row (shared sets + pinned SKU).
      // If filters remove every selectable row while the catalog still lists several CPUs, restore raw
      // options above — then do not hide here (selectableAfter.length === 0 && rawSelectable.length > 1).
      if (isCpuDropdown) {
        const rawOptions = group.options || [];
        const rawSelectable = rawOptions.filter((opt) => opt?.value && String(opt.value).trim());
        let selectableCpuOptions = optionsForRendering.filter((opt) => opt?.value && String(opt.value).trim());
        if (selectableCpuOptions.length === 0 && rawSelectable.length > 0) {
          console.warn('[GeniusOptions] CPU filter left no selectable options; using raw group options', group.displayName || group.name);
          optionsForRendering = rawOptions;
          selectableCpuOptions = rawSelectable;
        }
      }

      optionsForRendering = this.filterExcludedProductOptions(optionsForRendering);

      optionsForRendering = this.applyZhichunSerialPortDisplayOverrides(
        group,
        optionsForRendering
      );

      if (isCpuDropdown) {
        const rawOptions = group.options || [];
        const rawSelectable = rawOptions.filter((opt) => opt?.value && String(opt.value).trim());
        const selectableAfter = optionsForRendering.filter((opt) => opt?.value && String(opt.value).trim());
        const shouldHideCpu =
          (selectableAfter.length === 1 || rawSelectable.length <= 1) &&
          (selectableAfter.length > 0 || rawSelectable.length <= 1);
        if (shouldHideCpu) {
          const reason =
            selectableAfter.length === 1 && rawSelectable.length > 1
              ? 'single choice after product/catalog filter'
              : 'single SKU in catalog';
          console.log(`[GeniusOptions] Hiding CPU group (${reason}):`, group.displayName || group.name);
          this.seedHiddenSingleCpuGroup(group, groupId, optionsForRendering);
          return '';
        }
      }
      
      // Store group display name in map for later lookup
      this.groupDisplayNames[groupId] = group.displayName;
      console.log(`[GeniusOptions] Storing groupDisplayName: ${groupId} = "${group.displayName}"`);
      
      // Store all options data in map for reliable lookup
      optionsForRendering.forEach(opt => {
        const key = `${groupId}:${opt.value}`;
        const isCheckboxGroup = group.inputType === 'checkbox' || group.isMultiSelect;
        const effectiveMaxQuantity = isCheckboxGroup
          ? Math.max(opt.maxQuantity || 0, 50)
          : (opt.maxQuantity || 50);

        this.optionsDataMap[key] = {
          displayValue: opt.displayValue,
          priceModifier: opt.priceModifier || 0,
          priceModifierType: opt.priceModifierType || 'fixed',
          optionId: opt.id,
          variantOption: opt.variantOption || null,
          imageUrl: opt.imageUrl || '',
          allowQuantity: opt.allowQuantity || false,
          minQuantity: opt.minQuantity || 1,
          maxQuantity: effectiveMaxQuantity,
          isDefault: Boolean(opt.isDefault)
        };
        console.log(`[GeniusOptions] Storing option: ${key} = "${opt.displayValue}" (price: ${opt.priceModifier}, allowQty: ${opt.allowQuantity})`);
      });
      
      // Determine if this is a checkbox (multiselect) group
      const isCheckboxGroup = group.inputType === 'checkbox' || group.isMultiSelect;
      const inputType = isCheckboxGroup ? 'checkbox' : 'radio';

      // Few-choice dropdowns read as mixed UI next to chips — render as rows instead.
      // CPU/RAM: always prefer tappable chips when the list is short enough.
      if (
        isDropdownGroup &&
        !isCheckboxGroup &&
        !ZHICHUN_GROUPS_AS_DROPDOWN.has(group.name || '')
      ) {
        const selectableCount = optionsForRendering.filter(function (opt) {
          return opt && String(opt.value || '').trim();
        }).length;
        const chipLimit =
          isCpuDropdown || this.isRamGroup(group) ? 14 : 5;
        if (selectableCount > 0 && selectableCount <= chipLimit) {
          isDropdownGroup = false;
        }
      }

      let layoutClass = `genius-options-list--${group.layoutType || 'horizontal'}`;
      if (!isDropdownGroup && !isCheckboxGroup) {
        optionsForRendering = optionsForRendering.filter(function (opt) {
          return opt && String(opt.value || '').trim();
        });
        const labels = optionsForRendering.map(function (o) {
          return String(o.displayValue || '').trim();
        });
        const maxLen = labels.reduce(function (m, s) {
          return Math.max(m, s.length);
        }, 0);
        const count = labels.length;
        const shortChips = count >= 2 && count <= 6 && maxLen <= 28;
        if (shortChips) {
          layoutClass = 'genius-options-list--compact';
        } else {
          layoutClass = 'genius-options-list--stack';
        }
      }
      const columnsStyle = group.layoutType === 'grid' && layoutClass.indexOf('genius-options-list--grid') !== -1
        ? `--genius-columns: ${group.columnsCount || 4};` 
        : '';
      // For all checkbox groups, use the compact full-width accessory row styling
      const showLook = !!group.showImages && optionsForRendering.some(function (opt) {
        return opt && opt.imageUrl;
      });
      const checkboxGridClass = isCheckboxGroup && !showLook
        ? 'genius-options-list--checkbox-grid' 
        : '';
      const lookClass = showLook ? 'genius-options-list--look' : '';

      let optionsHtml = '';
      
      const useCustomDropdown =
        isDropdownGroup && this.shouldUseCustomDropdown(optionSet);
      if (isDropdownGroup) {
        optionsHtml = this.renderDropdown(
          group,
          groupId,
          useCustomDropdown,
          optionsForRendering
        );
      } else {
        optionsHtml = `
          <div class="genius-options-list ${layoutClass} ${checkboxGridClass} ${lookClass}" style="${columnsStyle}" role="listbox">
            ${optionsForRendering.map(opt => this.renderOption(opt, group, groupId, inputType)).join('')}
          </div>
        `;
      }

      // Dependency attributes - if dependsOnGroupName is set, treat as dependent
      // Even if dependsOnOptionValue is empty, group should start hidden
      const hasDependency = !!group.dependsOnGroupName;
      const dependencyAttrs = hasDependency 
        ? `data-depends-on-group="${this.escapeHtml(String(group.dependsOnGroupName || ''))}" data-depends-on-value="${this.escapeHtml(String(group.dependsOnOptionValue || ''))}"`
        : '';
      
      // Screen size based visibility attributes
      const hasScreenSizeCondition = (group.screenSizeMin !== null && group.screenSizeMin !== undefined) || 
                                     (group.screenSizeMax !== null && group.screenSizeMax !== undefined);
      const screenSizeAttrs = [];
      if (group.screenSizeMin !== null && group.screenSizeMin !== undefined) {
        screenSizeAttrs.push(`data-screen-size-min="${group.screenSizeMin}"`);
      }
      if (group.screenSizeMax !== null && group.screenSizeMax !== undefined) {
        screenSizeAttrs.push(`data-screen-size-max="${group.screenSizeMax}"`);
      }
      const screenSizeAttrStr = screenSizeAttrs.join(' ');
      
      // Metafield condition attributes
      const hasMetafieldCondition = group.metafieldNamespace && group.metafieldKey && group.metafieldOperator && group.metafieldValue;
      const metafieldAttrs = [];
      if (hasMetafieldCondition) {
        metafieldAttrs.push(
          `data-metafield-namespace="${this.escapeHtml(String(group.metafieldNamespace || ''))}"`
        );
        metafieldAttrs.push(
          `data-metafield-key="${this.escapeHtml(String(group.metafieldKey || ''))}"`
        );
        metafieldAttrs.push(
          `data-metafield-operator="${this.escapeHtml(String(group.metafieldOperator || ''))}"`
        );
        metafieldAttrs.push(
          `data-metafield-value="${this.escapeHtml(String(group.metafieldValue ?? ''))}"`
        );
      }
      const metafieldAttrStr = metafieldAttrs.join(' ');
      
      // Debug screen size conditions
      if (group.name && group.name.includes('touch')) {
        console.log(`[GeniusOptions] Group "${group.name}" screenSizeMin=${group.screenSizeMin} (${typeof group.screenSizeMin}), screenSizeMax=${group.screenSizeMax} (${typeof group.screenSizeMax})`);
        console.log(`[GeniusOptions] Group "${group.name}" hasScreenSizeCondition=${hasScreenSizeCondition}, attrs="${screenSizeAttrStr}"`);
      }
      
      // Debug metafield conditions
      if (hasMetafieldCondition) {
        console.log(`[GeniusOptions] Group "${group.name}" has metafield condition: ${group.metafieldNamespace}.${group.metafieldKey} ${group.metafieldOperator} "${group.metafieldValue}"`);
      }
      
      // Linked class for side-by-side display
      const linkedClass = isLinked ? ' genius-option-group--linked' : '';
      
      // Groups with dependencies, screen size conditions, or metafield conditions start hidden
      const hiddenClass = (hasDependency || hasScreenSizeCondition || hasMetafieldCondition) ? ' genius-option-group--hidden' : '';

      return `
        <div class="genius-option-group${linkedClass}${hiddenClass}" data-group-id="${this.escapeAttr(groupId)}" data-group-name="${this.escapeAttr(group.name || '')}" data-display-name="${this.escapeHtml(group.displayName)}" data-required="${group.isRequired}" ${dependencyAttrs} ${screenSizeAttrStr} ${metafieldAttrStr} ${useCustomDropdown ? 'data-custom-dropdown="true"' : ''}>
          <div class="genius-option-group__header">
            <label class="genius-option-group__title">
              ${this.escapeHtml(group.displayName)}
              ${group.isRequired ? `<span class="genius-option-group__required" aria-hidden="true">*</span>` : ''}
            </label>
          </div>
          ${group.description ? `<p class="genius-option-group__description">${this.escapeHtml(group.description)}</p>` : ''}
          ${optionsHtml}
        </div>
      `;
    },

    renderOption(option, group, groupId, inputType) {
      const optionId = `${groupId}-${option.id}`;
      const modifier = parseFloat(option.priceModifier) || 0;
      
      // For checkboxes, format price WITHOUT "each" - never add it in the first place
      let priceDisplay = '';
      if (inputType === 'checkbox') {
        if (modifier === 0) {
          priceDisplay = 'FREE';
        } else {
          // Format price directly for checkboxes - NO "each" text
          if (option.priceModifierType === 'percent') {
            priceDisplay = modifier > 0 ? `+%${modifier}` : `%${modifier}`;
          } else {
            priceDisplay = modifier > 0 
              ? `+${this.formatCurrency(modifier)}`
              : this.formatCurrency(modifier);
          }
          // Ensure "each" is NEVER in the result
          priceDisplay = priceDisplay.replace(/\beach\b/gi, '').trim();
        }
      } else {
        // For non-checkboxes, use standard formatPriceModifier
        priceDisplay = this.formatPriceModifier(option);
      }
      const hasColor = group.inputType === 'color_swatch' && option.colorCode;
      const hasImage = group.showImages && option.imageUrl;
      
      // Check if this option allows quantity selection
      // For checkbox groups, ALWAYS allow quantity (default true)
      const allowQuantity = inputType === 'checkbox' 
        ? true // Always true for checkboxes
        : (option.allowQuantity || false);
      const minQty = option.minQuantity || 1;
      const maxQty = inputType === 'checkbox'
        ? Math.max(option.maxQuantity || 0, 50)
        : (option.maxQuantity || 50);
      
      let visualElement = '';
      let optionClass = 'genius-option';

      if (hasColor) {
        optionClass += ' genius-option--color';
        visualElement = `<span class="genius-option__swatch" style="background-color: ${option.colorCode}"></span>`;
      } else if (hasImage) {
        optionClass += ' genius-option--image';
        visualElement = `<img class="genius-option__image" src="${option.imageUrl}" alt="${this.escapeHtml(option.displayValue)}" loading="lazy">`;
      } else if (inputType === 'checkbox') {
        optionClass += ' genius-option--checkbox genius-option--with-quantity';
        visualElement = `
          <span class="genius-option__check">
            <svg class="genius-option__check-icon" viewBox="0 0 16 16" fill="currentColor">
              <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z"/>
            </svg>
          </span>
        `;
      } else {
        optionClass += ' genius-option--button';
        if (allowQuantity) {
          optionClass += ' genius-option--with-quantity';
        }
        visualElement = '<span class="genius-option__pick" aria-hidden="true"></span>';
      }
      
      // Quantity input HTML - ALWAYS render for checkboxes, placed inside label
      const qtyMinusAria = this.escapeAttr(this.strings.qtyDecrease);
      const qtyPlusAria = this.escapeAttr(this.strings.qtyIncrease);
      const qtyInpAria = this.escapeAttr(this.strings.qtyInputAria);
      const quantityHtml = inputType === 'checkbox' ? `
        <div class="genius-option__quantity" data-option-value="${this.escapeAttr(option.value)}">
          <button type="button" class="genius-option__qty-btn genius-option__qty-minus" data-action="minus" aria-label="${qtyMinusAria}">−</button>
          <input type="number" class="genius-option__qty-input" value="${minQty}" min="${minQty}" max="${maxQty}" data-option-id="${optionId}" aria-label="${qtyInpAria}" title="${qtyInpAria}">
          <button type="button" class="genius-option__qty-btn genius-option__qty-plus" data-action="plus" aria-label="${qtyPlusAria}">+</button>
        </div>
      ` : (allowQuantity ? `
        <div class="genius-option__quantity" data-option-value="${this.escapeAttr(option.value)}">
          <button type="button" class="genius-option__qty-btn genius-option__qty-minus" data-action="minus" aria-label="${qtyMinusAria}">−</button>
          <input type="number" class="genius-option__qty-input" value="${minQty}" min="${minQty}" max="${maxQty}" data-option-id="${optionId}" aria-label="${qtyInpAria}" title="${qtyInpAria}">
          <button type="button" class="genius-option__qty-btn genius-option__qty-plus" data-action="plus" aria-label="${qtyPlusAria}">+</button>
        </div>
      ` : '');
      
      // For checkboxes, priceDisplay is already clean (no "each")
      // For other input types, use priceDisplay as-is
      const priceLabel = priceDisplay;

      return `
        <div class="${optionClass}" role="option" ${allowQuantity ? 'data-allow-quantity="true"' : ''}>
          <input 
            type="${inputType}" 
            id="${this.escapeAttr(optionId)}" 
            name="${this.escapeAttr(groupId)}" 
            value="${this.escapeAttr(option.value)}"
            class="genius-option__input"
            data-price-modifier="${option.priceModifier || 0}"
            data-price-type="${option.priceModifierType || 'fixed'}"
            data-display-value="${this.escapeHtml(option.displayValue)}"
            data-option-id="${this.escapeAttr(option.id)}"
            data-variant-option="${this.escapeAttr(option.variantOption || '')}"
            data-allow-quantity="${allowQuantity}"
            data-min-quantity="${minQty}"
            data-max-quantity="${maxQty}"
            ${option.imageUrl ? `data-image-url="${this.escapeAttr(option.imageUrl)}"` : ''}
            ${option.isDefault && !(inputType === 'radio' && this.isRamDdr4UpgradeButtonGroup(group)) ? 'data-default="true"' : ''}
            ${option.isDisabled ? 'disabled' : ''}
          >
          <label for="${this.escapeAttr(optionId)}" class="genius-option__label">
            ${visualElement}
            <span class="genius-option__content">
              <span class="genius-option__value">${this.escapeHtml(option.displayValue)}</span>
              ${option.helpText ? `<span class="genius-option__help">${this.escapeHtml(option.helpText)}</span>` : ''}
              ${priceLabel ? `<span class="genius-option__price ${modifier === 0 ? 'genius-option__price--included' : 'genius-option__price--extra'}" data-original-price="${this.escapeHtml(priceLabel)}">${this.escapeHtml(priceLabel)}</span>` : ''}
            </span>
            ${quantityHtml}
          </label>
        </div>
      `;
    },

    isCpuGroup(group) {
      const name = `${group?.name || ''} ${group?.displayName || ''}`.toLowerCase();
      return name.includes('cpu') || name.includes('islemci') || name.includes('işlemci') || name.includes('processor');
    },

    isRamGroup(group) {
      const name = `${group?.name || ''} ${group?.displayName || ''}`.toLowerCase();
      if (this.isCpuGroup(group)) return false;
      return (
        /\bram\b/.test(name) ||
        name.includes('ddr') ||
        name.includes('bellek') ||
        name.includes('memory')
      );
    },

    isAndroidPanelPcRamGroup(group) {
      return String(group?.name || '').toLowerCase().startsWith('ram_');
    },

    isDisplayBrightnessGroup(group) {
      const name = String(group?.name || '').toLowerCase();
      const display = String(group?.displayName || '').toLowerCase();
      return (
        name.startsWith('highlight_screen_') ||
        display.includes('display brightness') ||
        display.includes('parlaklık')
      );
    },

    shouldHideDisplayBrightnessForProduct() {
      return geniusIsOpenFrameAndroidProductFromConfig(this.config || {});
    },

    collectProductSkusForRules() {
      const skus = [];
      const push = (s) => {
        const t = String(s || '').trim();
        if (t) skus.push(t);
      };
      push(this.config.productSku);
      geniusCsvTokens(this.config.productSkus).forEach(push);
      if (Array.isArray(this.productVariants)) {
        this.productVariants.forEach((v) => push(v && v.sku));
      }
      return skus;
    },

    /** GCI-S07-… vb. ürünleri SKU önekinden tanır. */
    productHasGciSkuPrefix() {
      return this.collectProductSkusForRules().some((sku) => /^gci/i.test(sku));
    },

    /**
     * GCI ürünlerinde üst RAM sınırı CPU'ya bağlıdır (tedarikçi tablosu):
     *   others / A133P → 4GB
     *   RK3568         → 16GB
     *   RK3576/3576S   → 16GB
     *   RK3588/3588S   → 32GB
     */
    gciRamCapGbFromProductContext() {
      const custom = this.config?.productMetafields?.custom || {};
      const ctxCustom = this.config?.productContext?.metafields?.custom || {};
      const text = [
        this.config?.productTitle || '',
        this.config?.productTags || '',
        this.config?.productSku || '',
        this.config?.productSkus || '',
        custom.cpu_soc || '',
        custom.soc || '',
        ctxCustom.cpu_soc || '',
        ctxCustom.soc || '',
      ]
        .join(' ')
        .toLowerCase()
        .replace(/[\s_-]+/g, '');

      if (text.includes('rk3588')) return 32;
      if (text.includes('rk3576')) return 16;
      if (text.includes('rk3568')) return 16;
      return 4;
    },

    /** @deprecated use gciRamCapGbFromProductContext */
    gciProductSupportsExtendedRam() {
      return this.gciRamCapGbFromProductContext() > 4;
    },

    parseRamGbFromOptionValue(opt) {
      const v = String(opt?.value || '').toLowerCase();
      const d = String(opt?.displayValue || '').toLowerCase();
      const match =
        v.match(/(\d+)\s*g(?:b)?(?:\b|_|$)/) ||
        d.match(/(\d+)\s*g(?:b)?(?:\b|\s|\/)/);
      if (!match) return null;
      const n = parseInt(match[1], 10);
      return Number.isFinite(n) ? n : null;
    },

    filterRamOptionsForGciSkuCap(options, maxGb) {
      const source = Array.isArray(options) ? options : [];
      if (!this.productHasGciSkuPrefix()) return source;

      const cap =
        Number.isFinite(Number(maxGb)) && Number(maxGb) > 0
          ? Number(maxGb)
          : this.gciRamCapGbFromProductContext();

      const filtered = source.filter((opt) => {
        const gb = this.parseRamGbFromOptionValue(opt);
        if (gb == null) return true;
        return gb <= cap;
      });

      if (!filtered.length && source.length) {
        console.warn('[GeniusOptions] GCI RAM cap removed all options; keeping originals');
        return source;
      }

      if (filtered.length !== source.length) {
        console.log('[GeniusOptions] GCI SKU → RAM capped at', cap, 'GB:', {
          before: source.length,
          after: filtered.length,
        });
      } else {
        console.log('[GeniusOptions] GCI SKU → RAM cap', cap, 'GB (all options within limit)');
      }

      return filtered;
    },

    /**
     * DDR4 RAM düğmeleri: varsayılan seçim vitrinde korunur. Zhichun `zhichun_ram_ddr4` için fiyatlar seed tier’dan gelir.
     */
    isRamDdr4UpgradeButtonGroup(group) {
      const gn = String(group?.name || "").toLowerCase();
      const dn = String(group?.displayName || "").toLowerCase();
      if (gn.includes("zhichun_ram_ddr4")) return false;
      if (gn.includes("zhichun_ram") || gn.includes("ram_ddr4")) return true;
      if (/\bddr4\b/.test(dn) && /\bram\b/.test(dn)) return true;
      return false;
    },

    /**
     * SSD capacity rows (mSATA/NVMe/SATA sizes) depend on the ssd_technology parent — not RAM groups.
     */
    isSsdCapacityGroup(group) {
      return String(group?.dependsOnGroupName || '').toLowerCase() === 'ssd_technology';
    },

    /**
     * Discontinued 32 / 64 GB SSD tiers (value keys from admin + common labels).
     */
    isDiscontinuedSsdCapacityOption(opt) {
      const v = String(opt?.value || '').trim().toLowerCase();
      if (v === '32gb' || v === '64gb' || v === '32' || v === '64') return true;
      const d = String(opt?.displayValue || '').trim().replace(/\s+/g, ' ');
      if (/^(32|64)(\s*gb)?$/i.test(d)) return true;
      if (/^(32|64)\s+gb\s+/i.test(d)) return true;
      return false;
    },

    /**
     * Drop 32/64 GB SSD capacities and add a flat surcharge to each remaining tier.
     */
    getAdjustedSsdCapacityOptions(options) {
      const surcharge = SSD_CAPACITY_SURCHARGE_USD;
      const source = Array.isArray(options) ? options : [];
      const placeholderOptions = source.filter((opt) => !opt?.value || !String(opt.value).trim());
      const selectable = source.filter((opt) => opt?.value && String(opt.value).trim());
      const kept = selectable.filter((opt) => !this.isDiscontinuedSsdCapacityOption(opt));

      if (!kept.length && selectable.length) {
        console.warn('[GeniusOptions] SSD capacity filter removed all options; keeping originals');
        return source;
      }

      const adjusted = kept.map((opt) => {
        const isPercent = opt.priceModifierType === 'percent';
        const base = Number(opt.priceModifier || 0);
        return {
          ...opt,
          priceModifier: isPercent ? base : base + surcharge,
          priceModifierType: opt.priceModifierType || 'fixed',
          isDefault: false
        };
      });

      adjusted.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      if (adjusted.length) {
        adjusted[0].isDefault = true;
        const defaultRow = adjusted[0];
        if (defaultRow && defaultRow.priceModifierType !== 'percent') {
          const baseline = Number(defaultRow.priceModifier || 0);
          adjusted.forEach((o) => {
            if (o.priceModifierType === 'percent') return;
            o.priceModifier = Number(o.priceModifier || 0) - baseline;
          });
        }
      }

      return [...placeholderOptions, ...adjusted];
    },

    /**
     * Admin-only notices that must not appear as selectable options (e.g. "Power Wire ayrıca verilmez").
     */
    isPowerWireExcludedNoticeOption(opt) {
      const t = `${opt?.value || ''} ${opt?.displayValue || ''}`.toLowerCase();
      if (!/power\s*wire|powerwire/.test(t)) return false;
      if (/ayrıca\s*verilmez|ayrica\s*verilmez/.test(t)) return true;
      if (/not\s+(provided|supplied|included|available)(?:\s+separately)?/i.test(t)) return true;
      if (/\bverilmez\b/.test(t) && /\bpower\s*wire\b/.test(t)) return true;
      return false;
    },

    filterExcludedProductOptions(options) {
      if (!Array.isArray(options)) return options;
      const cols = [];
      const pcCtx = this.config.productContext || {};
      if (Array.isArray(pcCtx.collections)) {
        pcCtx.collections.forEach(function (c) {
          cols.push(String(c || ''));
        });
      }
      geniusCsvTokens(this.config.collectionHandles).forEach(function (ch) {
        if (cols.indexOf(ch) === -1) cols.push(ch);
      });
      const isOpenFrame = geniusIsOpenFrameAndroidProduct(
        this.resolveProductHandle(this.config.productHandle),
        this.config.productTitle,
        this.config.productTags,
        cols
      );
      return options.filter((opt) => {
        if (this.isPowerWireExcludedNoticeOption(opt)) return false;
        if (isOpenFrame && geniusIsPoeAccessoryOption(opt)) return false;
        return true;
      });
    },

    /**
     * When the CPU list is narrowed to exactly one real SKU (title vs catalog),
     * skip rendering the group but keep optionsDataMap + selectedOptions so cart/price/validation work.
     */
    seedHiddenSingleCpuGroup(group, groupId, optionsForRendering) {
      this.groupDisplayNames[groupId] = group.displayName;
      const list = Array.isArray(optionsForRendering) ? optionsForRendering : [];
      list.forEach((opt) => {
        const rawVal = typeof opt.value === 'string' ? opt.value : '';
        const value = rawVal.trim();
        if (!value) return;
        const key = `${groupId}:${opt.value}`;
        const effectiveMaxQuantity = Math.max(opt.maxQuantity || 0, 50);
        this.optionsDataMap[key] = {
          displayValue: opt.displayValue,
          priceModifier: opt.priceModifier || 0,
          priceModifierType: opt.priceModifierType || 'fixed',
          optionId: opt.id,
          variantOption: opt.variantOption || null,
          imageUrl: opt.imageUrl || '',
          allowQuantity: opt.allowQuantity || false,
          minQuantity: opt.minQuantity || 1,
          maxQuantity: effectiveMaxQuantity,
          isDefault: Boolean(opt.isDefault)
        };
      });

      const selectable = list.filter((opt) => opt?.value && String(opt.value).trim());
      const chosen = selectable.length === 1 ? selectable[0] : null;
      if (!chosen) return;

      const keySel = `${groupId}:${chosen.value}`;
      if (this.optionsDataMap[keySel]) {
        this.optionsDataMap[keySel].priceModifier = 0;
        this.optionsDataMap[keySel].isDefault = true;
      }

      const modType = chosen.priceModifierType || 'fixed';
      this.selectedOptions[groupId] = [{
        value: chosen.value,
        displayValue: chosen.displayValue || chosen.value,
        priceModifier: 0,
        priceType: modType,
        optionId: chosen.id,
        variantOption: chosen.variantOption || '',
        groupDisplayName: group.displayName,
        allowQuantity: chosen.allowQuantity || false,
        quantity: 1,
        isDefault: true
      }];
    },

    normalizeCpuToken(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/\bcore\b/g, '')
        .replace(/[^a-z0-9]/g, '');
    },

    extractCpuTokensFromText(text) {
      const normalizedText = String(text || '').toLowerCase();
      const tokenSet = new Set();
      const addToken = (token) => {
        const normalized = this.normalizeCpuToken(token);
        if (normalized) tokenSet.add(normalized);
      };

      // Full CPU forms: i5-7300u, core i7 1265u, i5 7300
      const fullCpuMatches = normalizedText.match(/\b(?:core[-\s]*)?i[3579][-\s]?\d{4,5}[a-z]{0,2}\b/g) || [];
      fullCpuMatches.forEach((match) => addToken(match));

      // RK and A-series forms used by Android products
      const rkMatches = normalizedText.match(/\brk\d{4}\b/g) || [];
      rkMatches.forEach((match) => addToken(match));
      const aSeriesMatches = normalizedText.match(/\ba\d{3,4}p?\b/g) || [];
      aSeriesMatches.forEach((match) => addToken(match));

      // Parse compact tags like "i5-celeron-7300-7360"
      const intelFamilyMatches = normalizedText.match(/\bi[3579]\b/g) || [];
      const cpuNumbers = normalizedText.match(/\b\d{4,5}[a-z]{0,2}\b/g) || [];
      if (intelFamilyMatches.length > 0 && cpuNumbers.length > 0) {
        intelFamilyMatches.forEach((family) => {
          cpuNumbers.forEach((num) => {
            // Add both forms to match option values that may or may not include suffix
            addToken(`${family}${num}`);
            if (/^\d{4,5}$/.test(num)) {
              addToken(`${family}${num}u`);
            }
          });
        });
      }

      return Array.from(tokenSet);
    },

    extractCpuTokensFromContext() {
      const textParts = [
        this.config?.productTitle || '',
        this.config?.productTags || '',
        this.config?.productSku || '',
        this.config?.productSkus || ''
      ];
      const merged = textParts.join(' ');
      return this.extractCpuTokensFromText(merged);
    },

    hasStrictIntel7300Rule() {
      const tags = String(this.config?.productTags || '').toLowerCase();
      if (tags.includes('i5-celeron-7300-7360')) return true;
      if (/i5[-_]?celeron[-_]?7300/.test(tags)) return true;
      if (tags.includes('7300') && tags.includes('celeron') && !tags.includes('8260')) return true;
      return false;
    },

    getStrictIntel7300CpuOptions(options) {
      const source = Array.isArray(options) ? options : [];
      const placeholderOptions = source.filter((opt) => !opt?.value || !String(opt.value).trim());
      const matched7300 = source.find((opt) => {
        const token = this.normalizeCpuToken(`${opt?.value || ''} ${opt?.displayValue || ''}`);
        return token.includes('i57300') || token.includes('7300');
      });
      const matched7360 = source.find((opt) => {
        const token = this.normalizeCpuToken(`${opt?.value || ''} ${opt?.displayValue || ''}`);
        return token.includes('i57360') || token.includes('7360');
      });

      const base7300 = matched7300 || {
        id: 'cpu-i5-7300u',
        value: 'Intel i5-7300U',
        displayValue: 'Intel i5-7300U',
        isDefault: true,
        isDisabled: false,
        variantOption: ''
      };

      const normalized7300 = {
        ...base7300,
        value: 'Intel i5-7300U',
        displayValue: 'Intel i5-7300U',
        priceModifier: 0,
        priceModifierType: 'fixed',
        isDefault: true,
        isDisabled: false
      };

      const upgrade7360 = matched7360 || {
        id: 'cpu-i5-7360u',
        value: 'Intel i5-7360U',
        displayValue: 'Intel i5-7360U',
        variantOption: ''
      };

      const normalized7360 = {
        ...upgrade7360,
        value: 'Intel i5-7360U',
        displayValue: 'Intel i5-7360U',
        priceModifier: 100,
        priceModifierType: 'fixed',
        isDefault: false,
        isDisabled: false
      };

      return [...placeholderOptions, normalized7300, normalized7360];
    },

    getAdjustedCpuOptions(options) {
      const source = Array.isArray(options) ? options : [];
      const placeholderOptions = source.filter((opt) => !opt?.value || !String(opt.value).trim());
      const cpuOptions = source.filter((opt) => opt?.value && String(opt.value).trim());

      // Remove Celeron options for CPU groups
      const nonCeleronOptions = cpuOptions.filter((opt) => {
        const token = this.normalizeCpuToken(`${opt?.value || ''} ${opt?.displayValue || ''}`);
        return !token.includes('celeron');
      });

      // If filtering removes everything, fail safe and keep original CPU options
      if (!nonCeleronOptions.length) {
        return source;
      }

      const minModifier = nonCeleronOptions.reduce((min, opt) => {
        const modifier = Number(opt?.priceModifier || 0);
        return Math.min(min, modifier);
      }, Number.POSITIVE_INFINITY);

      // Lowest-priced option becomes free and default.
      // All other CPU options receive an additional +$40.
      let defaultAssigned = false;
      const adjustedOptions = nonCeleronOptions.map((opt) => {
        const rawModifier = Number(opt?.priceModifier || 0);
        const normalizedModifier = rawModifier - minModifier;
        const isCheapest = normalizedModifier === 0 && !defaultAssigned;
        if (isCheapest) defaultAssigned = true;

        return {
          ...opt,
          priceModifier: isCheapest ? 0 : (normalizedModifier + 40),
          priceModifierType: 'fixed',
          isDefault: isCheapest,
          isDisabled: false
        };
      });

      return [...placeholderOptions, ...adjustedOptions];
    },

    getIntelGenerationFromToken(token) {
      const normalized = this.normalizeCpuToken(token || '');
      const match = normalized.match(/i[3579](\d{4,5})/);
      if (!match) return null;
      const digits = match[1];
      if (digits.length === 5) return parseInt(digits.slice(0, 2), 10);
      if (digits.length === 4) return parseInt(digits.slice(0, 1), 10);
      return null;
    },

    getCpuContextConstraints() {
      const title = String(this.config?.productTitle || '').toLowerCase();
      const tags = String(this.config?.productTags || '').toLowerCase();
      const text = `${title} ${tags}`;

      // e.g. "Core i5-8260U / i7-1195G7" → i5 and i7 both mentioned; do not narrow to one family
      const familyTokens = text.match(/\bi[3579]\b/g) || [];
      const uniqueFamilies = [...new Set(familyTokens.map((t) => t.toLowerCase()))];
      if (uniqueFamilies.length > 1) {
        return { family: null, generation: null };
      }

      // e.g. "i5-8260U / i5-1135G7" — same Intel family, two different SKUs; never narrow by gen
      const modelMatches = text.match(/\bi[3579]-\d{4,5}[a-z]{0,2}\b/gi) || [];
      const uniqueModels = [...new Set(modelMatches.map((m) => m.toLowerCase().replace(/\s+/g, '')))];
      if (uniqueModels.length > 1) {
        return { family: null, generation: null };
      }

      const family = uniqueFamilies.length === 1 ? uniqueFamilies[0] : null;
      const genMatch = text.match(/\b(10|11|12|13|14)th\b/);
      const generation = genMatch ? parseInt(genMatch[1], 10) : null;

      return { family, generation };
    },

    applyCpuContextFilter(options) {
      const source = Array.isArray(options) ? options : [];
      const placeholderOptions = source.filter((opt) => !opt?.value || !String(opt.value).trim());
      const selectableOptions = source.filter((opt) => opt?.value && String(opt.value).trim());
      const { family, generation } = this.getCpuContextConstraints();

      if (!family && !generation) {
        return source;
      }

      const filtered = selectableOptions.filter((opt) => {
        const token = this.normalizeCpuToken(`${opt?.value || ''} ${opt?.displayValue || ''}`);
        if (family && !token.includes(family)) return false;
        if (generation !== null) {
          const optGen = this.getIntelGenerationFromToken(token);
          if (optGen !== generation) return false;
        }
        return true;
      });

      if (!filtered.length) {
        console.warn('[GeniusOptions] CPU context filter matched nothing; keeping original CPU options', { family, generation });
        return source;
      }

      return [...placeholderOptions, ...filtered];
    },

    getFilteredDropdownOptions(group) {
      const options = Array.isArray(group?.options) ? group.options : [];
      if (!this.isCpuGroup(group)) return options;

      if (this.hasStrictIntel7300Rule()) {
        const strictOptions = this.getStrictIntel7300CpuOptions(options);
        console.log('[GeniusOptions] Applied strict CPU rule for tag i5-celeron-7300-7360', {
          before: options.length,
          after: strictOptions.length
        });
        return strictOptions;
      }

      const cpuTokens = this.extractCpuTokensFromContext();
      let scopedOptions = this.applyCpuContextFilter(options);

      if (cpuTokens.length) {
        const placeholderOptions = scopedOptions.filter((opt) => !opt?.value || !String(opt.value).trim());
        const matchingOptions = scopedOptions.filter((opt) => {
          const normalizedValue = this.normalizeCpuToken(opt?.value || '');
          const normalizedDisplay = this.normalizeCpuToken(opt?.displayValue || '');
          if (!normalizedValue && !normalizedDisplay) return false;

          return cpuTokens.some((token) =>
            normalizedValue.includes(token) ||
            normalizedDisplay.includes(token) ||
            token.includes(normalizedValue) ||
            token.includes(normalizedDisplay)
          );
        });

        if (matchingOptions.length) {
          scopedOptions = [...placeholderOptions, ...matchingOptions];
          console.log('[GeniusOptions] CPU options filtered by token match:', {
            group: group.displayName || group.name,
            tokens: cpuTokens,
            before: options.length,
            after: scopedOptions.length
          });
        }
      }

      const adjustedCpuOptions = this.getAdjustedCpuOptions(scopedOptions);
      if (adjustedCpuOptions.length) {
        console.log('[GeniusOptions] Applied generic CPU pricing rules (remove celeron, lowest free, +40 others)', {
          before: scopedOptions.length,
          after: adjustedCpuOptions.length
        });
        return adjustedCpuOptions;
      }

      if (!cpuTokens.length) return scopedOptions;

      const placeholderOptions = scopedOptions.filter((opt) => !opt?.value || !String(opt.value).trim());
      const matchingOptions = scopedOptions.filter((opt) => {
        const normalizedValue = this.normalizeCpuToken(opt?.value || '');
        const normalizedDisplay = this.normalizeCpuToken(opt?.displayValue || '');
        if (!normalizedValue && !normalizedDisplay) return false;

        return cpuTokens.some((token) =>
          normalizedValue.includes(token) ||
          normalizedDisplay.includes(token) ||
          token.includes(normalizedValue) ||
          token.includes(normalizedDisplay)
        );
      });

      if (!matchingOptions.length) {
        console.log('[GeniusOptions] CPU filter found no option matches; using scoped list');
        return scopedOptions;
      }

      const filtered = [...placeholderOptions, ...matchingOptions];
      console.log('[GeniusOptions] CPU options filtered by product context:', {
        group: group.displayName || group.name,
        tokens: cpuTokens,
        before: options.length,
        after: filtered.length
      });
      return filtered;
    },

    extractCpuTokensFromOptionSet(optionSet) {
      const tokenSet = new Set();
      if (!optionSet || !Array.isArray(optionSet.optionGroups)) return [];

      optionSet.optionGroups.forEach((group) => {
        if (!this.isCpuGroup(group) || !Array.isArray(group.options)) return;

        group.options.forEach((opt) => {
          const normalizedValue = this.normalizeCpuToken(opt?.value || '');
          const normalizedDisplay = this.normalizeCpuToken(opt?.displayValue || '');

          if (normalizedValue && /(?:i[3579]\d{4,5}|n\d{2,4}|rk\d{4}|a\d{3,4}p?)/.test(normalizedValue)) {
            tokenSet.add(normalizedValue);
          }
          if (normalizedDisplay && /(?:i[3579]\d{4,5}|n\d{2,4}|rk\d{4}|a\d{3,4}p?)/.test(normalizedDisplay)) {
            tokenSet.add(normalizedDisplay);
          }
        });
      });

      return Array.from(tokenSet);
    },

    parseCompactPanelInchToken(raw) {
      const s = String(raw || '').trim().toLowerCase();
      if (!s) return null;
      // Prefer X-Y decimal inch (15-6 → 15.6) anywhere: i5-15-6-hdmi-dp
      const decimalDash = s.match(/(?:^|-)(\d{1,2})-(\d)(?:-|$)/);
      if (decimalDash) {
        const inches = parseFloat(`${decimalDash[1]}.${decimalDash[2]}`);
        if (inches >= 7 && inches <= 50) return inches;
      }
      const plain = s.match(/^(\d+(?:\.\d+)?)(?:-|$)/);
      if (plain) {
        const inches = parseFloat(plain[1]);
        if (inches >= 7 && inches <= 50) return inches;
      }
      return null;
    },

    extractPanelInchFromContext() {
      const tags = String(this.config?.productTags || '');
      for (const part of tags.split(/[,;|]/)) {
        const fromTag = this.parseCompactPanelInchToken(part.trim());
        if (fromTag != null) return fromTag;
      }
      const title = String(this.config?.productTitle || '');
      // Turkish "İnç" (U+0130) does not case-fold under /i — match both forms.
      const m =
        title.match(/(\d+(?:[.,]\d+)?)\s*[″"']/u) ||
        title.match(/(\d+(?:[.,]\d+)?)\s*(?:inch|inç|inc|İnç|İNÇ)\b/i) ||
        title.match(/(\d+(?:[.,]\d+)?)\s*(?:["']|\u2033)/);
      if (!m) return null;
      const inches = parseFloat(String(m[1]).replace(',', '.'));
      return inches >= 7 && inches <= 50 ? inches : null;
    },

    extractInchFromSetName(name) {
      const m = String(name || '').match(/^(\d+(?:[.,]\d+)?)\s*[″"]/);
      if (!m) return null;
      const inches = parseFloat(String(m[1]).replace(',', '.'));
      return Number.isFinite(inches) ? inches : null;
    },

    scoreOptionSetByPanelContext(set) {
      const name = String(set?.name || '');
      const nameLo = name.toLowerCase();
      let score = 0;
      const productInch = this.extractPanelInchFromContext();
      const setInch = this.extractInchFromSetName(name);
      if (productInch != null && setInch != null) {
        if (Math.abs(productInch - setInch) < 0.01) score += 1000;
        else return -1;
      }
      const tagsLo = String(this.config?.productTags || '').toLowerCase();
      if (tagsLo.includes('pink') && nameLo.endsWith('-pink')) score += 200;
      if (tagsLo.includes('green') && nameLo.endsWith('-green')) score += 200;

      const productIs7300Family =
        /\b7300\b/.test(tagsLo) ||
        tagsLo.includes('celeron-7300') ||
        /i5[-_]?celeron[-_]?7300/.test(tagsLo);
      const productIs8260Family =
        /\b8260\b/.test(tagsLo) || tagsLo.includes('1135') || tagsLo.includes('1235');
      const setIs7300Family =
        nameLo.includes('7300') || nameLo.includes('celeron&i5') || nameLo.includes('celeron');
      const setIs8260Family =
        nameLo.includes('8260') || nameLo.includes('1135') || nameLo.includes('1235');
      if (productIs7300Family && !productIs8260Family && setIs8260Family && !setIs7300Family) {
        return -1;
      }
      if (productIs8260Family && !productIs7300Family && setIs7300Family && !setIs8260Family) {
        return -1;
      }

      if (tagsLo.includes('7300') && nameLo.includes('7300')) score += 150;
      if (tagsLo.includes('8260') && nameLo.includes('8260')) score += 150;
      if (tagsLo.includes('i5-15') && nameLo.includes('intel i5')) score += 120;
      if (tagsLo.includes('hdmi-dp') || tagsLo.includes('hdmi_dp')) {
        if (nameLo.includes('hdmi-dp') || nameLo.includes('hdmi&dp')) score += 400;
        else if (nameLo.includes('hdmi') && !nameLo.includes('hdmi-dp')) score -= 50;
      }
      if (/i5-15-6/.test(tagsLo) && (nameLo.includes('15,6') || nameLo.includes('15.6'))) {
        score += 150;
      }
      score += set.priority !== null && set.priority !== undefined ? set.priority : 0;
      return score;
    },

    selectBestOptionSet(optionSets) {
      if (!Array.isArray(optionSets) || optionSets.length === 0) return null;
      if (optionSets.length === 1) return optionSets[0];

      // Zhichun etiketi veya GZ-APC/GZ-WPC SKU: CPU skoru Android/Windows panel set'ini seçip Zhichun
      // yükseltme set'ini (genelde CPU grubu yok) ezer. Zhichun set'i proxy'de zaten eşleşmişse öne al.
      if (this.hasZhichunProductTag()) {
        const zhichunCandidates = optionSets.filter((s) =>
          (s.name || '').toLowerCase().includes('zhichun')
        );
        if (zhichunCandidates.length > 0) {
          zhichunCandidates.sort((a, b) => {
            const pa = a.priority !== null && a.priority !== undefined ? a.priority : 0;
            const pb = b.priority !== null && b.priority !== undefined ? b.priority : 0;
            return pb - pa;
          });
          console.log('[GeniusOptions] Zhichun (tag veya GZ panel SKU) → set:', zhichunCandidates[0].name);
          return zhichunCandidates[0];
        }
      }

      const handleLoDedicated = String(
        this.resolveProductHandle(this.config.productHandle) || ''
      ).toLowerCase();
      const pcCtxR6s = this.config.productContext || {};
      const colsSel = Array.isArray(pcCtxR6s.collections)
        ? pcCtxR6s.collections.map(function (c) {
            return String(c || '');
          })
        : [];
      geniusCsvTokens(this.config.collectionHandles).forEach(function (ch) {
        if (colsSel.indexOf(ch) === -1) colsSel.push(ch);
      });
      const tagsSel = String(this.config?.productTags || '');
      if (
        geniusProductQualifiesForNanoPiR6SExclusive(handleLoDedicated, tagsSel, colsSel)
      ) {
        const r6sCandidates = optionSets.filter((s) => {
          const n = (s.name || '').toLowerCase();
          return n.includes('nanopi r6s') || n.includes('r6s hardware');
        });
        if (r6sCandidates.length > 0) {
          r6sCandidates.sort((a, b) => {
            const pa = a.priority !== null && a.priority !== undefined ? a.priority : 0;
            const pb = b.priority !== null && b.priority !== undefined ? b.priority : 0;
            return pb - pa;
          });
          console.log(
            '[GeniusOptions] NanoPi R6S tag/collection/handle → dedicated set:',
            r6sCandidates[0].name
          );
          return r6sCandidates[0];
        }
      }

      // NanoPi R5S-LTS: proxy aynı SoC için birden fazla "Model Accessories" seti döndürebilir;
      // öncelik yüksek olan (ör. K3) listede önce gelir ve boş CPU token iken yanlış seçilir.
      const tagsRaw = String(this.config?.productTags || '');
      const tagParts = tagsRaw
        .split(/[,;|]/)
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
      const hasR5sLtsTag =
        tagParts.some((t) => t.includes('r5s-lts') || t === 'r5s lts') ||
        tagsRaw.toLowerCase().includes('r5s-lts');
      const titleLoR5s = String(this.config?.productTitle || '').toLowerCase();
      const handleLoR5s = String(
        this.resolveProductHandle(this.config.productHandle) || ''
      ).toLowerCase();
      const looksLikeR5sProduct =
        hasR5sLtsTag ||
        titleLoR5s.includes('r5s-lts') ||
        titleLoR5s.includes('r5s lts') ||
        titleLoR5s.includes('nanopi r5s') ||
        /(?:^|[-_])r5s[-_]?lts(?:[-_]|$)/i.test(handleLoR5s);
      if (looksLikeR5sProduct) {
        const r5sCandidates = optionSets.filter((s) => {
          const n = (s.name || '').toLowerCase();
          return (
            n.includes('r5s-lts') ||
            n.includes('r5s lts') ||
            (n.includes('nanopi') && n.includes('r5s'))
          );
        });
        if (r5sCandidates.length > 0) {
          r5sCandidates.sort((a, b) => {
            const pa = a.priority !== null && a.priority !== undefined ? a.priority : 0;
            const pb = b.priority !== null && b.priority !== undefined ? b.priority : 0;
            return pb - pa;
          });
          console.log('[GeniusOptions] R5S-LTS product → dedicated set:', r5sCandidates[0].name);
          return r5sCandidates[0];
        }
      }

      const exactModelTagMatches = optionSets.filter(function (set) {
        const setModel = String(set.name || '')
          .replace(/\s+model\s+accessories\s*$/i, '')
          .trim()
          .toLowerCase();
        if (!setModel) return false;
        return tagParts.some(function (t) {
          return t === setModel;
        });
      });
      if (exactModelTagMatches.length > 0) {
        exactModelTagMatches.sort(function (a, b) {
          const pa = a.priority !== null && a.priority !== undefined ? a.priority : 0;
          const pb = b.priority !== null && b.priority !== undefined ? b.priority : 0;
          return pb - pa;
        });
        console.log(
          '[GeniusOptions] Exact model tag match → set:',
          exactModelTagMatches[0].name
        );
        return exactModelTagMatches[0];
      }

      const productCpuTokens = this.extractCpuTokensFromContext();
      if (!productCpuTokens.length) {
        const panelScored = optionSets
          .map((set) => ({ set, score: this.scoreOptionSetByPanelContext(set) }))
          .filter((x) => x.score >= 0)
          .sort((a, b) => b.score - a.score);
        if (panelScored.length) {
          console.log('[GeniusOptions] Option set selected by panel inch/batch:', {
            selectedSet: panelScored[0].set?.name,
            score: panelScored[0].score,
          });
          return panelScored[0].set;
        }
        return optionSets[0];
      }

      let bestSet = optionSets[0];
      let bestScore = -1;
      let bestOverlap = 0;

      optionSets.forEach((set) => {
        const setTokens = this.extractCpuTokensFromOptionSet(set);
        const overlap = productCpuTokens.filter((productToken) =>
          setTokens.some((setToken) =>
            productToken.includes(setToken) || setToken.includes(productToken)
          )
        ).length;

        const priority = set.priority !== null && set.priority !== undefined ? set.priority : 0;
        const score = overlap * 1000 + priority;

        if (score > bestScore) {
          bestScore = score;
          bestSet = set;
          bestOverlap = overlap;
        }
      });

      if (bestOverlap > 0) {
        console.log('[GeniusOptions] Option set selected by CPU overlap:', {
          productCpuTokens,
          selectedSet: bestSet?.name,
          overlap: bestOverlap
        });
      }

      return bestSet;
    },

    renderDropdown(group, groupId, useCustomDropdown = false, optionsOverride = null) {
      const optValTrimmed = (opt) =>
        opt == null || opt.value === undefined || opt.value === null
          ? ''
          : String(opt.value).trim();
      const optDispTrimmed = (opt) => String(opt?.displayValue ?? '').trim();

      const uniqueOptions = [];
      const seenOptions = new Set();
      const sourceOptions = Array.isArray(optionsOverride)
        ? optionsOverride
        : this.getFilteredDropdownOptions(group);
      const cpuTokens = this.isCpuGroup(group) ? this.extractCpuTokensFromContext() : [];
      sourceOptions.forEach(opt => {
        const value = optValTrimmed(opt);
        const displayValue = optDispTrimmed(opt);
        const modifier = Number(opt.priceModifier || 0);
        const modifierType = opt.priceModifierType || 'fixed';
        const isPlaceholder = !value;
        const dedupeKey = isPlaceholder
          ? `placeholder:${displayValue.toLowerCase()}`
          : `${value.toLowerCase()}|${displayValue.toLowerCase()}|${modifier}|${modifierType}`;

        if (seenOptions.has(dedupeKey)) {
          return;
        }
        seenOptions.add(dedupeKey);
        uniqueOptions.push(opt);
      });

      const preferredSelectable =
        uniqueOptions.find((opt) => opt.isDefault && optValTrimmed(opt)) ||
        uniqueOptions.find((opt) => optValTrimmed(opt));

      const options = uniqueOptions.map(opt => {
        // If value is empty, treat as placeholder (non-selectable)
        const isPlaceholder = !optValTrimmed(opt);

        const priceDisplay = isPlaceholder ? '' : this.formatPriceModifier(opt);
        const label = priceDisplay
          ? `${opt.displayValue} (${priceDisplay})`
          : opt.displayValue;
        // Never auto-select empty placeholder — that breaks dependent SSD capacity UI.
        const isSelected =
          !isPlaceholder &&
          preferredSelectable &&
          String(opt.value ?? '') === String(preferredSelectable.value ?? '');

        return `
          <option
            value="${this.escapeAttr(opt.value || '')}"
            data-price-modifier="${isPlaceholder ? 0 : (opt.priceModifier || 0)}"
            data-price-type="${opt.priceModifierType || 'fixed'}"
            data-display-value="${this.escapeHtml(opt.displayValue)}"
            data-option-id="${this.escapeAttr(opt.id)}"
            data-variant-option="${this.escapeAttr(opt.variantOption || '')}"
            data-is-placeholder="${isPlaceholder}"
            data-is-default="${opt.isDefault ? 'true' : 'false'}"
            ${isSelected ? 'selected' : ''}
            ${opt.isDisabled || isPlaceholder ? 'disabled' : ''}
          >${this.escapeHtml(label)}</option>
        `;
      }).join('');

      const cpuMatchedOption = cpuTokens.length > 0
        ? uniqueOptions.find((opt) => {
            const valueToken = this.normalizeCpuToken(opt?.value || '');
            const labelToken = this.normalizeCpuToken(opt?.displayValue || '');
            return cpuTokens.some((token) =>
              valueToken.includes(token) ||
              labelToken.includes(token) ||
              token.includes(valueToken) ||
              token.includes(labelToken)
            );
          })
        : null;

      const selectedOption =
        cpuMatchedOption ||
        preferredSelectable ||
        uniqueOptions.find((opt) => optValTrimmed(opt)) ||
        uniqueOptions[0];
      const resetDefaultValue = String(selectedOption?.value ?? '');

      if (!useCustomDropdown) {
        return `
        <select class="genius-option-dropdown" name="${this.escapeAttr(groupId)}" aria-label="${this.escapeHtml(group.displayName)}" data-genius-default-value="${this.escapeAttr(resetDefaultValue)}">
          ${options}
        </select>
        `;
      }

      const selectedPrice = selectedOption ? this.formatPriceModifier(selectedOption) : '';
      const selectedLabel = selectedOption
        ? `${selectedOption.displayValue}${selectedPrice ? ` (${selectedPrice})` : ''}`
        : this.strings.selectOption;

      const customOptions = uniqueOptions
        .filter((opt) => optValTrimmed(opt))
        .map(opt => {
        const priceDisplay = this.formatPriceModifier(opt);
        const label = priceDisplay ? `${opt.displayValue} (${priceDisplay})` : opt.displayValue;
        const isSelected =
          selectedOption &&
          String(opt.value ?? '') === String(selectedOption.value ?? '');
        return `
          <button type="button" class="genius-dropdown__option" role="option" aria-selected="${isSelected}" data-value="${this.escapeAttr(opt.value)}" ${opt.isDisabled ? 'disabled' : ''}>
            <span class="genius-dropdown__option-text">${this.escapeHtml(label)}</span>
          </button>
        `;
      }).join('');

      const dropdownId = `dropdown-${groupId}-${Math.random().toString(36).substr(2, 9)}`;

      return `
        <div class="genius-dropdown" data-group-id="${this.escapeAttr(groupId)}" data-dropdown-id="${this.escapeAttr(dropdownId)}" data-force-down="true">
          <button type="button" class="genius-dropdown__trigger" aria-haspopup="listbox" aria-expanded="false">
            <span class="genius-dropdown__value">${this.escapeHtml(selectedLabel)}</span>
            <span class="genius-dropdown__chevron">▾</span>
          </button>
          <div class="genius-dropdown__menu" role="listbox" data-belongs-to="${this.escapeAttr(dropdownId)}">
            ${customOptions}
          </div>
          <select class="genius-option-dropdown genius-option-dropdown--hidden" name="${this.escapeAttr(groupId)}" aria-label="${this.escapeHtml(group.displayName)}" data-genius-default-value="${this.escapeAttr(resetDefaultValue)}">
            ${options}
          </select>
        </div>
      `;
    },

    shouldUseCustomDropdown() {
      return true;
    },

    formatPriceModifier(option) {
      if (option && option.isDefault === true) {
        return '';
      }
      const modifier = parseFloat(option.priceModifier) || 0;
      
      // Don't show anything for 0 price options
      if (modifier === 0) {
        return '';
      }
      
      if (option.priceModifierType === 'percent') {
        return modifier > 0 ? `+%${modifier}` : `%${modifier}`;
      }
      
      return modifier > 0 
        ? `+${this.formatCurrency(modifier)}`
        : this.formatCurrency(modifier);
    },

    formatCurrency(amount) {
      const currency = this.config.currency || 'USD';
      let locale = 'en-US';

      // Use Turkish formatting for TRY so it shows as 26.370,00 ₺
      if (currency === 'TRY') {
        locale = 'tr-TR';
      }

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch (e) {
        const rounded = Number(amount).toFixed(2);
        if (currency === 'TRY') {
          return `${rounded} TL`;
        }
        return `${currency} ${rounded}`;
      }
    },

    getOptionPriceType(opt) {
      const t = (opt && (opt.priceType || opt.priceModifierType)) || 'fixed';
      return String(t).toLowerCase();
    },

    /**
     * Extra $/₺ amount for one selected line (fixed or %), × quantity.
     * Keeps cart property `_genius_options_price` aligned with PDP totals.
     */
    computeLineExtraForOption(opt) {
      if (opt && opt.isDefault === true) {
        return 0;
      }
      const qty = opt.quantity || 1;
      const type = this.getOptionPriceType(opt);
      const mod = Number(opt.priceModifier) || 0;
      if (type === 'percent') {
        return this.basePrice * (mod / 100) * qty;
      }
      return mod * qty;
    },

    computeTotalExtraFromSelected() {
      let total = 0;
      const map = this.selectedOptions || {};
      Object.values(map).forEach((options) => {
        (options || []).forEach((opt) => {
          total += this.computeLineExtraForOption(opt);
        });
      });
      return total;
    },

    /**
     * Remove one trailing "(+…price…)" tail from admin displayValue so cart lines are not doubled.
     */
    stripTrailingPriceParenFromLabel(text) {
      return String(text || '')
        .replace(/\s*\([^()]*(\+|₺|\$|€|£|\bTL\b|USD|EUR)[^)]*\)\s*$/i, '')
        .trim();
    },

    /**
     * "RAM - 64GB" → "RAM – 64GB" (en tire). Aksi halde sepet/ödeme sayfalarındaki
     * `-[space]64` kalıbı yanlışlıkla "-64 TL" fiyatı sanılıp etiket bozuluyordu.
     */
    normalizeRamSpecDashForCartScripts(text) {
      return String(text || '').replace(/\bRAM\s*-\s*(?=\d)/gi, 'RAM – ');
    },

    cleanOptionLabelForStorage(text) {
      return this.normalizeRamSpecDashForCartScripts(
        this.stripTrailingPriceParenFromLabel(text)
      );
    },

    /**
     * Zhichun Android matrix (`Genius-price-product-list-Zhichun.xlsx`): factory RAM / eMMC.
     * Option values match DB: `2gb`, `4gb`, `32gb`, `64gb`, …
     */
    applyZhichunAndroidFactoryBomSelections(fact) {
      if (!fact || !this.container || fact.ramGb == null || fact.storageGb == null) return;
      const ramVal = fact.ramGb + 'gb';
      const storVal = fact.storageGb + 'gb';
      const groups = this.container.querySelectorAll(
        '.genius-option-group:not(.genius-option-group--hidden)'
      );
      groups.forEach((ge) => {
        const gn = (ge.dataset.groupName || '').toLowerCase();
        const sel = ge.querySelector('select.genius-option-dropdown');
        if (!sel) return;
        if (gn.startsWith('ram_')) {
          const match = Array.from(sel.options).find((o) => o.value === ramVal && !o.disabled);
          if (match) {
            sel.value = ramVal;
            this.handleSelection(sel);
          }
        } else if (gn.startsWith('storage')) {
          const match = Array.from(sel.options).find((o) => o.value === storVal && !o.disabled);
          if (match) {
            sel.value = storVal;
            this.handleSelection(sel);
          }
        }
      });
      if (typeof this.syncCustomDropdowns === 'function') this.syncCustomDropdowns();
      if (typeof this.updatePrice === 'function') this.updatePrice();
      if (typeof this.updateHiddenInputs === 'function') this.updateHiddenInputs();
    },

    /**
     * RAM/DDR4 radio groups: skip auto-default only when `isRamDdr4UpgradeButtonGroup` applies
     * (Zhichun `zhichun_ram_ddr4` is excluded — base tier is default at $0).
     */
    shouldSkipStorefrontAutoDefaultForInput(input) {
      if (!input || input.type !== 'radio') return false;
      const groupEl = input.closest('.genius-option-group');
      if (!groupEl) return false;
      return this.isRamDdr4UpgradeButtonGroup({
        name: groupEl.getAttribute('data-group-name') || '',
        displayName: groupEl.getAttribute('data-display-name') || '',
      });
    },

    /**
     * Uncheck all radios in RAM+DDR4 groups and drop their line items (covers stale HTML or theme quirks).
     */
    clearPreselectionForRamDdr4UpgradeGroups() {
      if (!this.container) return;
      this.container.querySelectorAll('.genius-option-group').forEach((groupEl) => {
        const group = {
          name: groupEl.getAttribute('data-group-name') || '',
          displayName: groupEl.getAttribute('data-display-name') || '',
        };
        if (!this.isRamDdr4UpgradeButtonGroup(group)) return;
        const radios = groupEl.querySelectorAll('input.genius-option__input[type="radio"]');
        if (!radios.length) return;
        radios.forEach((r) => {
          r.checked = false;
          r.removeAttribute('data-default');
        });
        const gid = radios[0].name;
        if (gid && this.selectedOptions[gid]) {
          delete this.selectedOptions[gid];
        }
      });
    },

    /** True when metafield / screen rules hid this group — skip setDefaults() selections there. */
    isOptionGroupDomHidden(groupEl) {
      if (!groupEl) return true;
      if (groupEl.classList.contains('genius-option-group--hidden')) return true;
      if (groupEl.hasAttribute('hidden')) return true;
      const vis = groupEl.style?.visibility;
      const disp = groupEl.style?.display;
      if (vis === 'hidden') return true;
      if (disp === 'none') return true;
      return false;
    },

    setDefaults() {
      const defaultInputs = this.container.querySelectorAll('[data-default="true"]');
      defaultInputs.forEach((input) => {
        const groupEl = input.closest('.genius-option-group');
        if (groupEl && this.isOptionGroupDomHidden(groupEl)) {
          return;
        }
        if (this.shouldSkipStorefrontAutoDefaultForInput(input)) {
          input.checked = false;
          input.removeAttribute('data-default');
          return;
        }
        input.checked = true;
        this.handleSelection(input);
      });
      
      const dropdowns = this.container.querySelectorAll('.genius-option-dropdown');
      dropdowns.forEach((dropdown) => {
        const groupEl = dropdown.closest('.genius-option-group');
        if (groupEl && this.isOptionGroupDomHidden(groupEl)) {
          return;
        }
        let def = dropdown.hasAttribute('data-genius-default-value')
          ? dropdown.getAttribute('data-genius-default-value')
          : null;
        if (def !== null && def !== '') {
          const match = Array.from(dropdown.options).find((o) => o.value === def && !o.disabled);
          if (match) dropdown.value = def;
        } else if (!String(dropdown.value || '').trim()) {
          const first = Array.from(dropdown.options).find((o) => o.value && !o.disabled);
          if (first) dropdown.value = first.value;
        }
        if (dropdown.selectedIndex >= 0) {
          this.handleSelection(dropdown);
        }
      });

      // Hide dependents that do not apply and clear their DOM + selectedOptions (setDefaults
      // runs handleSelection on every <select>, including hidden SSD capacity rows).
      this.initializeDependentGroups();

      this.clearPreselectionForRamDdr4UpgradeGroups();

      this.syncCustomDropdowns();
      this.updatePrice();
      if (typeof this.updateHiddenInputs === 'function') {
        this.updateHiddenInputs();
      }
    },

    resetSelectionsToDefaults() {
      if (!this.container) return;

      this.container.querySelectorAll('.genius-dropdown').forEach((dd) => {
        this.closeDropdownMenu(dd);
      });

      this.selectedOptions = {};

      this.container.querySelectorAll('input.genius-option__input[type="checkbox"], input.genius-option__input[type="radio"]').forEach((input) => {
        input.checked = false;
      });

      this.container.querySelectorAll('select.genius-option-dropdown').forEach((sel) => {
        const groupEl = sel.closest('.genius-option-group');
        if (groupEl && this.isOptionGroupDomHidden(groupEl)) {
          return;
        }
        let def = sel.hasAttribute('data-genius-default-value')
          ? sel.getAttribute('data-genius-default-value')
          : null;
        if (def !== null && def !== '') {
          const match = Array.from(sel.options).find((o) => o.value === def && !o.disabled);
          if (match) {
            sel.value = def;
            return;
          }
        }
        const defaultOpt = Array.from(sel.options).find((o) => o.defaultSelected && !o.disabled);
        if (defaultOpt) {
          sel.value = defaultOpt.value;
          return;
        }
        const first = Array.from(sel.options).find((o) => o.value && !o.disabled);
        if (first) sel.value = first.value;
      });

      this.container.querySelectorAll('.genius-option-group--error').forEach((el) => {
        el.classList.remove('genius-option-group--error');
      });

      this.setupScreenSizeBasedOptions();
      this.setupMetafieldBasedOptions();
      this.setDefaults();
    },

    removeEachTextFromCheckboxes() {
      if (!this.container) {
        console.warn('[GeniusOptions] Container not found, skipping "each" cleanup');
        return;
      }
      
      // Find all checkbox options
      const checkboxOptions = this.container.querySelectorAll('.genius-option--checkbox');
      
      if (checkboxOptions.length === 0) {
        console.log('[GeniusOptions] No checkbox options found yet for "each" cleanup');
        return; // No checkboxes found yet
      }
      
      console.log(`[GeniusOptions] Found ${checkboxOptions.length} checkbox options, cleaning "each" text...`);
      
      let removedCount = 0;
      
      checkboxOptions.forEach(option => {
        let foundEach = false;
        
        // Remove "each" from all text nodes in the option element
        const walker = document.createTreeWalker(
          option,
          NodeFilter.SHOW_TEXT,
          null
        );
        
        let node;
        while (node = walker.nextNode()) {
          if (node.textContent && /each/i.test(node.textContent)) {
            const originalText = node.textContent;
            // Remove "each" in ALL possible formats - ultra aggressive
            // Try multiple patterns to catch all variations
            let cleaned = node.textContent;
            
            // Pattern 1: "each" as standalone word (with word boundaries)
            cleaned = cleaned.replace(/\beach\b/gi, '');
            
            // Pattern 2: "each" at the end with optional spaces
            cleaned = cleaned.replace(/\s*each\s*$/i, '');
            
            // Pattern 3: "each" at the beginning with optional spaces
            cleaned = cleaned.replace(/^\s*each\s*/i, '');
            
            // Pattern 4: "each" in the middle with spaces
            cleaned = cleaned.replace(/\s+each\s+/gi, ' ');
            
            // Pattern 5: "each" attached to other characters (no word boundary)
            // This catches cases like "$13each" or "priceeach"
            cleaned = cleaned.replace(/([^\s])each/gi, '$1');
            cleaned = cleaned.replace(/each([^\s])/gi, '$1');
            
            // Normalize spaces and trim
            cleaned = cleaned.replace(/\s+/g, ' ').trim();
            
            node.textContent = cleaned;
            
            if (node.textContent !== originalText) {
              foundEach = true;
            }
          }
        }
        
        // Also check and clean specific elements with same ultra-aggressive approach
        const valueElement = option.querySelector('.genius-option__value');
        if (valueElement) {
          let text = valueElement.textContent || valueElement.innerText || '';
          const originalText = text;
          
          // Ultra-aggressive removal
          text = text.replace(/\beach\b/gi, '');
          text = text.replace(/\s*each\s*$/i, '');
          text = text.replace(/^\s*each\s*/i, '');
          text = text.replace(/\s+each\s+/gi, ' ');
          text = text.replace(/([^\s])each/gi, '$1');
          text = text.replace(/each([^\s])/gi, '$1');
          text = text.replace(/\s+/g, ' ').trim();
          
          if (text !== originalText) {
            valueElement.textContent = text;
            foundEach = true;
          }
        }
        
        const priceElement = option.querySelector('.genius-option__price');
        if (priceElement) {
          // Get original price from data attribute if available (set during render)
          let text = priceElement.dataset.originalPrice || priceElement.textContent || priceElement.innerText || '';
          const originalText = text;
          
          // Ultra-aggressive removal
          text = text.replace(/\beach\b/gi, '');
          text = text.replace(/\s*each\s*$/i, '');
          text = text.replace(/^\s*each\s*/i, '');
          text = text.replace(/\s+each\s+/gi, ' ');
          text = text.replace(/([^\s])each/gi, '$1');
          text = text.replace(/each([^\s])/gi, '$1');
          text = text.replace(/\s+/g, ' ').trim();
          
          // Always update, even if text appears unchanged (in case DOM was modified)
          priceElement.textContent = text;
          priceElement.dataset.originalPrice = text; // Update stored value
          
          if (text !== originalText || /each/i.test(priceElement.textContent)) {
            foundEach = true;
            // Force update again if "each" still present
            if (/each/i.test(priceElement.textContent)) {
              priceElement.textContent = text;
            }
          }
        }
        
        if (foundEach) {
          removedCount++;
        }
      });
      
      console.log('[GeniusOptions] Removed "each" text from', removedCount, 'checkbox options');
      
      // Set up mutation observer to catch dynamically added content
      if (!this.eachTextObserver) {
        this.eachTextObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList' || mutation.type === 'characterData') {
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.TEXT_NODE && /each/i.test(node.textContent)) {
                  node.textContent = node.textContent
                    .replace(/\s*each\s*$/i, '')
                    .replace(/\s+each\s+/i, ' ')
                    .replace(/^each\s+/i, '')
                    .replace(/\beach\b/gi, '')
                    .replace(/\s+/g, ' ')
                    .trim();
                } else if (node.nodeType === Node.ELEMENT_NODE) {
                  // Check if it's a checkbox option
                  const checkboxOption = node.classList?.contains('genius-option--checkbox') 
                    ? node 
                    : node.querySelector?.('.genius-option--checkbox');
                  
                  if (checkboxOption) {
                    this.removeEachTextFromCheckboxes();
                  }
                }
              });
            }
          });
        });
        
        this.eachTextObserver.observe(this.container, {
          childList: true,
          subtree: true,
          characterData: true
        });
      }
    },

    setupCustomDropdowns() {
      const dropdowns = this.container.querySelectorAll('.genius-dropdown');
      if (!dropdowns.length) return;

      const closeAll = () => {
        dropdowns.forEach(dropdown => {
          this.closeDropdownMenu(dropdown);
        });
      };

      document.addEventListener('click', (e) => {
        dropdowns.forEach(dropdown => {
          const dropdownId = dropdown.dataset.dropdownId;
          const menu = document.querySelector(`.genius-dropdown__menu[data-belongs-to="${dropdownId}"]`);
          
          const isClickInsideTrigger = dropdown.contains(e.target);
          const isClickInsideMenu = menu && menu.contains(e.target);
          
          if (!isClickInsideTrigger && !isClickInsideMenu) {
            this.closeDropdownMenu(dropdown);
          }
        });
      });

      dropdowns.forEach(dropdown => {
        const trigger = dropdown.querySelector('.genius-dropdown__trigger');
        const select = dropdown.querySelector('select.genius-option-dropdown');
        const dropdownId = dropdown.dataset.dropdownId;
        const menu = dropdown.querySelector('.genius-dropdown__menu');

        if (!trigger || !select || !menu) return;

        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const isOpen = dropdown.classList.contains('genius-dropdown--open');
          closeAll();
          if (!isOpen) {
            this.openDropdownMenu(dropdown);
          }
        });

        // Use event delegation on the menu for option clicks
        menu.addEventListener('click', (e) => {
          const option = e.target.closest('.genius-dropdown__option');
          if (option) {
            const value = option.dataset.value || '';
            select.value = value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            this.updateCustomDropdownSelection(dropdown, value);
            this.closeDropdownMenu(dropdown);
          }
        });

        select.addEventListener('change', () => {
          this.updateCustomDropdownSelection(dropdown, select.value);
        });

        this.updateCustomDropdownSelection(dropdown, select.value);
      });

      window.addEventListener('scroll', () => {
        dropdowns.forEach(dropdown => {
          if (dropdown.classList.contains('genius-dropdown--open')) {
            this.positionDropdownPortal(dropdown);
          }
        });
      }, { passive: true });

      window.addEventListener('resize', () => {
        dropdowns.forEach(dropdown => {
          if (dropdown.classList.contains('genius-dropdown--open')) {
            this.positionDropdownPortal(dropdown);
          }
        });
      });
    },

    syncCustomDropdowns() {
      const dropdowns = this.container.querySelectorAll('.genius-dropdown');
      dropdowns.forEach(dropdown => {
        const select = dropdown.querySelector('select.genius-option-dropdown');
        if (!select) return;
        this.updateCustomDropdownSelection(dropdown, select.value);
      });
    },

    updateCustomDropdownSelection(dropdown, value) {
      const dropdownId = dropdown.dataset.dropdownId;
      const menu = document.querySelector(`.genius-dropdown__menu[data-belongs-to="${dropdownId}"]`) || 
                   dropdown.querySelector('.genius-dropdown__menu');
      
      if (!menu) return;
      
      const options = menu.querySelectorAll('.genius-dropdown__option');
      const triggerValue = dropdown.querySelector('.genius-dropdown__value');
      let foundSelected = false;

      options.forEach(option => {
        const isSelected = option.dataset.value === value;
        option.setAttribute('aria-selected', String(isSelected));
        option.classList.toggle('is-selected', isSelected);
        if (isSelected && triggerValue) {
          triggerValue.textContent = option.textContent.trim();
          foundSelected = true;
        }
      });

      if (!foundSelected && triggerValue) {
        triggerValue.textContent = this.strings.selectOption;
      }
    },

    openDropdownMenu(dropdown) {
      const trigger = dropdown.querySelector('.genius-dropdown__trigger');
      const menu = dropdown.querySelector('.genius-dropdown__menu');
      if (!trigger || !menu) return;

      const dropdownId = dropdown.dataset.dropdownId;
      dropdown.classList.add('genius-dropdown--open');
      trigger.setAttribute('aria-expanded', 'true');
      
      menu.classList.add('genius-dropdown__menu--open');
      menu.style.display = 'block';
      menu.style.visibility = 'visible';
      menu.style.opacity = '1';
      menu.style.transform = 'translateY(0)';

      // Move menu to body to avoid clipping by theme containers
      if (!this.dropdownPortals.has(dropdownId)) {
        this.dropdownPortals.set(dropdownId, menu.parentElement);
      }
      document.body.appendChild(menu);

      /* Portaled menus leave `.genius-options--palette-mono`; TR/Genova mono styles attach here. */
      const mono = !!this.container?.classList?.contains('genius-options--palette-mono');
      menu.classList.toggle('genius-dropdown__menu--palette-mono', mono);

      this.positionDropdownPortal(dropdown);
    },

    closeDropdownMenu(dropdown) {
      const trigger = dropdown.querySelector('.genius-dropdown__trigger');
      const dropdownId = dropdown.dataset.dropdownId;
      const menu = document.querySelector(`.genius-dropdown__menu[data-belongs-to="${dropdownId}"]`);
      
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      dropdown.classList.remove('genius-dropdown--open');

      if (menu) {
        menu.classList.remove('genius-dropdown__menu--open', 'genius-dropdown__menu--palette-mono');
        const originalParent = this.dropdownPortals.get(dropdownId);
        if (originalParent) {
          originalParent.appendChild(menu);
        }
        menu.style.display = 'none';
        menu.style.visibility = 'hidden';
        menu.style.opacity = '0';
        menu.style.transform = '';
        menu.style.position = '';
        menu.style.left = '';
        menu.style.right = '';
        menu.style.top = '';
        menu.style.width = '';
        menu.style.minWidth = '';
        menu.style.maxWidth = '';
        menu.style.zIndex = '';
      }
    },

    positionDropdownPortal(dropdown) {
      const trigger = dropdown.querySelector('.genius-dropdown__trigger');
      const dropdownId = dropdown.dataset.dropdownId;
      const menu = document.querySelector(`.genius-dropdown__menu[data-belongs-to="${dropdownId}"]`);
      if (!trigger || !menu) return;

      const rect = trigger.getBoundingClientRect();
      const top = rect.bottom + 6;
      const left = rect.left;
      const width = rect.width;

      menu.style.position = 'fixed';
      menu.style.top = `${top}px`;
      menu.style.left = `${left}px`;
      menu.style.right = 'auto';
      menu.style.width = `${width}px`;
      menu.style.minWidth = `${width}px`;
      menu.style.maxWidth = `${width}px`;
      menu.style.boxSizing = 'border-box';
      menu.style.zIndex = '99';
      
      // Ensure it doesn't go off-screen
      if (left + width > window.innerWidth) {
        menu.style.left = `${window.innerWidth - width - 10}px`;
      }
    },

    repositionActiveDropdowns() {
      if (!this.container) return;
      const openMenus = document.querySelectorAll('.genius-dropdown__menu--open');
      openMenus.forEach(menu => {
        // Find the dropdown this menu belongs to
        // Since the menu is portaled to body, we need to find the trigger that matches
        // For simplicity, we can store a reference to the dropdown on the menu element
        const dropdownId = menu.dataset.belongsTo;
        if (dropdownId) {
          const dropdown = document.querySelector(`.genius-dropdown[data-dropdown-id="${dropdownId}"]`);
          if (dropdown) {
            this.positionDropdownPortal(dropdown);
          }
        }
      });
    },
    
    initializeDependentGroups() {
      console.log('[GeniusOptions] Initializing dependent groups...');
      
      // Find all groups that have a depends-on-group attribute
      const dependentGroups = this.container.querySelectorAll('[data-depends-on-group]');
      console.log('[GeniusOptions] Found', dependentGroups.length, 'dependent groups');
      
      // Get unique parent group names
      const parentNames = new Set();
      dependentGroups.forEach(group => {
        parentNames.add(group.dataset.dependsOnGroup);
      });
      
      // For each parent, update its dependent groups
      parentNames.forEach(parentName => {
        console.log('[GeniusOptions] Updating dependents of:', parentName);
        
        const parentGroup = this.findOptionGroupElementByName(parentName);
        if (!parentGroup) {
          console.log('[GeniusOptions] Parent group not found:', parentName);
          return;
        }
        
        // Get selected value from parent
        let selectedValue = '';
        const select = parentGroup.querySelector('select');
        const checkedRadio = parentGroup.querySelector('input[type="radio"]:checked');
        
        if (select) {
          selectedValue = select.value;
          console.log('[GeniusOptions] Parent select value:', selectedValue);
        } else if (checkedRadio) {
          selectedValue = checkedRadio.value;
          console.log('[GeniusOptions] Parent radio value:', selectedValue);
        }
        
        // Update all dependent groups for this parent
        const deps = this.findDependentGroupElementsForParent(parentName);
        deps.forEach(depGroup => {
          const requiredValue = depGroup.dataset.dependsOnValue || '';
          
          // If required value is empty, never show (invalid dependency config)
          // If selected value is empty/placeholder, don't show dependent groups
          const shouldShow = requiredValue && selectedValue && 
                             selectedValue.toLowerCase() === requiredValue.toLowerCase();
          
          console.log('[GeniusOptions] Dependent:', depGroup.dataset.groupName, 
                      'requires:', requiredValue, 
                      'current:', selectedValue,
                      'show:', shouldShow);
          
          if (shouldShow) {
            depGroup.classList.add('genius-option-group--visible');
            depGroup.classList.remove('genius-option-group--hidden');
            depGroup.style.display = '';
            depGroup.style.visibility = '';
          } else {
            depGroup.classList.remove('genius-option-group--visible');
            depGroup.classList.add('genius-option-group--hidden');
            depGroup.style.display = 'none';
            depGroup.style.visibility = 'hidden';
            const depGroupId = depGroup.dataset.groupId;
            if (depGroupId && this.selectedOptions[depGroupId]) {
              delete this.selectedOptions[depGroupId];
            }
            const inputs = depGroup.querySelectorAll('input:checked');
            inputs.forEach((input) => {
              input.checked = false;
            });
            const selects = depGroup.querySelectorAll('.genius-option-dropdown');
            selects.forEach((sel) => {
              sel.selectedIndex = 0;
            });
          }
        });
      });
    },

    bindEvents() {
      const self = this;
      
      // Handler function for option changes
      const handleChange = function(e) {
        const target = e.target;
        
        // Stop event propagation to prevent theme JS errors
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        console.log('[GeniusOptions] Option changed:', target.name, '=', target.value);
        
        try {
          self.handleSelection(target);
          self.updateDependentGroupsFromInput(target);
          self.updatePrice();
          self.updateHiddenInputs();
          console.log('[GeniusOptions] Selection updated, selected options:', JSON.stringify(self.selectedOptions));
        } catch (err) {
          console.error('[GeniusOptions] Error in change handler:', err);
        }
        
        // Try to update variant separately - this may trigger theme errors
        try {
          self.tryUpdateVariant();
        } catch (err) {
          const msg = err != null && typeof err === 'object' && 'message' in err ? err.message : err;
          console.warn('[GeniusOptions] Variant update failed (expected if custom options):', msg);
        }
      };
      
      // Add direct listeners to all dropdowns and inputs (more reliable than delegation)
      const dropdowns = this.container.querySelectorAll('.genius-option-dropdown');
      dropdowns.forEach(dropdown => {
        dropdown.addEventListener('change', handleChange, true);
        console.log('[GeniusOptions] Added change listener to dropdown:', dropdown.name);
      });
      
      const inputs = this.container.querySelectorAll('.genius-option__input');
      inputs.forEach(input => {
        input.addEventListener('change', handleChange, true);
      });
      
      // Also use event delegation as backup
      this.container.addEventListener('change', (e) => {
        if (e.target.matches('.genius-option__input, .genius-option-dropdown')) {
          handleChange(e);
        }
      }, true); // true = capture phase, runs before bubbling
      
      // Setup quantity button listeners
      this.setupQuantityListeners();
      
      // Intercept form submission to add our properties
      this.setupFormInterception();
    },
    
    setupQuantityListeners() {
      const self = this;
      
      // Event delegation for quantity buttons
      this.container.addEventListener('click', (e) => {
        const btn = e.target.closest('.genius-option__qty-btn');
        if (!btn) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        const quantityContainer = btn.closest('.genius-option__quantity');
        const input = quantityContainer.querySelector('.genius-option__qty-input');
        const optionContainer = btn.closest('.genius-option');
        const checkbox = optionContainer.querySelector('.genius-option__input');
        
        let currentQty = parseInt(input.value) || 1;
        const min = parseInt(input.min) || 1;
        const max = parseInt(input.max) || 50;
        
        if (btn.dataset.action === 'plus' && currentQty < max) {
          currentQty++;
        } else if (btn.dataset.action === 'minus' && currentQty > min) {
          currentQty--;
        }
        
        input.value = currentQty;
        
        // Auto-check the checkbox when quantity changes
        if (!checkbox.checked && currentQty > 0) {
          checkbox.checked = true;
          self.handleSelection(checkbox);
        }
        
        // Update the quantity in selectedOptions
        self.updateOptionQuantity(checkbox.name, checkbox.value, currentQty);
        self.updatePrice();
        self.updateHiddenInputs();
        
        console.log('[GeniusOptions] Quantity updated:', checkbox.value, '=', currentQty);
      });
      
      // Also handle direct input changes
      this.container.addEventListener('change', (e) => {
        if (e.target.matches('.genius-option__qty-input')) {
          const input = e.target;
          const optionContainer = input.closest('.genius-option');
          const checkbox = optionContainer.querySelector('.genius-option__input');
          
          let qty = parseInt(input.value) || 1;
          const min = parseInt(input.min) || 1;
          const max = parseInt(input.max) || 50;
          
          // Clamp value
          qty = Math.max(min, Math.min(max, qty));
          input.value = qty;
          
          // Auto-check if quantity > 0
          if (!checkbox.checked && qty > 0) {
            checkbox.checked = true;
            this.handleSelection(checkbox);
          }
          
          this.updateOptionQuantity(checkbox.name, checkbox.value, qty);
          this.updatePrice();
          this.updateHiddenInputs();
        }
      });
    },
    
    updateOptionQuantity(groupId, optionValue, quantity) {
      if (!this.selectedOptions[groupId]) return;
      
      const option = this.selectedOptions[groupId].find(opt => opt.value === optionValue);
      if (option) {
        option.quantity = quantity;
        console.log('[GeniusOptions] Updated quantity for', optionValue, 'to', quantity);
      }
    },
    
    setupFormInterception() {
      // Listen for form submissions - ALWAYS add to cart, store price in properties
      document.addEventListener('submit', async (e) => {
        const form = e.target;
        if (form.matches('form[action*="/cart/add"]') && this.container) {
          console.log('[GeniusOptions] Form submit intercepted');
          console.log('[GeniusOptions] Current selectedOptions:', JSON.stringify(this.selectedOptions));
          
          // Calculate total extra price from options (including quantity)
          let totalExtra = 0;
          let optionCount = 0;
          Object.values(this.selectedOptions).forEach(options => {
            options.forEach(opt => {
              optionCount++;
              totalExtra += this.computeLineExtraForOption(opt);
            });
          });
          
          // Always add to cart normally with properties
          // Store the extra price so cart checkout can use it
          console.log('[GeniusOptions] Adding to cart with', optionCount, 'options, extra price:', totalExtra);
          this.addPropertiesToForm(form, totalExtra);
          // Note/attributes backup for Admin (PayTR may drop line properties)
          this.syncCartNoteAndAttributesFromProperties(this.buildCartLineItemProperties());
          setTimeout(() => window.GeniusOrderOptionsSync?.persist?.(), 600);
        }
      }, true);
      
      // Also intercept fetch/XHR for AJAX add to cart
      this.interceptAjaxAddToCart();
      this.interceptXHRAddToCart();
      
      // Summary actions (Wholesale Quote only; direct checkout button removed)
      this.addDirectCheckoutButton();
      this.updateLocaleInDom();
    },
    
    /**
     * Handle Add to Cart with priced options - uses Draft Order checkout
     * Since Draft Orders can't be added to regular cart, we redirect to checkout
     */
    async handleAddToCartWithDraftOrder() {
      // Button already shows "Configure & Checkout" so user knows what will happen
      // Just use the same flow as Direct Checkout
      await this.handleDirectCheckout();
    },
    
    /**
     * Add summary action buttons (Wholesale Quote only)
     */
    addDirectCheckoutButton() {
      const summary = this.container.querySelector('.genius-options__summary');
      if (!summary) return;
      
      let wrap = summary.querySelector('.genius-buttons');
      if (!wrap) {
        summary.insertAdjacentHTML('beforeend', '<div class="genius-buttons"></div>');
        wrap = summary.querySelector('.genius-buttons');
      }
      if (!wrap) return;

      if (!wrap.querySelector('[data-genius-reset-options]')) {
        const resetBtn = document.createElement('button');
        resetBtn.type = 'button';
        resetBtn.className = 'genius-reset-options-btn';
        resetBtn.setAttribute('data-genius-reset-options', '');
        resetBtn.textContent = this.strings.resetSelections;
        resetBtn.addEventListener('click', () => this.resetSelectionsToDefaults());
        wrap.insertBefore(resetBtn, wrap.firstChild);
      }

      if (!wrap.querySelector('.genius-quote-btn')) {
        wrap.insertAdjacentHTML(
          'beforeend',
          `
          <button type="button" class="genius-quote-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
            </svg>
            <span data-genius-quote-label>${this.strings.wholesaleQuote}</span>
          </button>
        `
        );
        const quoteBtn = wrap.querySelector('.genius-quote-btn');
        if (quoteBtn) quoteBtn.addEventListener('click', () => this.handleRequestQuote());
      }
    },
    
    /**
     * Handle Wholesale Quote - opens modal for zip code then sends beautiful email
     */
    handleRequestQuote() {
      // Validate first
      if (!this.validate()) {
        const error = document.querySelector('.genius-option-group--error');
        if (error) {
          error.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      
      // Show Zip Code Modal
      this.showQuoteModal();
    },
    
    /**
     * Show Quote Modal for collecting additional info - Compact horizontal layout
     */
    showQuoteModal() {
      // Remove existing modal if any
      const existingModal = document.querySelector('.genius-quote-modal');
      if (existingModal) existingModal.remove();
      
      const modalHtml = `
        <div class="genius-quote-modal">
          <div class="genius-quote-modal__backdrop"></div>
          <div class="genius-quote-modal__content">
            <button type="button" class="genius-quote-modal__close">&times;</button>
            
            <div class="genius-quote-modal__header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
              </svg>
              <div>
                <h3>${this.strings.wholesaleQuote}</h3>
                <p>${this.strings.quoteSubtitleHtml}</p>
              </div>
            </div>
            
            <div class="genius-quote-modal__body">
              <div class="genius-quote-modal__row">
                <div class="genius-quote-modal__field">
                  <label for="genius-quote-name">${this.strings.quoteNameLabel}</label>
                  <input type="text" id="genius-quote-name" placeholder="${this.escapeAttr(this.strings.quoteNamePlaceholder)}">
                </div>
                <div class="genius-quote-modal__field">
                  <label for="genius-quote-email">${this.strings.quoteEmailLabel}</label>
                  <input type="email" id="genius-quote-email" placeholder="${this.escapeAttr(this.strings.quoteEmailPlaceholder)}">
                </div>
              </div>
              
              <div class="genius-quote-modal__row">
                <div class="genius-quote-modal__field genius-quote-modal__field--small">
                  <label for="genius-quote-quantity">${this.strings.quoteQtyLabel}</label>
                  <input type="number" id="genius-quote-quantity" min="10" value="10">
                </div>
                <div class="genius-quote-modal__field">
                  <label for="genius-quote-country">${this.strings.quoteCountryLabel}</label>
                  <select id="genius-quote-country">
                    <option value="">${this.strings.quoteCountryPlaceholder}</option>
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="GB">United Kingdom</option>
                    <option value="DE">Germany</option>
                    <option value="FR">France</option>
                    <option value="AU">Australia</option>
                    <option value="JP">Japan</option>
                    <option value="KR">South Korea</option>
                    <option value="CN">China</option>
                    <option value="IN">India</option>
                    <option value="BR">Brazil</option>
                    <option value="MX">Mexico</option>
                    <option value="IT">Italy</option>
                    <option value="ES">Spain</option>
                    <option value="NL">Netherlands</option>
                    <option value="SE">Sweden</option>
                    <option value="CH">Switzerland</option>
                    <option value="PL">Poland</option>
                    <option value="TR">Turkey</option>
                    <option value="AE">UAE</option>
                    <option value="SA">Saudi Arabia</option>
                    <option value="SG">Singapore</option>
                    <option value="MY">Malaysia</option>
                    <option value="TH">Thailand</option>
                    <option value="VN">Vietnam</option>
                    <option value="PH">Philippines</option>
                    <option value="ID">Indonesia</option>
                    <option value="NZ">New Zealand</option>
                    <option value="ZA">South Africa</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div class="genius-quote-modal__field">
                  <label for="genius-quote-zip">${this.strings.quoteZipLabel}</label>
                  <input type="text" id="genius-quote-zip" placeholder="${this.escapeAttr(this.strings.quoteZipPlaceholder)}">
                </div>
              </div>
              
              <div class="genius-quote-modal__row">
                <div class="genius-quote-modal__field">
                  <label for="genius-quote-notes">${this.strings.quoteNotesLabel}<span>${this.strings.quoteNotesHint}</span></label>
                  <textarea id="genius-quote-notes" placeholder="${this.escapeAttr(this.strings.quoteNotesPlaceholder)}" maxlength="200" rows="3" style="resize: vertical; min-height: 70px;"></textarea>
                </div>
              </div>
            </div>
            
            <div class="genius-quote-modal__footer">
              <button type="button" class="genius-quote-modal__submit">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                </svg>
                ${this.strings.quoteSend}
              </button>
            </div>
          </div>
        </div>
      `;
      
      document.body.insertAdjacentHTML('beforeend', modalHtml);
      
      // Add modal styles
      this.addQuoteModalStyles();
      
      // Event listeners
      const modal = document.querySelector('.genius-quote-modal');
      const backdrop = modal.querySelector('.genius-quote-modal__backdrop');
      const closeBtn = modal.querySelector('.genius-quote-modal__close');
      const submitBtn = modal.querySelector('.genius-quote-modal__submit');
      
      backdrop.addEventListener('click', () => modal.remove());
      closeBtn.addEventListener('click', () => modal.remove());
      submitBtn.addEventListener('click', () => this.submitQuoteRequest());
      
      // Focus on first input
      setTimeout(() => {
        modal.querySelector('#genius-quote-name').focus();
      }, 100);
    },
    
    /**
     * Add modal styles dynamically - Compact horizontal layout
     */
    addQuoteModalStyles() {
      if (document.querySelector('#genius-quote-modal-styles')) return;
      
      const styles = `
        <style id="genius-quote-modal-styles">
          .genius-quote-modal {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 10000;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 16px;
            animation: fadeIn 0.2s ease;
          }
          
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          .genius-quote-modal__backdrop {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.75);
            backdrop-filter: blur(4px);
          }
          
          .genius-quote-modal__content {
            position: relative;
            width: 100%;
            max-width: 600px;
            background: linear-gradient(145deg, #1e293b, #0f172a);
            border-radius: 16px;
            border: 1px solid rgba(59, 130, 246, 0.3);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
            animation: slideUp 0.25s ease;
          }
          
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(10px) scale(0.98); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          
          .genius-quote-modal__close {
            position: absolute;
            top: 12px;
            right: 12px;
            width: 28px;
            height: 28px;
            border: none;
            background: rgba(255, 255, 255, 0.1);
            color: #94a3b8;
            font-size: 20px;
            line-height: 1;
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s;
          }
          
          .genius-quote-modal__close:hover {
            background: rgba(255, 255, 255, 0.2);
            color: #fff;
          }
          
          .genius-quote-modal__header {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 16px 20px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.08);
            color: #22d3ee;
          }
          
          .genius-quote-modal__header h3 {
            margin: 0;
            font-size: 1.1rem;
            font-weight: 700;
            color: #fff;
          }
          
          .genius-quote-modal__header p {
            margin: 2px 0 0;
            font-size: 0.8rem;
            color: #94a3b8;
          }
          
          .genius-quote-modal__header strong {
            color: #22d3ee;
          }
          
          .genius-quote-modal__body {
            padding: 16px 20px;
          }
          
          .genius-quote-modal__row {
            display: flex;
            gap: 12px;
            margin-bottom: 12px;
          }
          
          .genius-quote-modal__row:last-child {
            margin-bottom: 0;
          }
          
          .genius-quote-modal__field {
            flex: 1;
          }
          
          .genius-quote-modal__field--small {
            flex: 0 0 100px;
          }
          
          .genius-quote-modal__field--grow {
            flex: 2;
          }
          
          .genius-quote-modal__field label {
            display: block;
            margin-bottom: 4px;
            font-size: 0.75rem;
            font-weight: 600;
            color: #94a3b8;
            text-transform: uppercase;
            letter-spacing: 0.03em;
          }
          
          .genius-quote-modal__field label span {
            font-weight: 400;
            text-transform: none;
            color: #64748b;
          }
          
          .genius-quote-modal__field input,
          .genius-quote-modal__field textarea,
          .genius-quote-modal__field select {
            width: 100%;
            padding: 10px 12px;
            background: rgba(15, 23, 42, 0.8);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            color: #fff;
            font-size: 0.9rem;
            transition: all 0.2s;
            box-sizing: border-box;
            font-family: inherit;
          }
          
          .genius-quote-modal__field select {
            cursor: pointer;
            appearance: none;
            -webkit-appearance: none;
            -moz-appearance: none;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
            background-repeat: no-repeat;
            background-position: right 12px center;
            padding-right: 36px;
          }
          
          .genius-quote-modal__field select option {
            background: #1e293b;
            color: #fff;
            padding: 10px;
          }
          
          .genius-quote-modal__field textarea {
            min-height: 70px;
            line-height: 1.4;
          }
          
          .genius-quote-modal__field input:focus,
          .genius-quote-modal__field textarea:focus,
          .genius-quote-modal__field select:focus {
            outline: none;
            border-color: #22d3ee;
            box-shadow: 0 0 0 2px rgba(34, 211, 238, 0.2);
          }
          
          .genius-quote-modal__field input::placeholder,
          .genius-quote-modal__field textarea::placeholder {
            color: #64748b;
            opacity: 1;
          }
          
          .genius-quote-modal__footer {
            padding: 12px 20px 16px;
            display: flex;
            justify-content: flex-end;
          }
          
          .genius-quote-modal__submit {
            padding: 10px 20px;
            background: linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%);
            border: none;
            border-radius: 8px;
            color: #000;
            font-size: 0.9rem;
            font-weight: 700;
            cursor: pointer;
            transition: all 0.2s;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          
          .genius-quote-modal__submit:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 15px rgba(34, 211, 238, 0.4);
          }
          
          @media (max-width: 500px) {
            .genius-quote-modal__row {
              flex-wrap: wrap;
            }
            .genius-quote-modal__field--small {
              flex: 1 1 45%;
            }
            .genius-quote-modal__field--grow {
              flex: 1 1 100%;
            }
          }
        </style>
      `;
      
      document.head.insertAdjacentHTML('beforeend', styles);
    },
    
    /**
     * Submit the quote request - Clean and simple email
     */
    async submitQuoteRequest() {
      const modal = document.querySelector('.genius-quote-modal');
      const submitBtn = modal.querySelector('.genius-quote-modal__submit');
      const customerName = modal.querySelector('#genius-quote-name').value.trim();
      const customerEmail = modal.querySelector('#genius-quote-email').value.trim();
      const quantity = modal.querySelector('#genius-quote-quantity').value || '10';
      const country = modal.querySelector('#genius-quote-country').value;
      const zipCode = modal.querySelector('#genius-quote-zip').value.trim();
      const notes = modal.querySelector('#genius-quote-notes').value.trim();
      
      // Validate required fields
      if (!customerName) {
        alert(this.strings.quoteErrName);
        modal.querySelector('#genius-quote-name').focus();
        return;
      }
      
      if (!customerEmail) {
        alert(this.strings.quoteErrEmail);
        modal.querySelector('#genius-quote-email').focus();
        return;
      }
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(customerEmail)) {
        alert(this.strings.quoteErrEmailFmt);
        modal.querySelector('#genius-quote-email').focus();
        return;
      }
      
      if (!country) {
        alert(this.strings.quoteErrCountry);
        modal.querySelector('#genius-quote-country').focus();
        return;
      }
      
      if (!zipCode) {
        alert(this.strings.quoteErrZip);
        modal.querySelector('#genius-quote-zip').focus();
        return;
      }
      
      // Show loading state
      const originalBtnText = submitBtn.innerHTML;
      submitBtn.innerHTML =
        '<span style="opacity:0.7">' +
        this.escapeHtml(this.strings.quoteSubmitting) +
        '</span>';
      submitBtn.disabled = true;
      
      // Collect product info
      const productTitle = document.querySelector('.product__title, h1')?.textContent?.trim() || 'Product';
      const productUrl = window.location.href;
      const productSku = document.querySelector('[data-sku], .product-sku')?.textContent?.trim() || null;
      
      // Collect selected options with prices
      const selectedOptionsList = [];
      let totalExtra = 0;
      
      Object.entries(this.selectedOptions).forEach(([groupId, options]) => {
        options.forEach(opt => {
          const line = this.computeLineExtraForOption(opt);
          totalExtra += line;
          selectedOptionsList.push({
            group: groupId,
            name: opt.displayValue,
            value: opt.value,
            price: line
          });
        });
      });
      
      const unitPrice = this.basePrice + totalExtra;
      
      try {
        // Submit to backend API
        const response = await fetch(`${this.config.appProxyUrl}?shop=${this.config.shop}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'submit_quote',
            data: {
              customerName,
              customerEmail,
              quantity,
              country,
              zipCode,
              notes,
              productTitle,
              productUrl,
              productSku,
              selectedOptions: selectedOptionsList,
              basePrice: this.basePrice,
              optionsTotal: totalExtra,
              unitPrice
            }
          })
        });
        
        const result = await response.json();
        
        if (result.success) {
          const safeEmail = this.escapeHtml(customerEmail);
          const quoteRef = this.escapeHtml(
            String(result.quoteId?.slice(0, 8) || 'N/A')
          );
          // Show success message
          modal.querySelector('.genius-quote-modal__content').innerHTML = `
            <div style="padding: 40px; text-align: center;">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" stroke-width="2" style="margin-bottom: 16px;">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <h3 style="color: #fff; margin: 0 0 8px;">${this.escapeHtml(this.strings.quoteSuccessTitle)}</h3>
              <p style="color: #94a3b8; margin: 0 0 16px; font-size: 0.9rem;">
                ${this.escapeHtml(this.strings.quoteSuccessLead)}<br><strong style="color: #22d3ee;">${safeEmail}</strong>
              </p>
              <p style="color: #64748b; font-size: 0.8rem; margin: 0;">
                ${this.escapeHtml(this.strings.quoteIdLabel)} ${quoteRef}
              </p>
              <button type="button" onclick="this.closest('.genius-quote-modal').remove()" 
                style="margin-top: 20px; padding: 10px 24px; background: #22d3ee; border: none; border-radius: 8px; color: #000; font-weight: 600; cursor: pointer;">
                ${this.escapeHtml(this.strings.quoteClose)}
              </button>
            </div>
          `;
        } else {
          throw new Error(result.error || 'Failed to submit quote');
        }
        
      } catch (error) {
        console.error('[GeniusOptions] Quote submission error:', error);
        
        // Restore button
        submitBtn.innerHTML = originalBtnText;
        submitBtn.disabled = false;
        
        alert(this.strings.quoteErrSubmit);
      }
    },
    
    /**
     * Handle direct checkout - create draft order with custom pricing
     */
    async handleDirectCheckout() {
      // Validate first
      if (!this.validate()) {
        const error = document.querySelector('.genius-option-group--error');
        if (error) {
          error.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      
      const btn = this.container.querySelector('.genius-checkout-btn');
      const originalText = btn ? btn.innerHTML : '';
      if (btn) {
        btn.innerHTML =
          '<span style="opacity:0.7">' +
          this.escapeHtml(this.strings.checkoutProcessing) +
          '</span>';
        btn.disabled = true;
      }
      
      try {
        // Get current variant ID from the form
        const form = document.querySelector('form[action*="/cart/add"]');
        const variantInput = form?.querySelector('input[name="id"], select[name="id"]');
        const variantId = variantInput?.value;
        
        if (!variantId) {
          alert(this.strings.variantSelectFirst);
          return;
        }
        
        // Calculate total price
        let totalExtra = 0;
        const optionsList = [];
        
        Object.values(this.selectedOptions).forEach(options => {
          options.forEach(opt => {
            const qty = opt.quantity || 1;
            const totalOptionPrice = this.computeLineExtraForOption(opt);
            totalExtra += totalOptionPrice;
            
            // Debug: log what we're sending
            console.log('[GeniusOptions] Option for checkout:', {
              groupDisplayName: opt.groupDisplayName,
              displayValue: opt.displayValue,
              value: opt.value,
              quantity: qty,
              unitPrice: price,
              totalPrice: totalOptionPrice
            });
            
            // Use groupDisplayName for name, displayValue for value
            // If displayValue equals groupDisplayName (error), try to get proper value
            let optionValue = opt.displayValue;
            if (optionValue === opt.groupDisplayName) {
              // This is an error - displayValue should be the selected option, not group name
              // Try to use the raw value instead
              console.warn('[GeniusOptions] displayValue equals groupDisplayName, using raw value');
              optionValue = opt.value || opt.displayValue;
            }
            
            // Include quantity in the value if > 1
            if (qty > 1) {
              optionValue = `${optionValue} ×${qty}`;
            }
            
            optionsList.push({
              name: opt.groupDisplayName || 'Option',
              value: optionValue,
              price: totalOptionPrice,  // Total price including quantity
              quantity: qty
            });
          });
        });
        
        const totalPrice = this.basePrice + totalExtra;
        
        console.log('[GeniusOptions] Creating checkout...', {
          variantId,
          basePrice: this.basePrice,
          totalExtra,
          totalPrice,
          options: optionsList
        });
        
        // Get shop domain from our config (permanent_domain from Liquid)
        const shopDomain = window.GeniusOptionsConfig?.shop || 
                           this.container?.dataset?.shop || 
                           window.Shopify?.shop || 
                           window.location.hostname;
        
        console.log('[GeniusOptions] Using shop domain:', shopDomain);
        
        // Call backend to create draft order
        const response = await fetch(`/apps/genius-options?shop=${encodeURIComponent(shopDomain)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'create_checkout',
            data: {
              variantId: variantId,
              quantity: 1,
              options: optionsList,
              basePrice: this.basePrice,
              totalPrice: totalPrice,
              totalExtra: totalExtra, // Additional cost from options
              productTitle: document.querySelector('.product__title, h1')?.textContent?.trim() || 'Product'
            }
          })
        });
        
        const result = await response.json();
        console.log('[GeniusOptions] Checkout result:', result);
        
        if (result.checkoutUrl) {
          // Redirect to checkout
          window.location.href = result.checkoutUrl;
        } else if (result.error) {
          console.error('[GeniusOptions] Checkout error:', result);
          alert('Could not create checkout: ' + (result.details || result.error));
        }
        
      } catch (error) {
        console.error('[GeniusOptions] Checkout error:', error);
        alert('An error occurred. Please try again.');
      } finally {
        if (btn) {
          btn.innerHTML = originalText;
          btn.disabled = false;
        }
      }
    },
    
    /** Cart API body: Dawn/modern themes use `{ items: [{ properties }] }`, not top-level `properties`. */
    mergePropertiesIntoCartAddBody(bodyData, properties) {
      if (!bodyData || typeof bodyData !== 'object') return bodyData;
      if (Array.isArray(bodyData.items) && bodyData.items.length > 0) {
        bodyData.items = bodyData.items.map((item) => ({
          ...item,
          properties: { ...(item.properties || {}), ...properties },
        }));
        return bodyData;
      }
      bodyData.properties = { ...(bodyData.properties || {}), ...properties };
      return bodyData;
    },

    buildCartLineItemProperties() {
      const properties = this.getPropertiesObject();
      if (this.selectedOptions && Object.keys(this.selectedOptions).length > 0) {
        const totalExtra = this.computeTotalExtraFromSelected();
        if (totalExtra > 0) {
          properties['_genius_options_price'] = totalExtra.toFixed(2);
        }
      }
      return properties;
    },

    /**
     * Cart note + cart attributes → Shopify Admin order "Notlar" / "Ek bilgiler".
     * Line item properties (above) show under the product; this is a durable backup
     * for gateways (e.g. PayTR) that drop line properties.
     */
    syncCartNoteAndAttributesFromProperties(properties) {
      try {
        const props = properties || {};
        const isTr =
          this.config?.locale === 'tr' || this.shouldForceTurkishStorefrontUi();
        const skipKeys = {
          Yapılandırma: 1,
          Configuration: 1,
          Seçenekler: 1,
          Options: 1,
        };
        const lines = Object.entries(props)
          .filter(([key, value]) => {
            if (!key || String(key).startsWith('_') || skipKeys[key]) return false;
            return String(value || '').trim() !== '';
          })
          .map(([key, value]) => `${key}: ${String(value).trim()}`);
        const summary = lines.join(' | ');
        if (!summary) return;

        const label = isTr ? 'Seçenekler' : 'Options';
        const note = `${label}:\n${lines.join('\n')}`;
        const attributes = {};
        attributes[label] = summary;

        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ note, attributes }),
        }).catch((err) => {
          console.warn('[GeniusOptions] cart note/attributes sync failed:', err);
        });
      } catch (e) {
        console.warn('[GeniusOptions] syncCartNoteAndAttributesFromProperties:', e);
      }
    },

    appendPropertiesToFormData(formData, properties) {
      Object.entries(properties).forEach(([key, value]) => {
        formData.append(`properties[${key}]`, value);
      });
    },

    interceptAjaxAddToCart() {
      const self = this;
      if (window.__geniusOptionsFetchPatched) return;
      window.__geniusOptionsFetchPatched = true;
      const originalFetch = window.fetch.bind(window);

      window.fetch = async function (...args) {
        const url = args[0];
        const init = args.length > 1 ? args[1] : undefined;
        const urlString = typeof url === 'string' ? url : url?.toString?.() || '';

        if (
          urlString.includes('/cart/add') &&
          self.selectedOptions &&
          Object.keys(self.selectedOptions).length > 0
        ) {
          console.log('[GeniusOptions] Intercepted fetch add to cart');
          const properties = self.buildCartLineItemProperties();
          const totalExtra = self.computeTotalExtraFromSelected();

          if (init && init.body) {
            try {
              if (typeof init.body === 'string' && init.body.trim().startsWith('{')) {
                const bodyData = self.mergePropertiesIntoCartAddBody(
                  JSON.parse(init.body),
                  properties
                );
                const nextInit = { ...init, body: JSON.stringify(bodyData) };
                console.log('[GeniusOptions] fetch cart/add merged properties, extra:', totalExtra);
                const response = await originalFetch(url, nextInit);
                if (response && response.ok) {
                  self.syncCartNoteAndAttributesFromProperties(properties);
                  window.GeniusOrderOptionsSync?.persist?.();
                }
                return response;
              }
              if (init.body instanceof FormData) {
                self.appendPropertiesToFormData(init.body, properties);
                console.log('[GeniusOptions] fetch cart/add FormData properties, extra:', totalExtra);
                const response = await originalFetch(url, init);
                if (response && response.ok) {
                  self.syncCartNoteAndAttributesFromProperties(properties);
                  window.GeniusOrderOptionsSync?.persist?.();
                }
                return response;
              }
            } catch (e) {
              console.warn('[GeniusOptions] Could not modify fetch cart/add:', e);
            }
          }
        }

        return originalFetch(...args);
      };
    },

    interceptXHRAddToCart() {
      const self = this;
      if (window.__geniusOptionsXHRPatched) return;
      window.__geniusOptionsXHRPatched = true;
      const XHR = XMLHttpRequest.prototype;
      const originalOpen = XHR.open;
      const originalSend = XHR.send;

      XHR.open = function (method, url, ...rest) {
        this._geniusCartUrl = typeof url === 'string' ? url : String(url || '');
        return originalOpen.call(this, method, url, ...rest);
      };

      XHR.send = function (body) {
        const urlString = this._geniusCartUrl || '';
        if (
          urlString.includes('/cart/add') &&
          self.selectedOptions &&
          Object.keys(self.selectedOptions).length > 0 &&
          typeof body === 'string' &&
          body.trim().startsWith('{')
        ) {
          try {
            const properties = self.buildCartLineItemProperties();
            const bodyData = self.mergePropertiesIntoCartAddBody(JSON.parse(body), properties);
            console.log(
              '[GeniusOptions] XHR cart/add merged properties, extra:',
              self.computeTotalExtraFromSelected()
            );
            this.addEventListener('load', function () {
              if (this.status >= 200 && this.status < 300) {
                self.syncCartNoteAndAttributesFromProperties(properties);
                window.GeniusOrderOptionsSync?.persist?.();
              }
            });
            return originalSend.call(this, JSON.stringify(bodyData));
          } catch (e) {
            console.warn('[GeniusOptions] Could not modify XHR cart/add:', e);
          }
        }
        return originalSend.call(this, body);
      };
    },
    
    getPropertiesObject() {
      const properties = {};
      
      // Use group display name as key, option display value as value
      // Multiple options from same group are combined with comma
      Object.entries(this.selectedOptions).forEach(([groupId, options]) => {
        options.forEach(opt => {
          const key = opt.groupDisplayName || groupId;
          let value = this.cleanOptionLabelForStorage(opt.displayValue);
          const qty = opt.quantity || 1;
          
          // If displayValue equals groupDisplayName (error), use raw value
          if (value === key) {
            console.warn('[GeniusOptions] getPropertiesObject: displayValue equals key, using raw value');
            value = opt.value || value;
          }
          
          // Add quantity indicator if > 1
          if (qty > 1) {
            value = `${value} ×${qty}`;
          }
          
          // Combine multiple options from same group
          if (properties[key]) {
            properties[key] += ', ' + value;
          } else {
            properties[key] = value;
          }
        });
      });
      
      // Add summary of options with prices (amounts = shop currency / proxy multiplier, same as PDP)
      const optionSummary = [];
      const totalExtra = this.computeTotalExtraFromSelected();
      
      Object.entries(this.selectedOptions).forEach(([groupId, options]) => {
        options.forEach(opt => {
          const qty = opt.quantity || 1;
          const lineExtra = this.computeLineExtraForOption(opt);
          const cleanDv = this.cleanOptionLabelForStorage(opt.displayValue);
          const groupLabel = String(opt.groupDisplayName || '').trim();
          const label =
            groupLabel && !cleanDv.startsWith(`${groupLabel}:`)
              ? `${groupLabel}: ${cleanDv}`
              : cleanDv;
          if (qty > 1 && lineExtra > 0) {
            optionSummary.push(`${label} ×${qty} (+$${lineExtra.toFixed(2)})`);
          } else if (lineExtra > 0) {
            optionSummary.push(`${label} (+$${lineExtra.toFixed(2)})`);
          } else {
            optionSummary.push(`${label}`);
          }
        });
      });
      
      if (optionSummary.length > 0) {
        // Use || as separator to avoid confusion with commas in option names
        properties['_genius_options'] = optionSummary.join(' || ');
      }

      if (totalExtra > 0) {
        properties['_genius_extra_cost'] = this.formatCurrency(totalExtra);
      }
      
      // Add detailed option data as JSON for cart display
      const detailList = [];
      Object.entries(this.selectedOptions).forEach(([groupId, options]) => {
        options.forEach(opt => {
          const qty = opt.quantity || 1;
          const lineExtra = this.computeLineExtraForOption(opt);
          detailList.push({
            name: this.cleanOptionLabelForStorage(opt.displayValue),
            group: opt.groupDisplayName || groupId,
            qty: qty,
            price: Number(opt.priceModifier) || 0,
            total: lineExtra
          });
        });
      });

      if (detailList.length > 0) {
        properties['_genius_options_detail'] = JSON.stringify(detailList);
      }
      
      return properties;
    },
    
    addPropertiesToForm(form, totalExtra = 0) {
      // Remove existing genius inputs
      form.querySelectorAll('[data-genius-property]').forEach(el => el.remove());

      const properties = this.buildCartLineItemProperties();
      if (!totalExtra) {
        totalExtra = this.computeTotalExtraFromSelected();
      }

      Object.entries(properties).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${key}]`;
        input.value = value;
        input.setAttribute('data-genius-property', 'true');
        form.appendChild(input);
      });
      
      console.log('[GeniusOptions] Added properties to form:', Object.keys(properties).length, 'extra price:', totalExtra);
    },
    
    /**
     * Try to update the product variant based on selected options
     * This maps our custom options to Shopify variants if possible
     */
    tryUpdateVariant() {
      if (!this.productVariants.length) return;
      
      // Collect selected option values that could map to variants
      const variantOptions = [];
      
      Object.values(this.selectedOptions).forEach(options => {
        options.forEach(opt => {
          if (opt.variantOption) {
            variantOptions.push(opt.variantOption);
          } else {
            variantOptions.push(opt.displayValue);
          }
        });
      });
      
      if (variantOptions.length === 0) return;
      
      // Find matching variant
      const matchingVariant = this.findMatchingVariant(variantOptions);
      
      if (matchingVariant) {
        console.log('[GeniusOptions] Found matching variant:', matchingVariant.id, matchingVariant.title);
        this.currentVariantId = matchingVariant.id;
        this.updateFormVariant(matchingVariant.id);
        
        // Update price display if variant has different price
        if (matchingVariant.price) {
          this.basePrice = matchingVariant.price / 100;
          this.updatePrice();
        }
      }
    },

    updateDependentGroupsFromInput(input) {
      const groupElement = input.closest('.genius-option-group');
      if (!groupElement) return;
      
      const parentGroupName = groupElement.dataset.groupName;
      if (!parentGroupName) return;
      
      const selectedValue = input.tagName === 'SELECT' 
        ? input.value 
        : input.value;
      
      console.log('[GeniusOptions] updateDependentGroupsFromInput - parent:', parentGroupName, 'value:', selectedValue);
      
      const dependentGroups = this.findDependentGroupElementsForParent(parentGroupName);
      
      dependentGroups.forEach(depGroup => {
        const requiredValue = depGroup.dataset.dependsOnValue || '';
        
        // If required value is empty, never show (invalid dependency config)
        // If selected value is empty/placeholder, don't show dependent groups
        const shouldShow = requiredValue && selectedValue && 
                           selectedValue.toLowerCase() === requiredValue.toLowerCase();
        
        console.log('[GeniusOptions] Checking:', depGroup.dataset.groupName, 'requires:', requiredValue, 'show:', shouldShow);
        
        if (shouldShow) {
          depGroup.classList.add('genius-option-group--visible');
          depGroup.classList.remove('genius-option-group--hidden');
          depGroup.style.display = '';
          depGroup.style.visibility = '';
        } else {
          depGroup.classList.remove('genius-option-group--visible');
          depGroup.classList.add('genius-option-group--hidden');
          depGroup.style.display = 'none';
          depGroup.style.visibility = 'hidden';
          
          const depGroupId = depGroup.dataset.groupId;
          if (this.selectedOptions[depGroupId]) {
            delete this.selectedOptions[depGroupId];
          }
          
          const inputs = depGroup.querySelectorAll('.genius-option__input');
          inputs.forEach(inp => inp.checked = false);
          
          const selects = depGroup.querySelectorAll('.genius-option-dropdown');
          selects.forEach(sel => sel.selectedIndex = 0);
        }
      });
    },

    handleSelection(input) {
      const groupId = input.name;
      const isCheckbox = input.type === 'checkbox';
      
      // Get group display name from our stored map (most reliable)
      // Fall back to DOM attribute if map doesn't have it
      const groupDisplayName = this.groupDisplayNames[groupId] || 
        input.closest('.genius-option-group')?.dataset?.displayName || 
        groupId;

      if (isCheckbox) {
        if (!this.selectedOptions[groupId]) {
          this.selectedOptions[groupId] = [];
        }

        // Special case: Accessories – only one Power Wire plug can be selected
        // (e.g., Power Wire UK / EU / US Plug). Other accessories (VESA, adapter)
        // can still be combined freely.
        if (input.checked && groupDisplayName === 'Accessories') {
          const currentLabel = (input.dataset.displayValue || '').toLowerCase();
          if (currentLabel.includes('power wire')) {
            const groupEl = input.closest('.genius-option-group');
            if (groupEl) {
              const otherPowerWireInputs = groupEl.querySelectorAll(
                '.genius-option__input[type=\"checkbox\"][data-display-value*=\"Power Wire\"]'
              );
              otherPowerWireInputs.forEach(other => {
                if (other !== input && other.checked) {
                  // Uncheck in DOM
                  other.checked = false;
                  // Remove from selectedOptions state
                  if (this.selectedOptions[groupId]) {
                    this.selectedOptions[groupId] = this.selectedOptions[groupId].filter(
                      opt => opt.value !== other.value
                    );
                  }
                }
              });
            }
          }
        }
        
        if (input.checked) {
          // Prevent duplicates - only add if not already in array
          const alreadySelected = this.selectedOptions[groupId].some(opt => opt.value === input.value);
          if (!alreadySelected) {
            // Look up option data from our map
            const mapKey = `${groupId}:${input.value}`;
            const optionData = this.optionsDataMap[mapKey];
            
            let displayValue =
              (input.dataset && input.dataset.displayValue
                ? String(input.dataset.displayValue).trim()
                : '') ||
                             optionData?.displayValue || 
              '';
            if (!displayValue) {
              const label = input.parentElement?.querySelector('.genius-option__value');
              displayValue =
                label?.textContent?.replace(/\s*\([^)]*\)\s*$/, '').trim() ||
                             input.value;
            }
            displayValue = this.cleanOptionLabelForStorage(displayValue);
            
            // IMPORTANT: If displayValue equals groupDisplayName, use alternative
            if (displayValue === groupDisplayName) {
              console.warn('[GeniusOptions] checkbox displayValue equals groupDisplayName, trying alternatives');
              const altDisplayValue = input.dataset.displayValue || optionData?.displayValue;
              if (altDisplayValue && altDisplayValue !== groupDisplayName) {
                displayValue = altDisplayValue;
              } else {
                displayValue = input.value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              }
            }
            
            // Get quantity from input if allowQuantity is enabled
            const allowQuantity = input.dataset.allowQuantity === 'true' || optionData?.allowQuantity;
            let quantity = 1;
            if (allowQuantity) {
              const optionContainer = input.closest('.genius-option');
              const qtyInput = optionContainer?.querySelector('.genius-option__qty-input');
              quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;
            }
            
          this.selectedOptions[groupId].push({
            value: input.value,
              displayValue: displayValue,
              priceModifier: optionData?.priceModifier ?? (parseFloat(input.dataset.priceModifier) || 0),
              priceType: optionData?.priceModifierType || input.dataset.priceType || 'fixed',
              optionId: optionData?.optionId || input.dataset.optionId,
              variantOption: optionData?.variantOption || input.dataset.variantOption,
              groupDisplayName: groupDisplayName,
              allowQuantity: allowQuantity,
              quantity: quantity,
              imageUrl: optionData?.imageUrl || input.dataset.imageUrl || '',
              isDefault:
                optionData?.isDefault === true ||
                String(input.getAttribute('data-default') || '') === 'true'
            });
            const added = this.selectedOptions[groupId][this.selectedOptions[groupId].length - 1];
            if (added && added.imageUrl) this.lastLookOption = added;
          }
        } else {
          // Remove from selection when unchecked
          this.selectedOptions[groupId] = this.selectedOptions[groupId]
            .filter(opt => opt.value !== input.value);
        }
        } else {
          const selectedOption = input.tagName === 'SELECT' 
            ? input.options[input.selectedIndex]
            : input;

          // Skip placeholder options (empty value)
          const isPlaceholder = selectedOption?.dataset?.isPlaceholder === 'true' || !selectedOption?.value;
          
          if (isPlaceholder) {
            // Clear selection for this group if placeholder is selected
            this.selectedOptions[groupId] = [];
          } else {
            const optionValue = selectedOption.value;
            
            // Look up option data from our map
            const mapKey = `${groupId}:${optionValue}`;
            const optionData = this.optionsDataMap[mapKey];
            
            // Prefer API label from data attribute (avoids TR locale / nested parens breaking textContent strip)
            let displayValue =
              (selectedOption.dataset && selectedOption.dataset.displayValue
                ? String(selectedOption.dataset.displayValue).trim()
                : '') ||
                             optionData?.displayValue || 
              '';
            if (!displayValue) {
              displayValue =
                selectedOption.textContent?.replace(/\s*\([^)]*\)\s*$/, '').trim() || optionValue;
            }
            displayValue = this.cleanOptionLabelForStorage(displayValue);
            
            // IMPORTANT: If displayValue equals groupDisplayName, it's likely a placeholder or error
            if (displayValue === groupDisplayName) {
              const altDisplayValue = selectedOption.dataset?.displayValue || 
                                      optionData?.displayValue;
              if (altDisplayValue && altDisplayValue !== groupDisplayName) {
                displayValue = altDisplayValue;
              } else {
                displayValue = optionValue.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              }
            }

            // Get quantity from input if allowQuantity is enabled
            const allowQuantity = selectedOption.dataset?.allowQuantity === 'true' || optionData?.allowQuantity;
            let quantity = 1;
            if (allowQuantity) {
              const optionContainer = input.closest('.genius-option');
              const qtyInput = optionContainer?.querySelector('.genius-option__qty-input');
              quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;
            }
            
            this.selectedOptions[groupId] = [{
              value: optionValue,
              displayValue: displayValue,
              priceModifier: optionData?.priceModifier ?? (parseFloat(selectedOption.dataset?.priceModifier) || 0),
              priceType: optionData?.priceModifierType || selectedOption.dataset?.priceType || 'fixed',
              optionId: optionData?.optionId || selectedOption.dataset?.optionId,
              variantOption: optionData?.variantOption || selectedOption.dataset?.variantOption,
              groupDisplayName: groupDisplayName,
              allowQuantity: allowQuantity,
              quantity: quantity,
              imageUrl: optionData?.imageUrl || selectedOption.dataset?.imageUrl || '',
              isDefault:
                optionData?.isDefault === true ||
                String(selectedOption.dataset?.isDefault || '') === 'true'
            }];
            const chosenLook = this.selectedOptions[groupId][0];
            if (chosenLook && chosenLook.imageUrl) this.lastLookOption = chosenLook;
          }
        }
    },

    updatePrice() {
      // Cleanup "each" text before updating price (in case it was added dynamically)
      try {
        if (this.container && typeof this.removeEachTextFromCheckboxes === 'function') {
          this.removeEachTextFromCheckboxes();
        }
      } catch (error) {
        console.error('[GeniusOptions] Error in updatePrice cleanup:', error);
      }
      
      let totalPrice = this.basePrice;
      let totalExtra = 0;
      const selectedItems = [];
      
      Object.values(this.selectedOptions).forEach(options => {
        options.forEach(opt => {
          const qty = opt.quantity || 1;
          const priceChange = this.computeLineExtraForOption(opt);
            totalPrice += priceChange;
            totalExtra += priceChange;
          
          if (priceChange !== 0 || qty > 1) {
            selectedItems.push({
              displayValue: opt.displayValue || opt.value,
              groupDisplayName: opt.groupDisplayName || '',
              priceChange: priceChange,
              quantity: qty
            });
          }
        });
      });

      // Update detailed summary
      this.updateDetailedSummary(totalPrice, totalExtra, selectedItems);
      
      // Also try to update Shopify's native price display
      this.updateNativePriceDisplay(totalPrice);
      
      // NOTE: We don't modify the native Add to Cart button anymore.
      // The native button still works - form interception handles priced options

      // Dispatch event for other scripts
      window.dispatchEvent(new CustomEvent('genius:priceUpdate', {
        detail: { 
          totalPrice, 
          basePrice: this.basePrice,
          selectedOptions: this.selectedOptions 
        }
      }));
      this.updateLookPreview();
    },

    jrConfiguredLook(names) {
      const sku = String(this.config && this.config.productSku || '').toUpperCase();
      if (sku !== 'GJ-WP' && sku !== 'GJ-EX') return null;
      const selected = new Set();
      Object.values(this.selectedOptions || {}).forEach((options) => {
        (options || []).forEach((opt) => {
          if (opt && opt.value) selected.add(String(opt.value));
        });
      });
      const conn = selected.has('sip') ? 'sip' : selected.has('4g') ? '4g' : 'al';
      const door = selected.has('door');
      const dial = ['handsfree', 'hot', 'fk', '3b', '2b', 'speed'].find((key) => selected.has(key)) || 'speed';
      const extras = ['lcd', 'led', 'horn', 'beacon'].filter((key) => selected.has(key));
      const extraKey = extras.join('+');
      const codes = {
        'GJ-WP|0|speed|': 'jr104-sc',
        'GJ-WP|0|fk|': 'jr103-fk',
        'GJ-WP|0|hot|': 'jr103-cb',
        'GJ-WP|0|fk|lcd': 'jr105-fk',
        'GJ-WP|1|speed|': 'jr102-sc',
        'GJ-WP|1|fk|': 'jr101-fk',
        'GJ-WP|1|fk|led': 'jr101-fk-l',
        'GJ-WP|1|hot|': 'jr101-cb',
        'GJ-WP|1|fk|horn': 'jr101-fk-hb',
        'GJ-WP|1|fk|beacon': 'jr101-fk-hb',
        'GJ-WP|1|fk|horn+beacon': 'jr101-fk-hb',
        'GJ-EX|1|fk|': 'jrex106-a01',
        'GJ-EX|1|hot|': 'jrex106-a01-cb',
        'GJ-EX|0|fk|': 'jrex106-a03',
        'GJ-EX|0|handsfree|': 'jrex106-a04',
        'GJ-EX|0|hot|': 'jrex106-a03-cb',
        'GJ-EX|1|fk|lcd': 'jrex106-cb-lcd',
        'GJ-EX|0|fk|lcd': 'jrex106-cb-lcd',
      };
      const code = codes[sku + '|' + (door ? '1' : '0') + '|' + dial + '|' + extraKey];
      if (!code) return null;
      const exAnalogOnly = code === 'jrex106-cb-lcd';
      const file = code + '-' + (exAnalogOnly ? 'al' : conn);
      return {
        imageUrl: 'https://www.jr-ltd.com/web/allimg/product/' + file + '.webp',
        displayValue: (names || []).join(' · '),
      };
    },

    mountLookStage(content) {
      if (!content || !content.querySelector('.genius-option--image')) return;
      const stage = document.createElement('div');
      stage.className = 'genius-look';
      stage.hidden = true;
      stage.innerHTML =
        '<p class="genius-look__label"></p>' +
        '<div class="genius-look__frame"><img class="genius-look__img" alt=""></div>' +
        '<p class="genius-look__caption"></p>';
      content.insertBefore(stage, content.firstChild);
    },

    updateLookPreview() {
      const stage = this.container && this.container.querySelector('.genius-look');
      if (!stage) return;
      const img = stage.querySelector('.genius-look__img');
      const caption = stage.querySelector('.genius-look__caption');
      const label = stage.querySelector('.genius-look__label');
      if (label) label.textContent = this.strings.lookLabel || 'Selected look';

      const names = [];
      const withImage = [];
      Object.values(this.selectedOptions || {}).forEach((options) => {
        (options || []).forEach((opt) => {
          const name = opt && (opt.displayValue || opt.value);
          if (name) names.push(name);
          if (opt && opt.imageUrl) withImage.push(opt);
        });
      });
      const lastStill = this.lastLookOption && withImage.some(function (opt) {
        return opt.value === this.lastLookOption.value && opt.imageUrl === this.lastLookOption.imageUrl;
      }, this);
      const configured = this.jrConfiguredLook(names);
      const look = configured || (lastStill ? this.lastLookOption : withImage[withImage.length - 1]);
      if (!look || !look.imageUrl) {
        stage.hidden = true;
        return;
      }
      stage.hidden = false;
      if (img) {
        img.src = look.imageUrl;
        img.alt = look.displayValue || '';
      }
      if (caption) caption.textContent = names.join(' · ');
      this.applyProductLook(look.imageUrl, look.displayValue || '');
    },

    applyProductLook(src, alt) {
      if (!src) return;
      const media = document.querySelector(
        '.product__main-media img, .product__media-wrapper .product__media img, media-gallery img, .product-media img'
      );
      if (!media) return;
      media.src = src;
      if (media.srcset) media.srcset = '';
      if (alt) media.alt = alt;
    },
    
    updateDetailedSummary(totalPrice, totalExtra, selectedItems) {
      const summary = this.container.querySelector('.genius-options__summary');
      if (!summary) return;
      
      // Find or create summary content container
      let summaryContent = summary.querySelector('.genius-options__summary-content');
      if (!summaryContent) {
        // Remove old simple row if exists
        const oldRow = summary.querySelector('.genius-options__summary-row');
        if (oldRow) oldRow.remove();
        
        summaryContent = document.createElement('div');
        summaryContent.className = 'genius-options__summary-content';
        const btnAnchor = summary.querySelector('.genius-buttons');
        if (btnAnchor) {
          summary.insertBefore(summaryContent, btnAnchor);
        } else {
          summary.appendChild(summaryContent);
        }
      }
      
      let html = '';
      
      // Base price
      html += `
        <div class="genius-options__summary-item">
          <span class="genius-options__summary-item-label">${this.strings.basePriceLabel}</span>
          <span class="genius-options__summary-item-price">${this.formatCurrency(this.basePrice)}</span>
        </div>
      `;
      
      // Selected options with price changes
      if (selectedItems.length > 0) {
        selectedItems.forEach(item => {
          const qtyText = item.quantity > 1 ? ` × ${item.quantity}` : '';
          const groupPrefix = item.groupDisplayName ? `${item.groupDisplayName}: ` : '';
          html += `
            <div class="genius-options__summary-item genius-options__summary-item--option">
              <span class="genius-options__summary-item-label">
                ${this.escapeHtml(groupPrefix + item.displayValue)}${qtyText}
              </span>
              <span class="genius-options__summary-item-price genius-options__summary-item-price--add">
                +${this.formatCurrency(item.priceChange)}
              </span>
            </div>
          `;
        });
      }
      
      // Total
      html += `
        <div class="genius-options__summary-item genius-options__summary-item--total">
          <span class="genius-options__summary-item-label">${this.strings.totalLabel}</span>
          <span class="genius-options__summary-item-price genius-options__summary-item-price--total" data-genius-total-price>
            ${this.formatCurrency(totalPrice)}
          </span>
        </div>
      `;
      
      summaryContent.innerHTML = html;
    },
    
    updateNativePriceDisplay(totalPrice) {
      // Check if product has compare_at_price (is on sale)
      // Multiple detection methods:
      // 1. .price--on-sale class on container (Genova theme)
      // 2. .price-item--sale element exists (means there's a sale price separate from regular)
      // 3. Classic indicators like .price-item--compare, s, del tags
      const hasCompareAt = document.querySelector(
        '.price--on-sale, .price-item--sale, .price-item--compare, .price__compare, [data-compare-price], .price s, .price del, .compare-at-price'
      );
      
      let updated = false;
      const formattedPrice = this.formatCurrency(totalPrice);
      
      if (hasCompareAt) {
        // Product is ON SALE - ONLY update the sale price element (.price-item--sale)
        // DO NOT touch .price-item--regular as that's the crossed-out compare_at_price
        const salePriceSelectors = [
          '.price-item--sale',  // Primary: the sale price element
          '.price__sale .price-item',
          '[data-sale-price]',
        ];
        
        for (const selector of salePriceSelectors) {
          const el = document.querySelector(selector);
          if (el) {
            el.textContent = formattedPrice;
            updated = true;
            console.log('[GeniusOptions] Updated sale price via:', selector);
            break;
          }
        }
        
        if (!updated) {
          console.log('[GeniusOptions] Product is on sale but could not find sale price element');
        }
      } else {
        // No compare_at_price / not on sale - update the main price display
        // .price-item--regular IS the current price when there's no sale
        const regularSelectors = [
          // Dawn 2.0 / Modern themes
          '.price__regular .money',
          '.price__regular .price-item--regular',
          '.price-item--regular',
          // Classic/older themes  
        '.product__price .price-item--regular',
        '.product-single__price',
        '[data-product-price]',
        '.product__info-price .price',
          // Generic fallbacks
          '.price--large .money',
          '.price--large',
          '.product-price',
      ];
      
        for (const selector of regularSelectors) {
        const el = document.querySelector(selector);
        if (el) {
            el.textContent = formattedPrice;
            updated = true;
            console.log('[GeniusOptions] Updated regular price via:', selector);
            break;
          }
        }
      }
      
      if (!updated) {
        console.log('[GeniusOptions] Could not find price element to update. Tried selectors but none found.');
      }
      
      console.log('[GeniusOptions] Price display result:', updated, 'hasCompareAt:', !!hasCompareAt, 'newPrice:', formattedPrice);
    },

    updateHiddenInputs() {
      const container = document.getElementById('genius-hidden-inputs');
      if (!container) return;

      const properties = this.buildCartLineItemProperties();
      const totalExtra = this.computeTotalExtraFromSelected();

      let html = '';
      Object.entries(properties).forEach(([key, value]) => {
        html += `<input type="hidden" name="properties[${key}]" value="${this.escapeHtml(value)}">`;
      });

      container.innerHTML = html;

      // Also update the product form directly
      const form = document.querySelector('form[action*="/cart/add"]');
      if (form) {
        form.querySelectorAll('[data-genius-property]').forEach(el => el.remove());
        
        Object.entries(properties).forEach(([key, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
          input.name = `properties[${key}]`;
          input.value = value;
            input.setAttribute('data-genius-property', 'true');
            form.appendChild(input);
        });
      }
      
      console.log('[GeniusOptions] Hidden inputs updated, extra price:', totalExtra);
    },

    validate() {
      const groups = this.container.querySelectorAll('[data-required="true"]');
      let isValid = true;
      
      groups.forEach(group => {
        const groupId = group.dataset.groupId;
        const hasSelection = this.selectedOptions[groupId]?.length > 0;
        
        group.classList.toggle('genius-option-group--error', !hasSelection);
        
        if (!hasSelection) {
          isValid = false;
        }
      });
      
      return isValid;
    },

    showError() {
      const loading = this.container.querySelector('.genius-options__loading');
      const error = this.container.querySelector('.genius-options__error');
      const globalTrust = this.container.querySelector('[data-genius-global-trust]');
      
      if (loading) loading.style.display = 'none';
      if (error) error.style.display = 'block';
      if (globalTrust) globalTrust.classList.remove('genius-options__global-trust--visible');
    },

    escapeHtml(text) {
      if (text === null || text === undefined) return '';
      const div = document.createElement('div');
      div.textContent = String(text);
      return div.innerHTML;
    },

    /** Attribute-safe (handles 0 / false; avoids breaking HTML on quotes, &, etc.). */
    escapeAttr(text) {
      return this.escapeHtml(text === null || text === undefined ? '' : text);
    },
  };

  // Initialize
  function initGeniusOptions() {
    if (!window.GeniusOptionsInitStarted) {
      window.GeniusOptionsInitStarted = true;
      GeniusOptions.init();
    }
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGeniusOptions);
  } else {
    setTimeout(initGeniusOptions, 50);
  }

  window.GeniusOptions = GeniusOptions;
})();
